import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  ButtonGroup,
  IconButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  useMediaQuery,
  useTheme,
  Menu,
  CircularProgress,
  Alert,
  Snackbar,
  Drawer,
  Chip,
  Switch,
} from '@mui/material';
import {
  Search as SearchIcon,
  People as PeopleIcon,
  Psychology as PsychologyIcon,
  BarChart as ChartIcon,
  GpsFixed as AreaSearchIcon,
  MenuBook as MenuBookIcon,
  ChevronLeft,
  ChevronRight,
  Assignment as AssignmentIcon,
  LocationOn as DwellIcon,
  ClearAll as ClearIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Group as GroupIcon,
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
  Shield as ShieldIcon,
  RocketLaunch as RocketIcon,
  Tune as TuneIcon,
  Close as CloseIcon,
  Menu as MenuIcon,
  CloudUpload as UploadIcon,
  Folder as FolderIcon,
  DeleteSweep as DeleteSweepIcon,
  Sensors as SensorsIcon,
  Wifi as WifiIcon,
  Bluetooth as BluetoothIcon,
  CellTower as CellTowerIcon,
  Favorite as HeartIcon,
  ChatBubbleOutline as PttChatIcon,
  Pentagon as PentagonIcon,
  Newspaper as NewspaperIcon,
  ExpandMore as MinimizeTimelineIcon,
} from '@mui/icons-material';
// Import the compact time slider component
import CompactTimeSlider from '../components/pathfinder/CompactTimeSlider';
import type { PathfinderCameraControls } from '../components/pathfinder/MapLayerControlsDialog';
import PttCaptionPanel, { pttSenderColor, buildPttSenderColors } from '../components/pathfinder/PttCaptionPanel';
import { pttUtterancesService, PathfinderPttUtterance } from '../services/pttUtterancesService';
import DigitalPoL from '../components/pathfinder/DigitalPoL';
import { parseBackendDateTime } from '@signals/app-shell/utils/parseDateTime';
import InteractiveCharts, { ChartFilter } from '../components/pathfinder/InteractiveCharts';
import PathfinderMapGL from '../components/pathfinder/PathfinderMapGL';
import { debugLog } from '@signals/app-shell/utils/debugLog';
import HierarchicalDeviceTree from '../components/pathfinder/HierarchicalDeviceTree';
import AreaSearch, { DrawCircle } from '../components/pathfinder/AreaSearch';
import GeofencePanel from '../components/pathfinder/GeofencePanel';
import OverwatchPanel from '../components/pathfinder/OverwatchPanel';
import AdtechResultsTable, { AdtechMapSignal } from '../components/pathfinder/AdtechResultsTable';
import { geofenceService, Geofence } from '@signals/app-shell/services/geofenceService';
import EnhancedCoTravelerAnalysis from '../components/pathfinder/EnhancedCoTravelerAnalysis';
import DwellAnalysis from '../components/pathfinder/DwellAnalysis';
import BiometricsPanel from '../components/pathfinder/BiometricsPanel';
import StoryMode from '../components/pathfinder/StoryMode';
import PathfinderMapNarrativeOverlay from '../components/pathfinder/PathfinderMapNarrativeOverlay';
import type { StoryEvent } from '../stores/narrativeStore';
import AnnotationPanel from '../components/pathfinder/AnnotationPanel';
import PathfinderSentinel from '../components/pathfinder/PathfinderSentinel';
import DateTimeRangePicker from '../components/pathfinder/DateTimeRangePicker';
import PathfinderToolRail, { PathfinderToolGroupId, PATHFINDER_TOOL_RAIL_WIDTH } from '../components/pathfinder/PathfinderToolRail';
import PathfinderToolPalette from '../components/pathfinder/PathfinderToolPalette';
import pathfinderApi, { PathfinderApiService, PathfinderStudent, PathfinderDataPoint, PathfinderRoute, AdtechStatus, OverwatchStatus, OverwatchArticle } from '../services/pathfinderApi';
import { colorForAdtechId, resolvePathfinderColor } from '../utils/pathfinderColors';
import apiService from '@signals/app-shell/services/api';
import { useFeatureFlagsStore } from '@signals/app-shell/stores/featureFlagsStore';
import { useEffectiveAccess } from '@signals/app-shell/stores/accessControlStore';
import { hasPermission, PATHFINDER_TAB_PERMISSION_BY_LABEL } from '@signals/app-shell/types/accessControl';
import { useAccessDataScopeClamp } from '@signals/app-shell/hooks/useAccessDataScopeClamp';
import { detectDwellsFromPoints, detectDwellCoLocations } from '../utils/clientDwellDetection';

let RFSignalEnvironment: React.ComponentType<any> | null = null;
try {
  RFSignalEnvironment = require('@signals/pathfinder-rf-ui').SignalEnvironment;
} catch { /* RF module not available */ }
import { useLayoutContext } from '@signals/app-shell/contexts/LayoutContext';
import { listItinerarySubmissions, ItinerarySubmission } from '@signals/app-shell/services/itineraryApi';
import { useMapStore, useDeviceStore, useSettingsStore } from '@signals/app-shell/stores';
import { usePathfinderStore } from '../stores/pathfinderStore';
import CSVUploadDialog from '../components/pathfinder/CSVUploadDialog';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import { useCourseLabels } from '@signals/app-shell/hooks/useCourseLabels';
import { getPreferredTimeZone, parseUtcInstant, wallClockDateInTimeZoneToUtcIso } from '@signals/app-shell/utils/time';


/** Default entity -> selected device keys for map filtering; must match per-area device tree logic. */
function buildAreaSelectedMapFromCircle(circle: DrawCircle): Record<string, string[]> {
  const areaSelectedMap: Record<string, string[]> = {};
  circle.results?.forEach((result: any) => {
    const fullId = result.entityId || result.entity_id || 'Unknown';
    const dashIdx = fullId.lastIndexOf(' - ');
    const entityId = dashIdx > 0 ? fullId.substring(0, dashIdx) : fullId;
    const rawType = dashIdx > 0 ? fullId.substring(dashIdx + 3) : (result.deviceType || 'phone');
    const deviceType = rawType.toLowerCase().includes('phone') ? 'phone' : rawType.toLowerCase();
    const deviceId = `${entityId}-${deviceType}`;
    if (!areaSelectedMap[entityId]) areaSelectedMap[entityId] = [];
    if (!areaSelectedMap[entityId].includes(deviceId)) areaSelectedMap[entityId].push(deviceId);
  });
  return areaSelectedMap;
}

const PathfinderPage: React.FC = () => {
  const { plural, pluralLower, singular } = useParticipantLabels();
  const preferredTimezoneSetting = useSettingsStore((state) => state.settings.temporal.preferredTimezone);
  const preferredTimezone = getPreferredTimeZone(preferredTimezoneSetting);
  const classificationBannerMode = useFeatureFlagsStore((s) => s.classificationBannerMode);
  const isCommercialBanner = classificationBannerMode === 'commercial';
  useAccessDataScopeClamp();
  const { courseSingular, allLabel } = useCourseLabels();
  // RF module availability -- used by signal environment loading and co-traveler digital analysis
  const rfEnabled: boolean = useFeatureFlagsStore((s) => s.rfDashboard) && RFSignalEnvironment != null;
  // AdTech (Venntel/GeoNeutron) module: enabled only when the Settings->Modules
  // "adtech" flag is ON *and* the backend reports credentials configured.
  const adtechFlag = useFeatureFlagsStore((s) => s.adtech);
  const [adtechStatus, setAdtechStatus] = useState<AdtechStatus | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!adtechFlag) { setAdtechStatus(null); return; }
    pathfinderApi.getAdtechStatus()
      .then((s) => { if (!cancelled) setAdtechStatus(s); })
      .catch(() => { if (!cancelled) setAdtechStatus(null); });
    return () => { cancelled = true; };
  }, [adtechFlag]);
  const adtechEnabled: boolean = adtechFlag && !!adtechStatus?.available;

  // Overwatch (GDELT media via DataHog): enabled when the Settings->Modules
  // "overwatch" flag is ON *and* the backend reports the sandbox DB configured.
  const overwatchFlag = useFeatureFlagsStore((s) => s.overwatch);
  const [overwatchStatus, setOverwatchStatus] = useState<OverwatchStatus | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!overwatchFlag) { setOverwatchStatus(null); return; }
    pathfinderApi.getOverwatchStatus()
      .then((s) => { if (!cancelled) setOverwatchStatus(s); })
      .catch(() => { if (!cancelled) setOverwatchStatus(null); });
    return () => { cancelled = true; };
  }, [overwatchFlag]);
  const overwatchEnabled: boolean = overwatchFlag && !!overwatchStatus?.available;

  // Overwatch article markers loaded onto the map.
  const [overwatchArticles, setOverwatchArticles] = useState<OverwatchArticle[]>([]);

  // AdTech signals loaded onto the map from an Area Search, plus the bottom
  // results table (Tracer-style) that lists them. focusId highlights one device.
  const [adtechMapSignals, setAdtechMapSignals] = useState<AdtechMapSignal[]>([]);
  // Hide/show the floating results table (toolbar or table X) without
  // discarding loaded signals. DeleteSweep clears the layer entirely.
  const [adtechTableVisible, setAdtechTableVisible] = useState(true);
  const [adtechSourceLabel, setAdtechSourceLabel] = useState<string>('');
  const [adtechFocusId, setAdtechFocusId] = useState<string | null>(null);
  // Pattern-of-life load state for discovered adtech devices (single or all).
  const [adtechPolLoading, setAdtechPolLoading] = useState(false);
  const [adtechPolMessage, setAdtechPolMessage] = useState<string>('');
  // Freeform polygon search area: draw any shape on the map and run the full
  // area analysis inside it (entities, dwells, co-locations, signals, AdTech),
  // clipped to the exact ring — same capability as a drawn circle.
  const [isDrawingAdtechPolygon, setIsDrawingAdtechPolygon] = useState(false);


  // Geofences (shared table via /api/geofences). Used by the Geofence tab to
  // list/draw/select fences and to render them on the map. Drawing reuses the
  // map's annotation polygon mode (see onAnnotationDrawn routing below).
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [geofencesLoading, setGeofencesLoading] = useState(false);
  const [selectedGeofenceId, setSelectedGeofenceId] = useState<number | null>(null);
  const [isDrawingGeofence, setIsDrawingGeofence] = useState(false);
  const [pendingGeofencePolygon, setPendingGeofencePolygon] = useState<any | null>(null);
  const loadGeofences = useCallback(async () => {
    setGeofencesLoading(true);
    try {
      const gs = await geofenceService.getGeofences();
      setGeofences(gs);
    } catch (e) {
      console.warn('[PathfinderPage] geofence load failed', e);
    } finally {
      setGeofencesLoading(false);
    }
  }, []);
  useEffect(() => { loadGeofences(); }, [loadGeofences]);

  const [signalData, setSignalData] = useState<any>(null);
  const [signalLayerVisibility, setSignalLayerVisibility] = useState<any>({ wifi: true, bluetooth: true, cellular: true });
  // Empty = show all classified device types on the map / Signal Environment list.
  const [signalDeviceTypeFilter, setSignalDeviceTypeFilter] = useState<string[]>([]);
  const theme = useTheme();
  const { toggleMobileDrawer } = useLayoutContext();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Quick Launch state
  const quickLaunchProcessed = useRef(false);
  const quickLaunchInProgress = useRef(false);  // Ref to track if Quick Launch is in progress (survives re-renders)
  const [quickLaunchPendingLoad, setQuickLaunchPendingLoad] = useState(false);  // Flag to trigger load after state is set
  const [quickLaunchCoTravelers, setQuickLaunchCoTravelers] = useState<string[]>([]); // Pre-computed co-travelers to load
  const [dateRangeIsUtcInstant, setDateRangeIsUtcInstant] = useState(false);
  
  // Refs to store current values for callbacks (avoid stale closures)
  const dataPointsRef = useRef<PathfinderDataPoint[]>([]);
  const routesRef = useRef<PathfinderRoute[]>([]);
  const [quickLaunchNotification, setQuickLaunchNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
  }>({ open: false, message: '', severity: 'info' });
  
  // Responsive breakpoints
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  
  const [mobileRightPanelOpen, setMobileRightPanelOpen] = useState(false);
  const [mobileControlsOpen, setMobileControlsOpen] = useState(false);
  const [toolPaletteOpen, setToolPaletteOpen] = useState(false);
  const [mobileToolDrawerOpen, setMobileToolDrawerOpen] = useState(false);
  
  // Zustand stores for map state
  const {
    mapCenter, setMapCenter,
    mapZoom, setMapZoom,
    showDataPoints, setShowDataPoints,
    showRouteLines, setShowRouteLines,
    showDirectionArrows, setShowDirectionArrows,
    showHeatmap, setShowHeatmap,
    showDwells, setShowDwells,
    enableClustering, setEnableClustering,
    hideGaps, setHideGaps,
    hideImplausibleMovement, setHideImplausibleMovement,
    implausibleMaxSpeedKmh, setImplausibleMaxSpeedKmh,
    gapMaxTimeMinutes, gapMaxDistanceM,
    trackFiltersUserCustomized, applyOrgTrackFilters,
    showCoTravelerPoints, setShowCoTravelerPoints,
    showCoTravelerLines, setShowCoTravelerLines,
    showCoTravelerHeatmap, setShowCoTravelerHeatmap,
    showCoTravelerConnections, setShowCoTravelerConnections,
    followMode, setFollowMode,
    isDrawingMode, setIsDrawingMode,
  } = useMapStore();

  // Zustand stores for device state
  const {
    selectedDevices, setSelectedDevices,
    actualDevices, setActualDevices,
    expandedStudents, setExpandedStudents,
  } = useDeviceStore();

  // Zustand store for pathfinder session data (survives page navigation)
  const {
    hasData,
    selectedCourse, setSelectedCourse,
    selectedStudents, setSelectedStudents,
    dateRange, setDateRange,
    customDeviceColors, setCustomDeviceColors,
    defaultRadius, setDefaultRadius,
    availableCourses, setAvailableCourses,
    coursesData, setCoursesData,
    availableStudents, setAvailableStudents,
    courseDateRanges, setCourseDateRanges,
    dataPoints, setDataPoints,
    routes, setRoutes,
    dwellLocations, setDwellLocations,
    coTravelerResults, setCoTravelerResults,
    coTravelerVisibility, setCoTravelerVisibility,
    digitalPoLData, setDigitalPoLData,
    itinerarySubmissions, setItinerarySubmissions,
    visibleDataPoints, setVisibleDataPoints,
    visibleRoutes, setVisibleRoutes,
    visibleDwells, setVisibleDwells,
    visibleAreaSearchDwells, setVisibleAreaSearchDwells,
    areaSearchDataPoints, setAreaSearchDataPoints,
    areaSearchRoutes, setAreaSearchRoutes,
    areaSearchSelectedDevices, setAreaSearchSelectedDevices,
    areaSearchActualDevices, setAreaSearchActualDevices,
    areaSearchDwellLocations, setAreaSearchDwellLocations,
    areaSearchExpandedStudents: _areaSearchExpandedStudents,
    activeToolId, setActiveToolId,
    timeRange, setTimeRange,
    drawCircles, setDrawCircles,
    activeAnnotations, setActiveAnnotations,
    showDigitalPoLOnMap, setShowDigitalPoLOnMap,
    showItineraryOnMap, setShowItineraryOnMap,
    uploadedDataPoints, setUploadedDataPoints,
    uploadedRoutes, setUploadedRoutes,
    uploadedFileNames, setUploadedFileNames,
    showUploadedData, setShowUploadedData,
    uploadedFileVisibility, setUploadedFileVisibility,
    clearUploadedData,
  } = usePathfinderStore();
  const queryTimeZone = dateRangeIsUtcInstant ? undefined : preferredTimezone;

  // Local state for time and playback
  const [currentTime, setCurrentTime] = useState(new Date());
  const [pttUtterances, setPttUtterances] = useState<PathfinderPttUtterance[]>([]);
  // One colour per PTT sender, shared by the map bubbles, panel avatars and ticker.
  const pttSenderColors = useMemo(() => buildPttSenderColors(pttUtterances), [pttUtterances]);
  const [pttSearch, setPttSearch] = useState('');
  const [showPttMessages, setShowPttMessages] = useState(true);
  const [clusterPttMessages, setClusterPttMessages] = useState(true);
  // Scrub request handed to the time slider when a caption is clicked.
  const [pttSeek, setPttSeek] = useState<{ time: Date; play?: boolean; nonce: number } | null>(null);
  // Live tail: on when the loaded range reaches "now"; connected reflects the SSE socket.
  const [pttLiveEligible, setPttLiveEligible] = useState(false);
  const [pttLiveConnected, setPttLiveConnected] = useState(false);
  const [isFullExtent, setIsFullExtent] = useState(true);
  const [playbackWindow, setPlaybackWindow] = useState<{ start: Date; end: Date } | null>(null);
  const [activeFilters, _setActiveFilters] = useState({}); // eslint-disable-line @typescript-eslint/no-unused-vars

  const handleLoadAdtechToMap = useCallback((signals: AdtechMapSignal[], label?: string) => {
    setAdtechMapSignals(signals);
    setAdtechSourceLabel(label || '');
    setAdtechFocusId(null);
    setAdtechTableVisible(true);

    // Drive the bottom timeslider from AdTech observation times so playback
    // works even when no GPS tracks are loaded (and expand an existing range
    // if AdTech spans outside the current track window).
    let minMs = Infinity;
    let maxMs = -Infinity;
    for (const s of signals) {
      if (!s.timestamp) continue;
      const parsed = parseBackendDateTime(s.timestamp) || new Date(s.timestamp);
      const t = parsed.getTime();
      if (!Number.isFinite(t)) continue;
      if (t < minMs) minMs = t;
      if (t > maxMs) maxMs = t;
    }
    if (Number.isFinite(minMs) && Number.isFinite(maxMs) && minMs <= maxMs) {
      const prev = usePathfinderStore.getState().timeRange;
      if (!prev) {
        setTimeRange({ start: new Date(minMs), end: new Date(maxMs) });
      } else {
        const nextStart = Math.min(prev.start.getTime(), minMs);
        const nextEnd = Math.max(prev.end.getTime(), maxMs);
        if (nextStart !== prev.start.getTime() || nextEnd !== prev.end.getTime()) {
          setTimeRange({ start: new Date(nextStart), end: new Date(nextEnd) });
        }
      }
      setCurrentTime(new Date(maxMs));
    }
  }, [setTimeRange]);

  const handleClearAdtech = useCallback(() => {
    setAdtechMapSignals([]);
    setAdtechSourceLabel('');
    setAdtechFocusId(null);
    setAdtechTableVisible(false);
  }, []);

  const handleCloseAdtechTable = useCallback(() => {
    setAdtechTableVisible(false);
  }, []);

  const handleAdtechRowClick = useCallback((s: AdtechMapSignal) => {
    const lat = Number(s.latitude);
    const lon = Number(s.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      setMapCenter([lat, lon]);
      setMapZoom(16);
    }
    setAdtechFocusId(s.registration_id);
  }, [setMapCenter, setMapZoom]);
  
  // Chart filter state (for interactive chart filtering)
  const [chartFilter, setChartFilter] = useState<ChartFilter | null>(null);
  
  // Co-traveler encounter areas (transient)
  const [_coTravelerEncounterAreas, setCoTravelerEncounterAreas] = useState<any[]>([]);

  // Biometric HR-zone highlight points (driven by Biometrics tab → map overlay)
  const [biometricHighlights, setBiometricHighlights] = useState<Array<{
    latitude: number;
    longitude: number;
    value: number;
    zone?: string;
    color: string;
    timestamp?: string;
    label?: string;
  }>>([]);
  
  // State for storing original map extent when "zooming to" a selected item (dwell/story/digital, etc.)
  // IMPORTANT: We capture from the live Mapbox map when possible to preserve fractional zoom.
  const [originalMapExtent, setOriginalMapExtent] = useState<{
    center: [number, number];
    zoom: number;
    followMode: boolean;
  } | null>(null);

  const getLiveMapExtent = useCallback(() => {
    const map = (window as any).__pathfinderMap;
    try {
      if (map?.getCenter && map?.getZoom) {
        const c = map.getCenter();
        const z = map.getZoom();
        if (c && Number.isFinite(c.lat) && Number.isFinite(c.lng) && Number.isFinite(z)) {
          return { center: [c.lat, c.lng] as [number, number], zoom: z as number };
        }
      }
    } catch (_e) {
      // fall back to store values
    }
    return { center: [mapCenter[0], mapCenter[1]] as [number, number], zoom: mapZoom };
  }, [mapCenter, mapZoom]);
  
  // Itinerary loading (transient)
  const [_itineraryLoading, setItineraryLoading] = useState(false);
  
  // White Cell sampling interval (minutes) - null means off
  const [whiteCellInterval, setWhiteCellInterval] = useState<number | null>(null);
  
  // Load button menu state
  const [loadMenuAnchor, setLoadMenuAnchor] = useState<null | HTMLElement>(null);
  const [loadingWithCoTravelers, setLoadingWithCoTravelers] = useState(false);
  
  // Date/Time picker dialog state
  const [dateTimePickerOpen, setDateTimePickerOpen] = useState(false);
  
  // CSV/KML/JSON Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  
  // Keep refs updated to avoid stale closures in callbacks
  useEffect(() => {
    dataPointsRef.current = dataPoints;
  }, [dataPoints]);
  
  useEffect(() => {
    routesRef.current = routes;
  }, [routes]);

  // PTT follows Load: only entities whose tracks are on the map (selected plus
  // loaded co-travelers), over the range that was loaded. Changing the picker
  // without pressing Load leaves the transcript alone.
  const loadedPttRangeRef = useRef(dateRange);
  loadedPttRangeRef.current = dateRange;
  const loadedEntityKey = useMemo(() => {
    const ids = new Set<string>();
    dataPoints.forEach((p) => {
      if (p.studentId) ids.add(p.studentId);
    });
    return Array.from(ids).sort().join(',');
  }, [dataPoints]);

  useEffect(() => {
    const { start, end } = loadedPttRangeRef.current;
    if (!isCommercialBanner || !loadedEntityKey || !start || !end) {
      setPttUtterances([]);
      setPttLiveEligible(false);
      return;
    }
    // Tail live traffic when the loaded range runs up to (or past) now.
    setPttLiveEligible(end.getTime() >= Date.now() - 2 * 60_000);
    let cancelled = false;
    pttUtterancesService
      .list({
        entity_ids: loadedEntityKey,
        start: start.toISOString(),
        end: end.toISOString(),
        limit: 1000,
      })
      .then((rows) => {
        if (cancelled) return;
        const sorted = [...rows].sort(
          (a, b) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime(),
        );
        setPttUtterances(sorted);
      })
      .catch(() => {
        if (!cancelled) setPttUtterances([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isCommercialBanner, loadedEntityKey, dataPoints]);

  // Live tail over SSE, scoped to the loaded entities. Appends and de-dupes by id.
  useEffect(() => {
    if (!isCommercialBanner || !pttLiveEligible || !loadedEntityKey) {
      setPttLiveConnected(false);
      return;
    }
    const ids = new Set<string>(String(loadedEntityKey).split(',').filter(Boolean));
    const dispose = pttUtterancesService.stream({
      entityIds: ids,
      onStatus: setPttLiveConnected,
      onUtterance: (u) => {
        setPttUtterances((prev) => {
          if (prev.some((p) => p.id === u.id)) return prev;
          const next = [...prev, u];
          next.sort((a, b) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime());
          return next;
        });
      },
    });
    return dispose;
  }, [isCommercialBanner, pttLiveEligible, loadedEntityKey]);

  const activePttId = useMemo(
    () => pttUtterancesService.activeId(pttUtterances, currentTime),
    [pttUtterances, currentTime],
  );

  /** Caption currently being spoken at the playback time (for the map ticker). */
  const nowSpeakingPtt = useMemo(() => {
    if (!currentTime || isFullExtent) return null;
    const t = currentTime.getTime();
    return (
      pttUtterances.find((u) => {
        const s = new Date(u.started_at).getTime();
        const e = new Date(u.ended_at || u.started_at).getTime();
        return s <= t && t <= e;
      }) ?? null
    );
  }, [pttUtterances, currentTime, isFullExtent]);

  
  // Right panel state - responsive default width
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const getDefaultRightPanelWidth = () => {
    if (typeof window !== 'undefined') {
      // Trimmed ~15% across the board so the map column has more room and
      // the bottom timeline controls stop being clipped by the panel edge.
      // The user can still drag the resize handle wider if they want.
      if (window.innerWidth < 900) return 280;  // Smaller on tablets
      if (window.innerWidth < 1200) return 460; // Medium
      if (window.innerWidth < 1536) return 540; // Large
      return 620; // Extra large
    }
    return 540;
  };
  const [rightPanelWidth, setRightPanelWidth] = useState(getDefaultRightPanelWidth);
  const [isResizing, setIsResizing] = useState(false);

  // "Crop to map view" — when enabled, right-side tables (Dwell Analysis,
  // Digital PoL, …) are filtered to entities whose geometry sits inside the
  // current map viewport. The map itself is unaffected.
  const [filterByMapExtent, setFilterByMapExtent] = useState(false);
  // Single source of truth for the consolidated Map Settings dialog (layers
  // + playback). Lifted up here so both the map's toolbar button and the
  // time slider's gear can flip the same boolean.
  const [mapSettingsOpen, setMapSettingsOpen] = useState(false);
  // Camera overlay visibility is owned here so the Map Settings dialog (mounted
  // by CompactTimeSlider) and the map toolbar button stay in sync.
  const [showCamerasOnMap, setShowCamerasOnMap] = useState(false);
  const [cameraCountOnMap, setCameraCountOnMap] = useState(0);
  // Camera filter controls published by the map (facets, near-track prefs) for the dialog.
  const [cameraControlsOnMap, setCameraControlsOnMap] = useState<PathfinderCameraControls | null>(null);
  const [showDwellLabelsOnMap, setShowDwellLabelsOnMap] = useState(false);
  // Map "fit to bbox" registered by PathfinderMapGL; used by Map Settings →
  // Reference layers → zoom-to.
  const fitBoundsRef = useRef<((bbox: [number, number, number, number]) => void) | null>(null);
  const registerFitBounds = useCallback((fn: ((bbox: [number, number, number, number]) => void) | null) => {
    fitBoundsRef.current = fn;
  }, []);
  const zoomToBounds = useCallback((bbox: [number, number, number, number]) => {
    fitBoundsRef.current?.(bbox);
  }, []);
  const [highlightClosestSrp, setHighlightClosestSrp] = useState(false);
  // Latest org track-filter defaults from system_settings (for seed / reset)
  const orgTrackFiltersRef = useRef<{
    hideImplausibleMovement: boolean;
    implausibleMaxSpeedKmh: number;
    hideGaps: boolean;
    gapMaxTimeMinutes: number;
    gapMaxDistanceM: number;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiService.client.get('/admin/system-settings/pathfinder-track-filters', {
          timeout: 30000,
        });
        if (cancelled || !resp?.data) return;
        const org = {
          hideImplausibleMovement: !!resp.data.hide_implausible_movement,
          implausibleMaxSpeedKmh: Number(resp.data.implausible_max_speed_kmh) || 160,
          hideGaps: !!resp.data.hide_gaps_default,
          gapMaxTimeMinutes: Number(resp.data.gap_max_time_minutes) || 30,
          gapMaxDistanceM: Number(resp.data.gap_max_distance_m) || 500,
        };
        orgTrackFiltersRef.current = org;
        if (!useMapStore.getState().trackFiltersUserCustomized) {
          applyOrgTrackFilters(org);
        }
      } catch (e) {
        console.warn('[PathfinderPage] Failed to load org track filters; using local defaults', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyOrgTrackFilters]);

  const handleUseOrgTrackFilterDefaults = useCallback(async () => {
    try {
      const resp = await apiService.client.get('/admin/system-settings/pathfinder-track-filters', {
        timeout: 30000,
      });
      if (resp?.data) {
        const org = {
          hideImplausibleMovement: !!resp.data.hide_implausible_movement,
          implausibleMaxSpeedKmh: Number(resp.data.implausible_max_speed_kmh) || 160,
          hideGaps: !!resp.data.hide_gaps_default,
          gapMaxTimeMinutes: Number(resp.data.gap_max_time_minutes) || 30,
          gapMaxDistanceM: Number(resp.data.gap_max_distance_m) || 500,
        };
        orgTrackFiltersRef.current = org;
        applyOrgTrackFilters(org);
        return;
      }
    } catch (e) {
      console.warn('[PathfinderPage] Re-fetch org track filters failed; using cached', e);
    }
    if (orgTrackFiltersRef.current) {
      applyOrgTrackFilters(orgTrackFiltersRef.current);
    }
  }, [applyOrgTrackFilters]);

  const [mapBounds, setMapBounds] = useState<{
    west: number;
    south: number;
    east: number;
    north: number;
  } | null>(null);
  const handleMapBoundsChange = useCallback(
    (bounds: { west: number; south: number; east: number; north: number } | null) => {
      setMapBounds(bounds);
    },
    [],
  );
  const isInMapBounds = useCallback(
    (lat?: number | null, lon?: number | null): boolean => {
      if (!mapBounds) return true;
      if (lat == null || lon == null || !Number.isFinite(lat) || !Number.isFinite(lon)) return false;
      const { west, south, east, north } = mapBounds;
      if (lat < south || lat > north) return false;
      // Antimeridian-spanning viewport: bounds wrap around so west > east.
      return west <= east ? lon >= west && lon <= east : lon >= west || lon <= east;
    },
    [mapBounds],
  );
  
  // Left panel width state
  const getDefaultLeftPanelWidth = () => {
    if (isSmallScreen) return 280;
    if (window.innerWidth < 1200) return 300;
    return 320;
  };
  const [leftPanelWidth, setLeftPanelWidth] = useState(getDefaultLeftPanelWidth);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [isResizingLeft, setIsResizingLeft] = useState(false);
  const mapTimelineBoundsRef = useRef<HTMLDivElement | null>(null);
  const timelineOverlayRef = useRef<HTMLDivElement | null>(null);
  // Portal target inside the timeline card for the map's cameras-in-view strip.
  const [camerasInViewDockEl, setCamerasInViewDockEl] = useState<HTMLDivElement | null>(null);
  const timelineDragRef = useRef<{ offsetX: number; offsetY: number } | null>(null);
  // Anchored by `bottom` (not `top`) so that when the card grows — e.g. the
  // cameras-in-view strip docks into it — it expands upward over the map and
  // the slider never gets pushed below the frame.
  const [timelinePosition, setTimelinePosition] = useState<{ left: number; bottom: number } | null>(null);
  // Collapse the timeline card to a slim pill (handy in fullscreen / for
  // screenshots). The slider stays mounted so playback state is preserved.
  const [timelineMinimized, setTimelineMinimized] = useState(false);
  

  // Left panel tabs: 0 = Students, 1 = Area Search
  const [leftPanelTab, setLeftPanelTab] = useState(0);
  
  // Per-area device selection state (keyed by circle ID, independent per area)
  const [perAreaSelectedDevices, setPerAreaSelectedDevices] = useState<Record<string, Record<string, string[]>>>({});
  /** Entity accordion open/closed per search area (matches Entities tab behavior). */
  const [perAreaExpandedEntities, setPerAreaExpandedEntities] = useState<Record<string, Record<string, boolean>>>({});

  // Memoize student data to prevent rebuilding during every time update
  const memoizedStudentData = useMemo(() => {
    return Object.entries(actualDevices).map(([studentId, devices], index) => ({
      id: studentId,
      name: availableStudents.find(s => s.id === studentId)?.name || studentId,
      devices: devices as any[], // Type assertion for store compatibility
      expanded: expandedStudents[studentId] ?? (index === 0), // First student expanded by default
      selected: selectedStudents.includes(studentId),
    }));
  }, [actualDevices, availableStudents, selectedStudents, expandedStudents]);



  // Compute the most recent DHOST and DHOQC courses for highlighting
  const mostRecentCourses = useMemo(() => {
    const parseCourseVersion = (courseId: string) => {
      // Extract year and number from course ID like "DHOST 26-002" or "DHOQC 25-001"
      const match = courseId.match(/(\d{2})-(\d+)$/);
      if (match) {
        return { year: parseInt(match[1], 10), number: parseInt(match[2], 10) };
      }
      return null;
    };

    const findMostRecent = (prefix: string) => {
      const matchingCourses = availableCourses.filter(c => c.id.toUpperCase().startsWith(prefix));
      if (matchingCourses.length === 0) return null;
      
      let mostRecent = matchingCourses[0];
      let mostRecentVersion = parseCourseVersion(mostRecent.id);
      
      for (const course of matchingCourses) {
        const version = parseCourseVersion(course.id);
        if (version && mostRecentVersion) {
          // Compare year first, then number
          if (version.year > mostRecentVersion.year || 
              (version.year === mostRecentVersion.year && version.number > mostRecentVersion.number)) {
            mostRecent = course;
            mostRecentVersion = version;
          }
        }
      }
      return mostRecent?.id || null;
    };

    return {
      dhost: findMostRecent('DHOST'),
      dhoqc: findMostRecent('DHOQC'),
    };
  }, [availableCourses]);

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [_availableDateRange, setAvailableDateRange] = useState<{ min: Date | null; max: Date | null }>({ min: null, max: null });

  // Annotation drawing state (transient flags stay local)
  const [isDrawingAnnotation, setIsDrawingAnnotation] = useState(false);
  const [drawnAnnotationGeometry, setDrawnAnnotationGeometry] = useState<any>(null);
  const filterByAnnotations = true;
  const [pendingAnnotationLoad, setPendingAnnotationLoad] = useState<any>(null);

  // Sentinel area analysis drawing state
  const [isDrawingAnalysisArea, setIsDrawingAnalysisArea] = useState(false);
  const [analysisArea, setAnalysisArea] = useState<GeoJSON.Polygon | null>(null);

  // Convert drawn annotation geometry to analysis area when in Sentinel mode
  useEffect(() => {
    if (isDrawingAnalysisArea && drawnAnnotationGeometry) {
      // Convert the drawn geometry to GeoJSON Polygon format
      if (drawnAnnotationGeometry.type === 'Polygon' || drawnAnnotationGeometry.coordinates) {
        setAnalysisArea(drawnAnnotationGeometry as GeoJSON.Polygon);
        setIsDrawingAnalysisArea(false); // Exit drawing mode
        setIsDrawingAnnotation(false); // Exit annotation drawing mode
      }
    }
  }, [drawnAnnotationGeometry, isDrawingAnalysisArea]);

  // Point-in-polygon utility (ray casting algorithm)
  const isPointInPolygon = useCallback((point: [number, number], polygon: number[][]) => {
    if (!polygon || polygon.length < 3) return false;
    
    const [lat, lng] = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [lngI, latI] = polygon[i];  // GeoJSON is [lng, lat]
      const [lngJ, latJ] = polygon[j];
      
      if (((latI > lat) !== (latJ > lat)) &&
          (lng < (lngJ - lngI) * (lat - latI) / (latJ - latI) + lngI)) {
        inside = !inside;
      }
    }
    
    return inside;
  }, []);

// Check if a point is in ANY of the active annotation polygons (unused helper)
// const isPointInActiveAnnotations = useCallback((point: [number, number], entityId: string) => {
//   if (activeAnnotations.length === 0) return true; // No filter active
//   
//   return activeAnnotations.some(annotation => {
//     // Check entity match
//     if (annotation.entity_id !== entityId) return false;
//     
//     // Check polygon if present
//     if (annotation.geometry?.coordinates?.[0]) {
//       return isPointInPolygon(point, annotation.geometry.coordinates[0]);
//     }
//     
//     // No polygon - just entity match is enough
//     return true;
//   });
// }, [activeAnnotations, isPointInPolygon]);

  const selectTool = useCallback((toolId: string) => {
    setActiveToolId(toolId);
    if (rightPanelCollapsed) setRightPanelCollapsed(false);
    if (isSmallScreen) setMobileRightPanelOpen(true);
  }, [rightPanelCollapsed, isSmallScreen, setActiveToolId]);

  // Right panel resize handlers
  const handleRightPanelToggle = () => {
    setRightPanelCollapsed(!rightPanelCollapsed);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Left panel resize handler
  const handleLeftPanelMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingLeft(true);
  };

  const handleTimelineDragStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const boundsEl = mapTimelineBoundsRef.current;
    const overlayEl = timelineOverlayRef.current;
    if (!boundsEl || !overlayEl) return;

    e.preventDefault();
    const boundsRect = boundsEl.getBoundingClientRect();
    const overlayRect = overlayEl.getBoundingClientRect();
    timelineDragRef.current = {
      offsetX: e.clientX - overlayRect.left,
      // Distance from the pointer to the card's bottom edge (bottom-anchored).
      offsetY: overlayRect.bottom - e.clientY,
    };

    const clampPosition = (clientX: number, clientY: number) => {
      const currentBounds = boundsEl.getBoundingClientRect();
      const currentOverlay = overlayEl.getBoundingClientRect();
      const padding = 8;
      const maxLeft = Math.max(padding, currentBounds.width - currentOverlay.width - padding);
      const maxBottom = Math.max(padding, currentBounds.height - currentOverlay.height - padding);
      const drag = timelineDragRef.current;
      if (!drag) return;
      setTimelinePosition({
        left: Math.min(Math.max(clientX - currentBounds.left - drag.offsetX, padding), maxLeft),
        bottom: Math.min(Math.max(currentBounds.bottom - clientY - drag.offsetY, padding), maxBottom),
      });
    };

    // Pin the overlay to its current rendered position before movement so
    // dragging starts cleanly from the default bottom-center placement.
    setTimelinePosition({
      left: overlayRect.left - boundsRect.left,
      bottom: boundsRect.bottom - overlayRect.bottom,
    });

    const handlePointerMove = (event: PointerEvent) => {
      clampPosition(event.clientX, event.clientY);
    };

    const handlePointerUp = () => {
      timelineDragRef.current = null;
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
  }, []);

  // Placement strategy when the map column changes size:
  //  - Entering/leaving fullscreen: a position dragged in the narrow layout is
  //    meaningless in the new geometry, so snap back to the default
  //    bottom-centre placement.
  //  - Any other resize of the map or the card itself (e.g. the cameras-in-view
  //    strip docking in): re-clamp a custom position so the card stays fully
  //    inside the map frame.
  useEffect(() => {
    const boundsEl = mapTimelineBoundsRef.current;
    const overlayEl = timelineOverlayRef.current;
    if (!boundsEl || !overlayEl) return;

    const onFullscreenChange = () => setTimelinePosition(null);

    const reclamp = () => {
      if (timelineDragRef.current) return; // drag handler owns clamping while dragging
      setTimelinePosition((pos) => {
        if (!pos) return pos;
        const b = boundsEl.getBoundingClientRect();
        const o = overlayEl.getBoundingClientRect();
        if (b.width === 0 || b.height === 0) return pos;
        const padding = 8;
        const maxLeft = Math.max(padding, b.width - o.width - padding);
        const maxBottom = Math.max(padding, b.height - o.height - padding);
        const left = Math.min(Math.max(pos.left, padding), maxLeft);
        const bottom = Math.min(Math.max(pos.bottom, padding), maxBottom);
        return left === pos.left && bottom === pos.bottom ? pos : { left, bottom };
      });
    };

    document.addEventListener('fullscreenchange', onFullscreenChange);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(reclamp) : null;
    ro?.observe(boundsEl);
    ro?.observe(overlayEl);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      ro?.disconnect();
    };
  }, []);

  const pathfinderMapSettings = useMemo(() => ({
    center: mapCenter,
    zoom: mapZoom,
    style: 'streets',
  }), [mapCenter, mapZoom]);

  // Side panels should never reload or remount the map. The Mapbox component
  // owns a debounced ResizeObserver that fires map.resize() once after the
  // CSS width transition finishes, so we deliberately do NOT call map.resize()
  // here on every frame; that's what produces the white flash that reads as
  // a "reload".

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Handle right panel resize
      if (isResizing) {
        const newWidth = window.innerWidth - e.clientX;
        if (newWidth >= 300 && newWidth <= 900) {
          setRightPanelWidth(newWidth);
        }
      }
      
      // Handle left panel resize
      if (isResizingLeft) {
        const newWidth = e.clientX;
        if (newWidth >= 220 && newWidth <= 500) {
          setLeftPanelWidth(newWidth);
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setIsResizingLeft(false);
    };

    if (isResizing || isResizingLeft) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, isResizingLeft]);

  // Area search / circle drawing handlers
  const handleDrawModeChange = useCallback((enabled: boolean) => {
    setIsDrawingMode(enabled);
  }, []);

  const handleCircleAdd = useCallback((circle: DrawCircle) => {
    setDrawCircles(prev => [...prev, circle]);
  }, []);

  const handleCircleUpdate = useCallback((updatedCircle: DrawCircle) => {
    setDrawCircles(prev => prev.map(circle => 
      circle.id === updatedCircle.id ? updatedCircle : circle
    ));
  }, []);

  const handleCircleDelete = useCallback((circleId: string) => {
    setDrawCircles(prev => prev.filter(circle => circle.id !== circleId));
    setPerAreaSelectedDevices(prev => {
      const next = { ...prev };
      delete next[circleId];
      return next;
    });
    setPerAreaExpandedEntities(prev => {
      const next = { ...prev };
      delete next[circleId];
      return next;
    });
  }, []);

  // Handle Digital PoL data update
  const handleDigitalPoLDataChange = useCallback((data: any[], showOnMap: boolean) => {
    setDigitalPoLData(data);
    setShowDigitalPoLOnMap(showOnMap);
  }, []);

  const handleCircleDrawn = useCallback((center: [number, number], radius: number) => {
    const newCircle: DrawCircle = {
      id: `circle-${Date.now()}`,
      center,
      radius,
      name: `Area ${drawCircles.length + 1}`,
      visible: true,
      results: [],
      loading: false,
    };
    handleCircleAdd(newCircle);
    setIsDrawingMode(false); // Exit drawing mode after creating circle
  }, [drawCircles.length, handleCircleAdd]);

  // Handle loading dwells from area search
  const handleLoadAreaDwells = useCallback((areaDwells: any[], circleId: string) => {
    
    if (areaDwells.length === 0) {
      return;
    }
    
    // Convert DwellInsight format to dwell location format for map (red circles)
    const dwellLocationsData = areaDwells.map((insight, idx) => {
      const dwellLocation = {
        id: `${circleId}-${idx}`,
        latitude: insight.coordinates[0],         // Map expects 'latitude' not 'center_latitude'
        longitude: insight.coordinates[1],        // Map expects 'longitude' not 'center_longitude'
        center_latitude: insight.coordinates[0],  // Keep for compatibility
        center_longitude: insight.coordinates[1], // Keep for compatibility
        total_visits: insight.dwellCount,
        total_duration: insight.totalDwellTime,
        average_duration: insight.totalDwellTime / insight.dwellCount,
        duration_seconds: insight.totalDwellTime,
        place_name: insight.placeName || 'Unknown Location',
        place_type: insight.placeType || 'unknown',
        students: [insight.entityId],
        entity_id: insight.entityId,
        device_type: insight.deviceType,
        radius: 50,  // Default radius for dwell circle display
        // Add start_time and end_time for time-based filtering
        start_time: insight.longestDwell.startTime.toISOString(),
        end_time: insight.longestDwell.endTime.toISOString(),
        areaSearchCircleId: circleId,
      };
      
      return dwellLocation;
    });
    
    // Merge per circle (replacing prior rows for this circle only). Store setters take arrays only, not updaters.
    const store = usePathfinderStore.getState();
    const mergedMain = [
      ...store.areaSearchDwellLocations.filter((d: any) => d.areaSearchCircleId !== circleId),
      ...dwellLocationsData,
    ];
    const mergedVisible = [
      ...store.visibleAreaSearchDwells.filter((d: any) => d.areaSearchCircleId !== circleId),
      ...dwellLocationsData,
    ];
    setAreaSearchDwellLocations(mergedMain);
    setVisibleAreaSearchDwells(mergedVisible);
    setShowDwells(true);  // Ensure dwells are visible
    
    // Switch to Area Search tab
    setLeftPanelTab(1);
  }, []);

  // Handle loading data for entities found in area search
  // Stores data in SEPARATE area search state (not main student data)
  const handleLoadAreaData = useCallback(async (areaDataPoints: PathfinderDataPoint[], circleId: string) => {
        
        // Ensure coordinates are in the right format for MapGL
        const validatedDataPoints = areaDataPoints.map(point => {
          // Ensure coordinates are numbers and in [lat, lng] format
          if (!point.coordinates || !Array.isArray(point.coordinates) || point.coordinates.length < 2) {
            return null;
          }
          
          const lat = parseFloat(String(point.coordinates[0]));
          const lng = parseFloat(String(point.coordinates[1]));
          
          if (isNaN(lat) || isNaN(lng)) {
            return null;
          }
          
          return {
            ...point,
            coordinates: [lat, lng] as [number, number],
            areaSearchCircleId: circleId,
          };
        }).filter(p => p !== null) as PathfinderDataPoint[];
    
    // Use the validated data points directly from the area search
    setLoading(true);
    setLoadingMessage('Processing area search data...');
    try {
      // Read latest state directly from store (avoids stale closure in useCallback)
      const store = usePathfinderStore.getState();
      
      // Merge with existing area search data (accumulate across multiple areas; dedupe per circle)
      const pointKey = (p: PathfinderDataPoint) => {
        const ts = p.timestamp instanceof Date ? p.timestamp.getTime() : new Date(p.timestamp as unknown as string).getTime();
        return p.id || `${p.deviceId}-${ts}-${p.areaSearchCircleId ?? ''}`;
      };
      const existingIds = new Set(store.areaSearchDataPoints.map((p: any) => pointKey(p as PathfinderDataPoint)));
      const newPoints = validatedDataPoints.filter((p: any) => !existingIds.has(pointKey(p)));
      const mergedPoints = [...store.areaSearchDataPoints, ...newPoints];
      setAreaSearchDataPoints(mergedPoints);
      
      // Process ALL accumulated points into routes
      const newRoutes = pathfinderApi.generateRoutes(mergedPoints);
      setAreaSearchRoutes(newRoutes);
      
      // Extract devices and auto-select them for area search
      const deviceMap: Record<string, any[]> = {};
      const selectedDeviceMap: Record<string, string[]> = {};
      
      // Build device map from the validated points
      const devicePointCounts = new Map<string, number>();
      validatedDataPoints.forEach(point => {
        const studentId = point.studentId || point.deviceId;
        const rawType = (point.deviceType || 'phone').toLowerCase();
        const deviceType = rawType.includes('phone') ? 'phone' : rawType;
        const uniqueDeviceId = `${studentId}-${deviceType}`;
        devicePointCounts.set(uniqueDeviceId, (devicePointCounts.get(uniqueDeviceId) || 0) + 1);
      });
      
      const processedDevices = new Set<string>();
      validatedDataPoints.forEach(point => {
          const studentId = point.studentId || point.deviceId;
          const rawType = (point.deviceType || 'phone').toLowerCase();
          const deviceType = rawType.includes('phone') ? 'phone' : rawType;
          const uniqueDeviceId = `${studentId}-${deviceType}`;
          
          if (!processedDevices.has(uniqueDeviceId)) {
            processedDevices.add(uniqueDeviceId);
            
            if (!deviceMap[studentId]) {
              deviceMap[studentId] = [];
              selectedDeviceMap[studentId] = [];
            }
            
            const pointCount = devicePointCounts.get(uniqueDeviceId) || 0;
            const isAdtech = (point.metadata as any)?.source === 'adtech';
            const device = {
              id: uniqueDeviceId,
              type: deviceType,
              name: `${deviceType.charAt(0).toUpperCase() + deviceType.slice(1)}`,
              active: true,
              color: isAdtech
                ? colorForAdtechId(studentId)
                : resolvePathfinderColor({ deviceType }),
              lastSeen: point.timestamp,
              coordinates: point.coordinates,
              dataPointCount: pointCount,
              totalDataPoints: validatedDataPoints.length,
            };
            
            deviceMap[studentId].push(device);
            selectedDeviceMap[studentId].push(uniqueDeviceId); // Auto-select all devices
          }
        });
        
      // Merge with existing area search devices (read latest from store)
      const currentDevices = usePathfinderStore.getState().areaSearchActualDevices;
      const mergedDevices = { ...currentDevices };
      Object.entries(deviceMap).forEach(([studentId, devices]) => {
        if (!mergedDevices[studentId]) {
          mergedDevices[studentId] = devices;
        } else {
          const existingIds = new Set(mergedDevices[studentId].map((d: any) => d.id));
          devices.forEach((d: any) => { if (!existingIds.has(d.id)) mergedDevices[studentId].push(d); });
        }
      });
      setAreaSearchActualDevices(mergedDevices);

      const currentSelected = usePathfinderStore.getState().areaSearchSelectedDevices;
      const mergedSelected = { ...currentSelected };
      Object.entries(selectedDeviceMap).forEach(([studentId, deviceIds]) => {
        if (!mergedSelected[studentId]) {
          mergedSelected[studentId] = deviceIds;
        } else {
          const existingIds = new Set(mergedSelected[studentId]);
          deviceIds.forEach(id => { if (!existingIds.has(id)) mergedSelected[studentId].push(id); });
        }
      });
      setAreaSearchSelectedDevices(mergedSelected);
      
      // Set time range if we have validated data
      if (validatedDataPoints.length > 0) {
        // Use reduce instead of spread operator to avoid stack overflow on large datasets
        let minTimeMs = Infinity;
        let maxTimeMs = -Infinity;
        validatedDataPoints.forEach((p: PathfinderDataPoint) => {
          const time = p.timestamp.getTime();
          if (time < minTimeMs) minTimeMs = time;
          if (time > maxTimeMs) maxTimeMs = time;
        });
        const minTime = new Date(minTimeMs);
        const maxTime = new Date(maxTimeMs);
        setTimeRange({ start: minTime, end: maxTime });
        setCurrentTime(maxTime);
        
        // Show all tracks initially after area search load
        setShowDataPoints(true);
        setShowRouteLines(true);
      }
      
      // Switch to Area Search tab to show the results
      setLeftPanelTab(1);
      
    } catch (error) {
      console.error('❌ Failed to load area search data:', error);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }, []);

  // Data loading functions (matching Analysis tab pattern with database tags)
  const loadDatabases = async () => {
    // Database loading removed - no longer needed
  };

  const loadCourses = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Loading courses...');
      
      // OPTIMIZED: Fetch courses and date ranges in parallel
      const [courses, dateRanges] = await Promise.all([
        apiService.getSentinelCourses(),
        apiService.getCourseDateRanges().catch((error) => {
          console.error('❌ PathfinderPage: Failed to load course date ranges:', error);
          return {}; // Return empty object on error
        })
      ]);
      
      // Store the raw courses data (course ID -> students array mapping)
      setCoursesData(courses);

      // Transform to course options for the dropdown
      const transformedCourses = Object.keys(courses).map(key => ({
        id: key,
        title: key
      }));

      setAvailableCourses(transformedCourses);
      setCourseDateRanges(dateRanges);
      
      // Only reset course selection if the currently selected course is no longer available
      // This preserves the user's selection when courses are reloaded
      setSelectedCourse(prev => {
        if (!prev) {
          // No course was selected, keep it undefined
          return undefined;
        }
        const courseStillExists = transformedCourses.some(c => c.id === prev);
        if (courseStillExists) {
          return prev; // Keep the current selection if still valid
        } else {
          return undefined; // Reset if course no longer exists
        }
      });
      
      // Clear students if course was reset (will be handled by useEffect)
      // Don't clear here to avoid race conditions
    } catch (error) {
      console.error('❌ PathfinderPage: Failed to load courses:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update available date range when course changes
  useEffect(() => {
    if (selectedCourse && courseDateRanges[selectedCourse]) {
      const range = courseDateRanges[selectedCourse];
      // Course min/max are UTC-at-rest (often naive ISO). Do not use raw Date() —
      // that treats timezone-less strings as local and shifts the window by the offset.
      const minDate = parseUtcInstant(range.min_date);
      const maxDate = parseUtcInstant(range.max_date);

      if (!minDate || !maxDate) {
        setAvailableDateRange({ min: null, max: null });
        return;
      }

      setAvailableDateRange({ min: minDate, max: maxDate });
      
      // Keep user's selected date range (default: last 7 days)
      // Only set to course range if dates are null or outside course bounds
      setDateRange(prev => {
        const defaultEnd = maxDate;
        const defaultStart = new Date(maxDate);
        defaultStart.setDate(defaultStart.getDate() - 7);
        const finalStart = defaultStart < minDate ? minDate : defaultStart;
        const defaultCourseRange = { start: finalStart, end: defaultEnd };

        // If user already has dates selected, keep them
        if (prev.start && prev.end) {
          const rangeStartsAfterCourse = prev.start > maxDate;
          const rangeEndsBeforeCourse = prev.end < minDate;

          // If the existing range is outside this course, move both dates together.
          if (rangeStartsAfterCourse || rangeEndsBeforeCourse) {
            return defaultCourseRange;
          }

          // Clamp partially-overlapping ranges to course bounds.
          const clampedStart = prev.start < minDate ? minDate : prev.start;
          const clampedEnd = prev.end > maxDate ? maxDate : prev.end;
          return { start: clampedStart, end: clampedEnd };
        }
        
        // No dates selected - default to last 7 days of course
        return defaultCourseRange;
      });
    } else {
      // No course selected, allow any date range
      setAvailableDateRange({ min: null, max: null });
    }
  }, [selectedCourse, courseDateRanges]);

  const loadStudents = async () => {
    if (!selectedCourse || !coursesData[selectedCourse]) {
      setAvailableStudents([]);
      // Only clear selected students if NOT in a Quick Launch flow
      if (!quickLaunchInProgress.current && !quickLaunchPendingLoad) {
        setSelectedStudents([]);
      }
      return;
    }

    try {
      setLoading(true);
      // Extract students from the stored courses data
      const studentIds = coursesData[selectedCourse];
      
      // Convert student IDs to PathfinderStudent format
      const students: PathfinderStudent[] = studentIds.map(studentId => ({
        id: studentId,
        name: studentId,
        devices: [],
        course: selectedCourse,
        status: 'active' as const
      }));
      
      setAvailableStudents(students);
      // NOTE: Don't clear selectedStudents here - that's handled by the dedicated useEffect
      // Clearing here interferes with Quick Launch which sets selectedStudents before this runs
    } catch (error) {
      console.error('Failed to load students:', error);
    } finally {
      setLoading(false);
    }
  };

  // Lightweight device extraction
  const extractDevicesEfficiently = async (points: PathfinderDataPoint[]) => {
    setLoadingMessage('Organizing device information...');
    
    // Use Set for efficient device tracking
      const devicesByStudent: Record<string, any[]> = {};
      const deviceSelectionState: Record<string, string[]> = {};
    const seenDevices = new Set<string>();
    
    // Debug: Check what device types we actually have
    // const deviceTypeSample = points.slice(0, 10).map(p => ({
    //   deviceType: p.deviceType,
    //   studentId: p.studentId,
    //   deviceId: p.deviceId
    // }));
      
    // Single pass through data (already sorted by server/database)
    points.forEach(point => {
        const studentId = point.studentId || point.deviceId;
        const rawType = (point.deviceType || 'phone').toLowerCase();
        const deviceType = rawType.includes('phone') ? 'phone' : rawType;
      const uniqueDeviceId = `${studentId}-${deviceType}`;
      
      // Only process unique devices (Set lookup is O(1))
      if (!seenDevices.has(uniqueDeviceId)) {
        seenDevices.add(uniqueDeviceId);
        
        if (!devicesByStudent[studentId]) {
          devicesByStudent[studentId] = [];
          deviceSelectionState[studentId] = [];
        }
        
          const isAdtech = (point.metadata as any)?.source === 'adtech';
          const newDevice = {
            id: uniqueDeviceId,
            type: deviceType,
            name: `${deviceType.charAt(0).toUpperCase() + deviceType.slice(1)}`,
            active: true,
            color: isAdtech
              ? colorForAdtechId(studentId)
              : resolvePathfinderColor({ deviceType }),
            lastSeen: point.timestamp,
            coordinates: point.coordinates,
            dataPointCount: 1, // Will be calculated properly below
            totalDataPoints: points.length
          };
          devicesByStudent[studentId].push(newDevice);
          deviceSelectionState[studentId].push(uniqueDeviceId);
        }
      });
      
      // Calculate actual data point counts for each device
      const devicePointCounts = new Map<string, number>();
      points.forEach(point => {
        const studentId = point.studentId || point.deviceId;
        const rawType2 = (point.deviceType || 'phone').toLowerCase();
        const deviceType = rawType2.includes('phone') ? 'phone' : rawType2;
        const uniqueDeviceId = `${studentId}-${deviceType}`;
        devicePointCounts.set(uniqueDeviceId, (devicePointCounts.get(uniqueDeviceId) || 0) + 1);
      });

      // Update devices with actual point counts
      Object.keys(devicesByStudent).forEach(studentId => {
        devicesByStudent[studentId].forEach(device => {
          const pointCount = devicePointCounts.get(device.id) || 0;
          device.dataPointCount = pointCount;
          device.totalDataPoints = points.length;
        });
      });

      setActualDevices(devicesByStudent);
      setSelectedDevices(deviceSelectionState);
      
      // Automatically add all students found in data to selectedStudents
      // Students cannot be untoggled, so they should always be selected when data is loaded
      const studentIds = Object.keys(devicesByStudent);
      setSelectedStudents(prev => {
        const newStudents = [...prev];
        studentIds.forEach(studentId => {
          if (!newStudents.includes(studentId)) {
            newStudents.push(studentId);
          }
        });
        return newStudents;
      });
    
    // Generate routes (server-side sorted data makes this efficient)
    setLoadingMessage('Generating route visualization...');
    const generatedRoutes = pathfinderApi.generateRoutes(points);
    setRoutes(generatedRoutes);
    
    // Set time range and bounds (using already-sorted data)
    if (points.length > 0) {
      const startTime = points[0].timestamp;
      const endTime = points[points.length - 1].timestamp;
      setTimeRange({ start: startTime, end: endTime });
      setCurrentTime(startTime);
      
      // MapGL handles bounds automatically, no manual bounds calculation needed
    }
  };

  // STEP 2: Load COMPLETE GPS tracks for all identified co-traveler devices
  // This loads the FULL Pattern of Life for the ENTIRE selected timespan
  // NOT just the encounter areas where they met the primary subject
  // Optional primaryPoints parameter allows passing fresh data when state hasn't updated yet
  const loadCoTravelerTracks = useCallback(async (coTravelerResults: any[], primaryPoints?: PathfinderDataPoint[], primaryRoutes?: PathfinderRoute[]) => {
    try {
      // Extract unique co-traveler device IDs from the analysis results
      const coTravelerDeviceIds = Array.from(new Set(
        coTravelerResults.map(result => result.coTravelerDevice)
      ));
      
      // Create a mapping from device ID to entity type for color assignment
      const deviceToEntityType = new Map<string, string>();
      coTravelerResults.forEach(result => {
        deviceToEntityType.set(result.coTravelerDevice, result.coTravelerEntityType || 'Unknown');
      });
      
      if (coTravelerDeviceIds.length === 0) {
        console.warn('🚗 [loadCoTravelerTracks] No device IDs found, returning');
        return;
      }
      
      // Extract entity IDs from device IDs (e.g., "Student-07 - Phone" -> "Student-07")
      const coTravelerEntityIds = Array.from(new Set(
        coTravelerDeviceIds.map(deviceId => {
          // IMPORTANT: For Students, we need the entity ID (e.g., "Student-12")
          // not the device ID (e.g., "Student-12-phone")
          
          // Handle different device ID formats with spaces: "Student-04 - Vehicle", "RP-48 - Phone"
          if (deviceId.includes(' - ')) {
            const entityId = deviceId.split(' - ')[0]; // Take everything before " - "
            return entityId;
          }
          
          // Handle old format without spaces: "Student-07-phone"
          if (deviceId.includes('-phone') || deviceId.includes('-watch') || deviceId.includes('-vehicle')) {
            const entityId = deviceId.split('-').slice(0, -1).join('-');
            return entityId;
          }
          
          // If it starts with "Student-" but has no device suffix, it's already an entity ID
          if (deviceId.startsWith('Student-')) {
            return deviceId;
          }
          
          // For entities that don't have device suffixes (like pure SRP, RP)
          return deviceId;
        })
      ));
      
      // CRITICAL: Query GPS data for co-traveler ENTITIES using the FULL selected date range
      // This MUST load ALL GPS points for the entire timespan, not just encounter areas
      // We want COMPLETE tracks for full Pattern of Life playback
      //
      // IMPORTANT: Do NOT filter by course! SRP and RP entities may be in different courses
      // or have no course assigned. We want their FULL tracks regardless of course assignment.
      const coTravelerQuery = {
        dateRange: [dateRange.start!, dateRange.end!] as [Date, Date],
        timeZone: queryTimeZone,
        studentIds: coTravelerEntityIds, // Use entity IDs to get ALL their data
        selectedCourse: undefined, // NO COURSE FILTER - SRP/RP may be in different courses!
        deviceTypes: ['phone', 'vehicle', 'watch', 'wardriver'], // Load ALL device types for full PoL
        limit: undefined, // NO LIMIT - load all GPS points for entire timespan
        // IMPORTANT: This is a SEPARATE query from co-traveler identification
        // This loads COMPLETE tracks, not filtered by proximity to primary subject
      };
      
      const coTravelerPointsRaw = await pathfinderApi.queryDataPoints(coTravelerQuery);

      // Analyze the actual date range of received data
      if (coTravelerPointsRaw.length > 0) {
        // Use reduce instead of spread operator to avoid stack overflow on large datasets
        let minTs = Infinity, maxTs = -Infinity;
        coTravelerPointsRaw.forEach(p => {
          const ts = p.timestamp.getTime();
          if (ts < minTs) minTs = ts;
          if (ts > maxTs) maxTs = ts;
        });
        const actualStart = new Date(minTs);
        const actualEnd = new Date(maxTs);
        
        // Check if we got the full range
        const requestedStart = coTravelerQuery.dateRange[0].getTime();
        const requestedEnd = coTravelerQuery.dateRange[1].getTime();
        const receivedStart = actualStart.getTime();
        const receivedEnd = actualEnd.getTime();
        
        if (receivedStart > requestedStart + 86400000 || receivedEnd < requestedEnd - 86400000) {
          console.error('❌ DATA MISMATCH: Did not receive full date range!');
          console.error('   Missing start:', ((receivedStart - requestedStart) / (1000 * 60 * 60)).toFixed(1), 'hours');
          console.error('   Missing end:', ((requestedEnd - receivedEnd) / (1000 * 60 * 60)).toFixed(1), 'hours');
        }
      }
      
      // FILTER CO-TRAVELER GPS TRACKS: Exclude points IN Davis-Monthan AFB
      // This is DISPLAY filtering - we already found co-travelers based on encounters OUTSIDE the base,
      // but now we need to hide their GPS tracks when they were IN the base (matching behavior)
      const davisMonthanPolygon: [number, number][] = [
        [32.1899942, -110.8668227],  // [lat, lon] format
        [32.1759060, -110.8666880],
        [32.1804096, -110.8931131],
        [32.1944209, -110.8933825]
      ];
      
      const isPointInDavisMonthan = (lat: number, lon: number): boolean => {
        // Using correct coordinate system: lon=x, lat=y
        const x = lon;
        const y = lat;
        let inside = false;
        for (let i = 0, j = davisMonthanPolygon.length - 1; i < davisMonthanPolygon.length; j = i++) {
          const [yi, xi] = davisMonthanPolygon[i];  // polygon is [lat, lon]
          const [yj, xj] = davisMonthanPolygon[j];
          const intersect = ((yi > y) !== (yj > y)) && 
                           (x < (xj - xi) * (y - yi) / (yj - yi + Number.EPSILON) + xi);
          if (intersect) inside = !inside;
        }
        return inside;
      };
      
      // EXCLUSION TOGGLE: Set to false to disable display filtering for testing
      // TO RE-ENABLE: Change both ENABLE_DISPLAY_EXCLUSION here and ENABLE_EXCLUSION_ZONE in pathfinderApi.ts to true
      const ENABLE_DISPLAY_EXCLUSION = false; // TEMPORARILY DISABLED to show FULL co-traveler tracks
      
      // Filter out co-traveler points that are IN Davis-Monthan
      const coTravelerPoints = ENABLE_DISPLAY_EXCLUSION ?
        coTravelerPointsRaw.filter(point => {
          const inDM = isPointInDavisMonthan(point.coordinates[0], point.coordinates[1]);
          return !inDM; // Keep points OUTSIDE Davis-Monthan
        }) :
        coTravelerPointsRaw; // Show ALL points when exclusion is disabled
      
      if (coTravelerPoints.length === 0) {
        return;
      }
      
      // Resolve cotraveler track color via the shared helper. Cotraveler
      // tracks no longer use an inverted device-type palette - that earned
      // us a "purple line on the map means vehicle for primary but phone
      // for cotraveler" inconsistency that had to be reverse-engineered
      // every time. Cotraveler tracks instead distinguish themselves from
      // the primary via dashed line style + reduced opacity (set below).
      // Resolve the matching cotraveler row key (e.g. "Student-22 - Phone") for
      // a (studentId, deviceType) pair coming off the API. The cotraveler row
      // key is what the device-tree's color picker stores under, so we stash
      // it on each point/route below and the studentColors useMemo can paint
      // that key with the user-picked color in real time.
      // Extract the entity id prefix from a device-tree / cotraveler row key.
      // "SRP-16 - Phone" / "SRP-16-phone" → "SRP-16". Exact equality is required
      // — substring matching wrongly pairs SRP-16 with RP-16 because
      // "SRP-16".includes("RP-16") and "SRP-16 - Phone".includes("RP-16").
      const entityIdFromDeviceKey = (deviceId: string): string => {
        if (deviceId.includes(' - ')) return deviceId.split(' - ')[0];
        const lower = deviceId.toLowerCase();
        if (lower.endsWith('-phone') || lower.endsWith('-watch') || lower.endsWith('-vehicle')) {
          return deviceId.split('-').slice(0, -1).join('-');
        }
        return deviceId;
      };

      const findCoTravelerKey = (
        studentId: string,
        deviceType: string,
      ): { key?: string; entityType?: string } => {
        let entityType: string | undefined;
        let looseKey: string | undefined;
        let strictKey: string | undefined;
        const dtLower = (deviceType || '').toLowerCase();
        const studentEntityId = entityIdFromDeviceKey(studentId).toUpperCase();
        for (const [deviceId, type] of Array.from(deviceToEntityType.entries())) {
          const rowEntityId = entityIdFromDeviceKey(deviceId).toUpperCase();
          if (rowEntityId !== studentEntityId) continue;
          if (!entityType) {
            entityType = type;
            looseKey = deviceId;
          }
          if (dtLower && deviceId.toLowerCase().includes(dtLower)) {
            strictKey = deviceId;
            break;
          }
        }
        return { key: strictKey || looseKey, entityType };
      };

      const getCoTravelerRouteColor = (studentId: string, deviceType: string): string => {
        // Best-effort lookup of the cotraveler's entity_type and matching
        // row key. Entity type carries WIFI / BLUETOOTH / CELLULAR / SRP / RP
        // through to the color resolver. The row key is preferred as the
        // deviceId so the resolver can apply any per-cotraveler custom
        // color the user picked.
        const { key: coTravKey, entityType } = findCoTravelerKey(studentId, deviceType);
        return resolvePathfinderColor({
          entityType,
          deviceType,
          deviceId: coTravKey || studentId,
          customDeviceColors,
        });
      };
      
      // Optimize: Use Map for O(1) duplicate detection instead of O(n²) filter
      const pointsMap = new Map<string, PathfinderDataPoint>();
      
      // Add existing points first (use provided primaryPoints or state)
      const existingPoints = primaryPoints || dataPoints;
      existingPoints.forEach(point => pointsMap.set(point.id, point));
      
      // Add co-traveler points (will overwrite duplicates automatically)
      coTravelerPoints.forEach(point => {
        const dt = point.deviceType || 'phone';
        const trackColor = getCoTravelerRouteColor(point.studentId, dt);
        // Stash the matching cotraveler row key so the live studentColors
        // map can paint the user-picked custom color in real time. The API
        // returns deviceIds like "Student-22-phone" but the device-tree
        // stores user-picked colors under "Student-22 - Phone", so we
        // resolve the bridge once here and carry it forward.
        const { key: coTravelerKey } = findCoTravelerKey(point.studentId, dt);

        const enhancedPoint = {
          ...point,
          isCoTraveler: true,
          coTravelerType: 'track',
          originalStudentId: point.studentId,
          coTravelerKey,
          trackType: 'co-traveler',
          trackOpacity: 0.7,
          trackColor,
        };
        pointsMap.set(point.id, enhancedPoint);
      });
      
      const uniquePoints = Array.from(pointsMap.values());
      const coTravelerRoutesRaw = pathfinderApi.generateRoutes(coTravelerPoints);
      
      // Enhance co-traveler routes with special styling based on entity type
      const coTravelerRoutes = coTravelerRoutesRaw.map(route => {
        const routeColor = getCoTravelerRouteColor(route.studentId, route.deviceType);
        const { key: coTravelerKey } = findCoTravelerKey(route.studentId, route.deviceType);
        return {
          ...route,
          isCoTravelerRoute: true,
          routeType: 'co-traveler',
          routeColor,
          coTravelerKey,
          routeOpacity: 0.7,
          routeWidth: 3,
          routeStyle: 'dashed',
        };
      });
      
      // Use provided primaryRoutes or state routes
      const existingRoutes = primaryRoutes || routes;
      const allRoutes = [...existingRoutes, ...coTravelerRoutes];
      
      // Optimize: Calculate time range incrementally instead of processing all points
      let newTimeRange = timeRange;
      if (coTravelerPoints.length > 0) {
        // Use reduce instead of spread operator to avoid stack overflow on large datasets
        let coTravelerStart = Infinity, coTravelerEnd = -Infinity;
        coTravelerPoints.forEach(p => {
          const ts = p.timestamp.getTime();
          if (ts < coTravelerStart) coTravelerStart = ts;
          if (ts > coTravelerEnd) coTravelerEnd = ts;
        });
        
        newTimeRange = {
          start: new Date(Math.min(timeRange?.start?.getTime() || coTravelerStart, coTravelerStart)),
          end: new Date(Math.max(timeRange?.end?.getTime() || coTravelerEnd, coTravelerEnd))
        };
      }
      
      // Update data points first (most important for visualization)
      setDataPoints(uniquePoints);
      
      // CRITICAL: Update VISIBLE data points and routes to show co-travelers on map
      // Without this, the map only shows the original data, not the co-traveler tracks
      setVisibleDataPoints(uniquePoints);
      setVisibleRoutes(allRoutes);

      // Defer route and time range updates to next tick to prevent UI blocking
      setTimeout(() => {
        setRoutes(allRoutes);
        setTimeRange(newTimeRange);
        
        // const stateUpdateTime = performance.now() - stateUpdateStart;
      }, 0);
      
    } catch (error) {
      console.error('❌ Failed to load co-traveler tracks:', error);
      // Don't throw - this is supplementary data
    }
  }, [dataPoints, dateRange.start, dateRange.end, selectedCourse]);

  const loadSignalEnvironment = async () => {
    if (!rfEnabled || selectedStudents.length === 0 || !dateRange.start || !dateRange.end) return;
    try {
      setLoading(true);

      setLoadError(null);
      setLoadingMessage('Loading GPS tracks...');
      const query = {
        dateRange: [dateRange.start, dateRange.end] as [Date, Date],
        timeZone: queryTimeZone,
        studentIds: selectedStudents,
        selectedCourse: selectedCourse,
        deviceTypes: ['phone', 'watch', 'vehicle', 'wardriver'],
        limit: undefined,
      };
      const points = await pathfinderApi.queryDataPoints(query);
      setDataPoints(points);
      await extractDevicesEfficiently(points);
      const dwells = await loadDwellLocations();
      setLoadingMessage('Loading signal environment...');
      const axios = (await import('axios')).default;
      const token = localStorage.getItem('access_token');
      const baseURL = process.env.REACT_APP_API_URL || '';
      const resp = await axios.post(`${baseURL}/pathfinder-postgres/signal-environment`, {
        device_ids: selectedStudents,
        start_date: queryTimeZone
          ? wallClockDateInTimeZoneToUtcIso(dateRange.start!, queryTimeZone)
          : dateRange.start!.toISOString(),
        end_date: queryTimeZone
          ? wallClockDateInTimeZoneToUtcIso(dateRange.end!, queryTimeZone)
          : dateRange.end!.toISOString(),
      }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setSignalData(resp.data);
    } catch (error: any) {
      console.error('Failed to load signal environment:', error);
      setLoadError(error?.message || 'Failed to load signal data.');
    } finally {
      setLoading(false);

      setLoadingMessage('');
    }
  };

  const loadData = async () => {
    
    if (selectedStudents.length === 0) {
      console.warn('⚠️ PathfinderPage: No students selected, skipping data load');
      return;
    }
    
    // Check if date range is selected
    if (!dateRange.start || !dateRange.end) {
      console.warn('⚠️ PathfinderPage: Date range not selected, skipping data load');
      return;
    }
    
    try {
      setLoading(true);
      setLoadError(null); // Clear previous errors
      setLoadingMessage('Connecting to backend...');
      
      const query = {
        dateRange: [dateRange.start, dateRange.end] as [Date, Date],
        timeZone: queryTimeZone,
        studentIds: selectedStudents,
        selectedCourse: selectedCourse, // Pass the selected course ID
        deviceTypes: ['phone', 'watch', 'vehicle', 'wardriver'], // Only actual device types, not entity types // Include all device types
        limit: undefined  // No limit - load all data
      };
      
      setLoadingMessage('Loading GPS data...');
      const points = await pathfinderApi.queryDataPoints(query);
      
      setDataPoints(points);
      await extractDevicesEfficiently(points);
      await loadDwellLocations();
    } catch (error: any) {
      console.error('Failed to load data:', error);
      // Set user-friendly error message
      const errorMessage = error?.message?.includes('timeout') 
        ? 'Request timed out. The date range may be too large - try a shorter period or fewer students.'
        : error?.message?.includes('Network Error') || error?.message?.includes('ECONNABORTED')
        ? 'Network error. Please check your connection and try again.'
        : error?.message || 'Failed to load GPS data. Please try again.';
      setLoadError(errorMessage);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // Load full pattern-of-life for one or more discovered AdTech devices and
  // fold them into the device tree / map / time slider like student tracks.
  // `registrationIds` come from the AdTech results table (single row-group or
  // "load all"). Reuses extractDevicesEfficiently so they get colors,
  // visibility toggles, routes and time-slider playback for free.
  const handleLoadAdtechPol = useCallback(async (registrationIds: string[], label?: string) => {
    const ids = Array.from(new Set((registrationIds || []).filter(Boolean)));
    if (ids.length === 0) return;
    if (!dateRange.start || !dateRange.end) {
      setLoadError('Select a date range before loading AdTech pattern of life.');
      return;
    }
    if (ids.length > 25) {
      const ok = window.confirm(
        `Load full pattern of life for ${ids.length} AdTech devices? This queries the commercial provider and may take a while.`,
      );
      if (!ok) return;
    }
    try {
      setAdtechPolLoading(true);
      setAdtechPolMessage(
        ids.length === 1 ? 'Loading device pattern of life…' : `Loading pattern of life for ${ids.length} devices…`,
      );
      const result = await pathfinderApi.getAdtechPatternOfLife({
        registrationIds: ids,
        dateRange: [dateRange.start, dateRange.end],
        timeZone: queryTimeZone,
      });
      const polPoints = PathfinderApiService.adtechPolToDataPoints(result.devices);
      if (polPoints.length === 0) {
        setLoadError(
          `No AdTech location history found for the selected ${ids.length === 1 ? 'device' : 'devices'} in this window ` +
            `(commercial adtech lands ~24h late).`,
        );
        return;
      }

      // Merge with existing tracks (dedupe by id), sort by time so timeRange /
      // route generation stay correct, then rebuild the device tree.
      const existing = dataPoints || [];
      const seen = new Set(existing.map((p) => p.id));
      const merged = [...existing];
      for (const p of polPoints) {
        if (!seen.has(p.id)) {
          seen.add(p.id);
          merged.push(p);
        }
      }
      merged.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

      // Give the discovered devices friendly labels in the tree.
      const knownStudentIds = new Set(availableStudents.map((s) => s.id));
      const studentAdditions: PathfinderStudent[] = [];
      for (const rid of Object.keys(result.devices || {})) {
        if (!knownStudentIds.has(rid)) {
          studentAdditions.push({
            id: rid,
            name: `AdTech ${rid.slice(0, 8)}`,
            devices: [],
            course: selectedCourse || '',
            status: 'active',
          });
        }
      }
      if (studentAdditions.length) {
        setAvailableStudents([...availableStudents, ...studentAdditions]);
      }

      setDataPoints(merged);
      setVisibleDataPoints(merged);
      setShowDataPoints(true);
      setShowRouteLines(true);
      await extractDevicesEfficiently(merged);

      // Make sure the entity/device tree is the active context so they render.
      setLeftPanelTab(0);

      // --- Dwell + co-location analysis on the loaded PoL -----------------
      // The server dwell pipeline only knows trainee tracks, so AdTech PoL
      // dwells are detected client-side and merged into the same dwell store —
      // they then show up in the Dwell Analysis tab, the map dwell layer, and
      // charts like any other dwell. Co-locations are computed against every
      // other loaded dwell (trainees + other adtech) and, when the Intel
      // module is on, written to the Intel Packet as [POL INTEL] notes.
      let dwellCount = 0;
      let colocCount = 0;
      try {
        const idSet = new Set(ids);
        const polDwells = detectDwellsFromPoints(polPoints);
        dwellCount = polDwells.length;

        const store = usePathfinderStore.getState();
        // Replace prior client-detected dwells for these devices (reload case).
        const keptDwells = (store.dwellLocations || []).filter(
          (dw: any) => !(dw?.client_detected && idSet.has(dw.entity_id)),
        );
        if (polDwells.length > 0) {
          const mergedDwells = [...keptDwells, ...polDwells];
          setDwellLocations(mergedDwells);
          setVisibleDwells(mergedDwells);
          setShowDwells(true);
        }

        const colocs = detectDwellCoLocations(polDwells, [...keptDwells, ...polDwells] as any);
        colocCount = colocs.length;

      } catch (analysisErr) {
        console.warn('AdTech PoL dwell/co-location analysis failed:', analysisErr);
      }

      setQuickLaunchNotification({
        open: true,
        severity: 'success',
        message:
          `Pattern of life loaded: ${polPoints.length.toLocaleString()} points across ${ids.length} device${ids.length > 1 ? 's' : ''}` +
          ` • ${dwellCount} dwell${dwellCount === 1 ? '' : 's'} detected` +
          ` • ${colocCount} co-location${colocCount === 1 ? '' : 's'}`,
      });

      // Zoom to the extent of the newly loaded device tracks ([lat, lon]).
      const lats = polPoints.map((p) => p.coordinates[0]);
      const lons = polPoints.map((p) => p.coordinates[1]);
      const cLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      const cLon = (Math.min(...lons) + Math.max(...lons)) / 2;
      if (Number.isFinite(cLat) && Number.isFinite(cLon)) {
        setMapCenter([cLat, cLon]);
        setMapZoom(ids.length === 1 ? 13 : 11);
      }
    } catch (error: any) {
      console.error('AdTech pattern-of-life load failed:', error);
      setLoadError(error?.message || 'Failed to load AdTech pattern of life.');
    } finally {
      setAdtechPolLoading(false);
      setAdtechPolMessage('');
    }
  }, [dateRange.start, dateRange.end, queryTimeZone, selectedCourse, dataPoints, availableStudents, setDataPoints, setVisibleDataPoints, setAvailableStudents, setLeftPanelTab, setMapCenter, setMapZoom, setDwellLocations, setVisibleDwells, setShowDwells, setShowDataPoints, setShowRouteLines]);

  // AdTech IP lookup: resolve one or more IP addresses to the registration ids
  // observed at them, then hand those ids to the pattern-of-life loader so the
  // discovered devices get the full track + dwell + intel treatment. Keeps a
  // single code path for "plot these adtech devices" regardless of entry point.
  const handleLookupAdtechIps = useCallback(async (ipAddresses: string[]) => {
    const ips = Array.from(new Set((ipAddresses || []).map((s) => s.trim()).filter(Boolean)));
    if (ips.length === 0) return;
    if (!dateRange.start || !dateRange.end) {
      setLoadError('Select a date range before looking up AdTech IPs.');
      return;
    }
    try {
      setAdtechPolLoading(true);
      setAdtechPolMessage(`Resolving ${ips.length} IP${ips.length > 1 ? 's' : ''} to devices…`);
      const result = await pathfinderApi.queryAdtechIpSearch({
        ipAddresses: ips,
        dateRange: [dateRange.start, dateRange.end],
        timeZone: queryTimeZone,
      });
      const discoveredIds = Object.keys(result.devices || {});
      if (discoveredIds.length === 0) {
        setLoadError(
          `No AdTech devices found for the given IP${ips.length > 1 ? 's' : ''} in this window (commercial adtech lands ~24h late).`,
        );
        return;
      }
      // Delegate to the shared PoL loader (handles map, tree, dwells, intel).
      await handleLoadAdtechPol(discoveredIds, `IP lookup (${ips.length})`);
    } catch (error: any) {
      console.error('AdTech IP lookup failed:', error);
      setLoadError(error?.message || 'Failed to look up AdTech IPs.');
    } finally {
      setAdtechPolLoading(false);
      setAdtechPolMessage('');
    }
  }, [dateRange.start, dateRange.end, queryTimeZone, handleLoadAdtechPol]);

  // Completion handler for an ad-hoc AdTech polygon search: the map hands us
  // the drawn GeoJSON polygon; we query adtech inside it and plot the results
  // to the map + floating table (same surface as circle area search).
  // A freeform polygon drawn on the map becomes a first-class search area, just
  // like a drawn circle: it runs the full area analysis (entities, dwells,
  // co-locations, signal environment, and AdTech when enabled), clipped to the
  // exact ring. We store the ring plus its bounding circle (center + radius) so
  // the radius-only backends can be queried and then clipped in AreaSearch.
  const handleAdtechPolygonComplete = useCallback((geometry: any) => {
    const ring: Array<[number, number]> = geometry?.coordinates?.[0];
    if (!ring || ring.length < 3) return;

    // Drop a duplicated closing vertex if present.
    const pts = ring.slice();
    const f = pts[0], l = pts[pts.length - 1];
    if (f[0] === l[0] && f[1] === l[1]) pts.pop();

    // Centroid (simple average is fine for a bounding circle) in [lon, lat].
    let sumLon = 0, sumLat = 0;
    pts.forEach(([lon, lat]) => { sumLon += lon; sumLat += lat; });
    const cLon = sumLon / pts.length;
    const cLat = sumLat / pts.length;

    // Bounding radius = max distance centroid -> vertex (meters, haversine).
    const R = 6371000;
    const toRad = (d: number) => (d * Math.PI) / 180;
    let radius = 0;
    pts.forEach(([lon, lat]) => {
      const dLat = toRad(lat - cLat);
      const dLon = toRad(lon - cLon);
      const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(cLat)) * Math.cos(toRad(lat)) * Math.sin(dLon / 2) ** 2;
      const dist = 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
      if (dist > radius) radius = dist;
    });

    const newArea: DrawCircle = {
      id: `poly-${Date.now()}`,
      center: [cLat, cLon],
      radius: Math.max(radius, 25),
      name: `Area ${drawCircles.length + 1}`,
      visible: true,
      polygon: pts,
      // Leave `results` undefined so AreaSearch auto-runs the full analysis.
      loading: false,
    };
    handleCircleAdd(newArea);
  }, [drawCircles.length, handleCircleAdd]);

  // Co-traveler filter type for the dropdown options
  type CoTravelerFilterType = 'all' | 'students' | 'rp_srp' | 'digital_wifi' | 'digital_bluetooth' | 'digital_all' | 'adtech';
  
  // Load data WITH co-traveler analysis - loads selected students + automatically runs co-traveler analysis
  const loadDataWithCoTravelers = async (coTravelerFilter: CoTravelerFilterType = 'all') => {
    if (selectedStudents.length === 0) {
      console.warn('⚠️ PathfinderPage: No students selected, skipping data load');
      return;
    }
    
    if (!dateRange.start || !dateRange.end) {
      console.warn('⚠️ PathfinderPage: Date range not selected, skipping data load');
      return;
    }
    
    try {
      setLoading(true);
      setLoadingWithCoTravelers(true);
      setLoadError(null);
      setLoadingMessage('Connecting to backend...');
      
      // Step 1: Load the primary GPS data (same as loadData)
      const query = {
        dateRange: [dateRange.start, dateRange.end] as [Date, Date],
        timeZone: queryTimeZone,
        studentIds: selectedStudents,
        selectedCourse: selectedCourse,
        deviceTypes: ['phone', 'watch', 'vehicle', 'wardriver'],
        limit: undefined
      };
      
      setLoadingMessage('Loading GPS data for selected devices...');
      const points = await pathfinderApi.queryDataPoints(query);
      
      // Generate routes from the loaded points BEFORE setting state
      const primaryRoutes = pathfinderApi.generateRoutes(points);
      
      setDataPoints(points);
      setRoutes(primaryRoutes);
      await extractDevicesEfficiently(points);
      await loadDwellLocations();

      // Step 2: Run co-traveler analysis automatically
      // IMPORTANT: Primary data must be fully loaded before this step (we awaited it above)
      setLoadingMessage('Analyzing co-travelers...');
      
      try {
        // Build device IDs from selected students (format: "Student-01 - Phone")
        const deviceIds = selectedStudents.flatMap(studentId => [
          `${studentId} - Phone`,
          `${studentId} - Watch`, 
          `${studentId} - Vehicle`
        ]);
        
        // Run co-traveler analysis for the primary students
        // NOTE: Use 100m threshold to match the co-traveler tab analysis (50m was too tight)
        // Map filter type to entity types for DB-level filtering (performance optimization)
        let entityTypesFilter: string[] | undefined;
        let courseIdForQuery: string | undefined = selectedCourse;
        
        if (coTravelerFilter === 'students') {
          entityTypesFilter = ['Student'];
          // Students are typically in the same course, so keep the course filter
        } else if (coTravelerFilter === 'rp_srp') {
          entityTypesFilter = ['RP', 'SRP'];
          // IMPORTANT: RP/SRP may be in different courses (e.g., 'Exercise Support')
          // Don't filter by course for RP/SRP - query ALL courses
          courseIdForQuery = undefined;
        }
        // 'all' = no filter (undefined) - keep course filter

        // AdTech (Venntel/GeoNeutron) co-traveler analysis -- early return.
        // Queries commercial mobile-location data near the operator's GPS track
        // and surfaces registrationIDs that recur across distinct locations.
        if (coTravelerFilter === 'adtech' && adtechEnabled) {
          setLoadingMessage('Querying AdTech co-travelers...');
          try {
            const adtechResults = await pathfinderApi.analyzeAdtechCoTravelers({
              entityIds: selectedStudents,
              dateRange: [dateRange.start!, dateRange.end!],
              timeZone: queryTimeZone,
            });
            const candidates = adtechResults.candidates || [];
            if (candidates.length > 0) {
              const primaryDevice = selectedStudents[0] || 'Primary';
              const transformed = candidates.map((c) => {
                const locs = c.locations || [];
                const conf = Math.max(0, Math.min(1, c.confidence || 0));
                const encounters = locs.map((loc) => ({
                  timestamp: new Date(loc.recorded_at),
                  location: [Number(loc.latitude), Number(loc.longitude)] as [number, number],
                  duration: 0,
                  proximity: 0,
                  confidence: conf,
                  locationName: c.display_name || undefined,
                }));
                const firstSeen = c.first_seen ? new Date(c.first_seen).getTime() : 0;
                const lastSeen = c.last_seen ? new Date(c.last_seen).getTime() : firstSeen;
                return {
                  primaryDevice,
                  coTravelerDevice: c.external_id,
                  coTravelerEntityType: 'ADTECH' as const,
                  encounters,
                  totalEncounters: Number(c.sighting_count ?? encounters.length),
                  totalDuration: Math.max(0, (lastSeen - firstSeen) / 60000),
                  averageProximity: 0,
                  confidenceScore: conf,
                  temporalOverlap: 0,
                  spatialOverlap: 0,
                  frequentLocations: [],
                };
              });
              setCoTravelerResults(transformed);
              const autoVis: Record<string, boolean> = {};
              transformed.forEach((r) => { autoVis[`${r.primaryDevice}-${r.coTravelerDevice}`] = true; });
              setCoTravelerVisibility(autoVis);
              setShowCoTravelerConnections(true);
              setShowCoTravelerPoints(true);
              setShowCoTravelerLines(true);
              setLoadingMessage(
                `Found ${candidates.length} AdTech co-travelers from ${adtechResults.total_signals_scanned} signals across ${adtechResults.search_areas} areas`
              );
            } else {
              setCoTravelerResults([]);
              setLoadingMessage('No AdTech co-travelers found');
            }
          } catch (err: any) {
            console.error('AdTech co-traveler analysis failed:', err);
            setLoadingMessage(err?.message || 'AdTech co-traveler analysis failed');
          } finally {
            setLoading(false);
            setLoadingWithCoTravelers(false);
            setTimeout(() => setLoadingMessage(''), 4000);
          }
          return;
        }

        // Digital co-traveler analysis (RF signal-based) -- early return
        if (coTravelerFilter.startsWith('digital_') && rfEnabled) {
          const signalType = coTravelerFilter === 'digital_wifi' ? 'wifi'
            : coTravelerFilter === 'digital_bluetooth' ? 'bluetooth'
            : 'all';
          setLoadingMessage(`Analyzing digital co-travelers (${signalType})...`);
          try {
            const axios = (await import('axios')).default;
            const token = localStorage.getItem('access_token');
            // Fallback must match pathfinderApi.getApiBaseUrl() ("/api"); an empty
            // string here produces /pathfinder-postgres/... which 404s in commercial
            // because all backend routes live under /api.
            const baseURL = process.env.REACT_APP_API_URL || '/api';
            const resp = await axios.post(`${baseURL}/pathfinder-postgres/digital-co-travelers`, {
              device_ids: selectedStudents,
              start_date: queryTimeZone
                ? wallClockDateInTimeZoneToUtcIso(dateRange.start!, queryTimeZone)
                : dateRange.start!.toISOString(),
              end_date: queryTimeZone
                ? wallClockDateInTimeZoneToUtcIso(dateRange.end!, queryTimeZone)
                : dateRange.end!.toISOString(),
              signal_type: signalType,
              min_locations: 2,
              min_sightings: 3,
              limit: 200,
            }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            const digitalResults = resp.data;
            const candidates: any[] = digitalResults.candidates || [];
            if (candidates.length > 0) {
              // Transform DigitalCoTravelerCandidate -> CoTravelerResult so the
              // existing EnhancedCoTravelerAnalysis panel can render them. The
              // digital response has a different shape (external_id, sighting_count,
              // locations[]) so we project into the panel's expected fields.
              //
              // primaryDevice is set to the plain entity name (e.g. "DoubleCross75").
              // The HierarchicalDeviceTree's getCoTravelersForDevice() has an
              // entity-prefix fallback that strips "-phone" / "-cellphone" / "-watch"
              // / "-vehicle" from device.id and matches on the entity name, so the
              // digital co-travelers appear under every device of the primary entity
              // (matches the overwatch_rf reference implementation).
              const primaryDevice = selectedStudents[0] || 'Primary';
              // Normalize the backend's co_occurrence_score (an unbounded
              // utility, distinct_locs * ln(sightings+1) * hours) into a
              // bounded 0..1 confidence. Mirrors backend's _score_to_confidence
              // so older deployments that don't yet return c.confidence still
              // produce a sane 0..1 value instead of leaking into the popup
              // and rendering as e.g. "53589%".
              const scoreToConfidence = (s: number): number => {
                if (!Number.isFinite(s) || s <= 0) return 0;
                return Math.max(0, Math.min(1, 1 - Math.exp(-s / 100)));
              };
              const resolveConfidence = (c: any): number => {
                if (typeof c?.confidence === 'number' && Number.isFinite(c.confidence)) {
                  return Math.max(0, Math.min(1, c.confidence));
                }
                return scoreToConfidence(Number(c?.co_occurrence_score ?? 0));
              };
              const transformed = candidates.map((c: any) => {
                const sigType = (c.signal_type || signalType || 'wifi').toUpperCase();
                const locs: any[] = c.locations || [];
                const candidateConfidence = resolveConfidence(c);
                const encounters = locs.map((loc: any) => ({
                  timestamp: new Date(loc.recorded_at || loc.timestamp || c.first_seen),
                  location: [
                    Number(loc.latitude ?? loc.lat ?? 0),
                    Number(loc.longitude ?? loc.lon ?? loc.lng ?? 0),
                  ] as [number, number],
                  duration: 0,
                  proximity: 0,
                  confidence: candidateConfidence,
                  locationName: c.display_name || undefined,
                }));
                const firstSeen = c.first_seen ? new Date(c.first_seen).getTime() : 0;
                const lastSeen = c.last_seen ? new Date(c.last_seen).getTime() : firstSeen;
                return {
                  primaryDevice,
                  coTravelerDevice: c.display_name
                    ? `${c.display_name} (${c.external_id})`
                    : c.external_id,
                  coTravelerEntityType: sigType,
                  encounters,
                  totalEncounters: Number(c.sighting_count ?? encounters.length),
                  totalDuration: Math.max(0, (lastSeen - firstSeen) / 60000),
                  averageProximity: 0,
                  confidenceScore: candidateConfidence,
                  temporalOverlap: 0,
                  spatialOverlap: 0,
                  frequentLocations: [],
                };
              });
              setCoTravelerResults(transformed);

              // Auto-enable per-result visibility so every digital co-traveler
              // is ON by default (matches the "combo Load" flow for track-based
              // co-travelers — see ~line 1527). Without this each row in the
              // HierarchicalDeviceTree would start with its switch OFF and the
              // map filter (`coTravelerVisibility[resultKey] === true`) would
              // hide every encounter point.
              const digitalAutoVisibility: Record<string, boolean> = {};
              transformed.forEach((r) => {
                const resultKey = `${r.primaryDevice}-${r.coTravelerDevice}`;
                digitalAutoVisibility[resultKey] = true;
              });
              setCoTravelerVisibility(digitalAutoVisibility);

              // Auto-enable the global encounter-point and connection-line map
              // layers so digital co-travelers visibly render. Track-based
              // co-travelers can use GPS tracks (loadCoTravelerTracks) but
              // digital candidates are RF emitters without GPS tracks — their
              // only geometry is the encounter points, so we turn those on.
              setShowCoTravelerConnections(true);
              setShowCoTravelerPoints(true);
              setShowCoTravelerLines(true);

              setLoadingMessage(
                `Found ${candidates.length} digital co-travelers from ${digitalResults.total_signals_scanned} signals`
              );
            } else {
              setCoTravelerResults([]);
              setLoadingMessage('No digital co-travelers found');
            }
          } catch (err: any) {
            console.error('Digital co-traveler analysis failed:', err);
            setLoadingMessage('Digital co-traveler analysis failed');
          } finally {
            setLoading(false);
            setLoadingWithCoTravelers(false);
            // Keep the outcome message visible briefly so the user sees the count;
            // the regular path clears this via setLoadingMessage('') in its own
            // finally, but the digital branch returns before that.
            setTimeout(() => setLoadingMessage(''), 4000);
          }
          return;
        }

        const coTravelerParams = {
          deviceIds,
          dateRange: [dateRange.start, dateRange.end] as [Date, Date],
          timeZone: queryTimeZone,
          proximityThreshold: 100, // 100 meters (same as co-traveler tab)
          timeThreshold: 5, // 5 minutes
          courseId: courseIdForQuery,  // undefined for RP/SRP to search all courses
          primaryCourseId: selectedCourse,  // ALWAYS use selected course for primary student data
          entityTypesFilter,  // Pass to backend for DB-level filtering
        };
        
        // Analyze using loaded data bounds - use cross-areas for better performance
        const results = await pathfinderApi.analyzeCoTravelersCrossAreas(coTravelerParams);
        
        if (results && results.length > 0) {
          // Filter out results where the co-traveler is one of the primary selected students
          // Note: Entity type filtering is now done at DB level for better performance
          const filteredResults = results.filter((r: any) => {
            const coTravelerEntityId = r.coTravelerDevice?.split(' - ')[0] || r.coTravelerDevice;
            return !selectedStudents.includes(coTravelerEntityId);
          });
          
          setCoTravelerResults(filteredResults);
          
          // Step 3: Load full GPS tracks for all co-travelers
          // Pass primary points and routes to ensure they're merged correctly
          // (state updates are async, so we pass the fresh data directly)
          if (filteredResults.length > 0) {
            setLoadingMessage(`Loading ${filteredResults.length} co-traveler tracks...`);
            
            // AUTO-ENABLE visibility for all co-travelers when loading via combo button
            // This is different from the co-traveler tab where they default to OFF
            const autoVisibility: Record<string, boolean> = {};
            filteredResults.forEach((result: any) => {
              const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
              autoVisibility[resultKey] = true; // Show co-travelers by default
            });
            setCoTravelerVisibility(autoVisibility);
            
            await loadCoTravelerTracks(filteredResults, points, primaryRoutes);
          } else {
            // All results were primary students - no external co-travelers found
            setLoadingMessage('No external co-travelers found in time range');
          }
        } else {
          // No co-travelers detected at all
          setLoadingMessage('No co-travelers detected in selected area/time');
        }
      } catch (coTravelerError: any) {
        console.error('⚠️ Co-traveler analysis failed:', coTravelerError);
        // Show user-friendly error but don't fail the whole load
        const errorMsg = coTravelerError?.message?.includes('timeout') 
          ? 'Co-traveler analysis timed out'
          : 'Co-traveler analysis failed';
        setLoadingMessage(errorMsg);
      }
      
    } catch (error: any) {
      console.error('Failed to load data:', error);
      const errorMessage = error?.message?.includes('timeout') 
        ? 'Request timed out. The date range may be too large - try a shorter period or fewer students.'
        : error?.message?.includes('Network Error') || error?.message?.includes('ECONNABORTED')
        ? 'Network error. Please check your connection and try again.'
        : error?.message || 'Failed to load GPS data. Please try again.';
      setLoadError(errorMessage);
    } finally {
      setLoading(false);
      setLoadingWithCoTravelers(false);
      setLoadingMessage('');
    }
  };

  // Load itinerary submissions for selected students and date range
  const loadItinerarySubmissions = useCallback(async () => {
    if (selectedStudents.length === 0 || !dateRange.start || !dateRange.end) {
      return;
    }

    try {
      setItineraryLoading(true);
      
      // Load itinerary submissions for each selected student
      const allSubmissions: ItinerarySubmission[] = [];
      
      for (const studentId of selectedStudents) {
        // Extract student number from studentId (e.g., "Student-12" -> "Student-12")
        const studentNumber = studentId;
        
        try {
          const response = await listItinerarySubmissions({
            student_number: studentNumber,
            start_date: queryTimeZone
              ? wallClockDateInTimeZoneToUtcIso(dateRange.start, queryTimeZone)
              : dateRange.start.toISOString(),
            end_date: queryTimeZone
              ? wallClockDateInTimeZoneToUtcIso(dateRange.end, queryTimeZone)
              : dateRange.end.toISOString(),
            page_size: 100, // Get all submissions in the date range
          });
          
          allSubmissions.push(...response.items);
        } catch (error) {
          console.error(`Failed to load itinerary for ${studentId}:`, error);
        }
      }
      
      setItinerarySubmissions(allSubmissions);
    } catch (error) {
      console.error('Failed to load itinerary submissions:', error);
    } finally {
      setItineraryLoading(false);
    }
  }, [selectedStudents, dateRange]);

  // Auto-load itinerary when data is loaded
  useEffect(() => {
    if (dataPoints.length > 0 && selectedStudents.length > 0 && dateRange.start && dateRange.end) {
      loadItinerarySubmissions();
    }
  }, [dataPoints.length, selectedStudents, dateRange.start, dateRange.end, loadItinerarySubmissions]);

  // Clear all map data without refreshing the app
  const clearMapData = useCallback(() => {
    // Clear main data
    setDataPoints([]);
    setVisibleDataPoints([]);
    setRoutes([]);
    setVisibleRoutes([]);
    setDwellLocations([]);
    setVisibleDwells([]);
    setCoTravelerResults([]);
    setCoTravelerEncounterAreas([]);
    setDigitalPoLData([]);
    setItinerarySubmissions([]);
    setSignalData(null);
    setPlaybackWindow(null);
    
    // Clear area search data
    setAreaSearchDataPoints([]);
    setAreaSearchRoutes([]);
    setAreaSearchDwellLocations([]);
    setVisibleAreaSearchDwells([]);
    setAreaSearchSelectedDevices({});
    setAreaSearchActualDevices({});
    setPerAreaSelectedDevices({});
    setPerAreaExpandedEntities({});
    
    // Clear device selections
    setActualDevices({});
    setSelectedDevices({});
    
    // Clear annotations
    setActiveAnnotations([]);
    
    // Clear draw circles
    setDrawCircles([]);
    
    // Reset time range
    setTimeRange(null);
    setCurrentTime(new Date());
    
    // Clear chart filter
    setChartFilter(null);
    
    // Clear uploaded data
    clearUploadedData();
  }, [clearUploadedData]);

  // Toggle annotation active state (on/off)
  const handleToggleAnnotation = useCallback((annotation: any) => {
    setActiveAnnotations(prev => {
      const isActive = prev.some(a => a.id === annotation.id);
      if (isActive) {
        // Turn off - remove from active
        return prev.filter(a => a.id !== annotation.id);
      } else {
        // Turn on - add to active
        return [...prev, annotation];
      }
    });
  }, []);

  // Callback for loading annotation data (loads data then activates filter)
  const handleLoadAnnotationData = useCallback(async (annotation: any) => {
    // Activate this annotation
    setActiveAnnotations(prev => {
      const isActive = prev.some(a => a.id === annotation.id);
      if (!isActive) {
        return [...prev, annotation];
      }
      return prev;
    });
    
    // Set course
    setSelectedCourse(annotation.course_id);
    
    // Set date range from annotation
    const startTime = new Date(annotation.start_time);
    const endTime = new Date(annotation.end_time);
    setDateRangeIsUtcInstant(true);
    setDateRange({ start: startTime, end: endTime });
    
    // Set the student (replace to ensure clean load)
    setSelectedStudents([annotation.entity_id]);
    
    // Pan map to annotation location
    if (annotation.center_latitude && annotation.center_longitude) {
      setMapCenter([annotation.center_latitude, annotation.center_longitude]);
      setMapZoom(14);
    }
    
    // Mark this annotation as pending load
    setPendingAnnotationLoad(annotation);
  }, []);

  // Trigger load when annotation state is ready
  useEffect(() => {
    if (!pendingAnnotationLoad) return;
    
    const annotation = pendingAnnotationLoad;
    
    // Check if state is ready
    const isReady = 
      selectedCourse === annotation.course_id &&
      selectedStudents.includes(annotation.entity_id) &&
      dateRange.start && dateRange.end;
    
    if (isReady) {
      setPendingAnnotationLoad(null);
      loadData();
    }
  }, [pendingAnnotationLoad, selectedCourse, selectedStudents, dateRange]);

  // Saved Devices ("watchlist"): select the saved trainee ids and load them.
  // Uses the same pending-load pattern as annotations because loadData() reads
  // selectedStudents from its closure.
  const [pendingSavedDeviceLoad, setPendingSavedDeviceLoad] = useState<string[] | null>(null);
  const handleLoadSavedDevices = useCallback((studentIds: string[]) => {
    if (!studentIds || studentIds.length === 0) return;
    setSelectedStudents(studentIds);
    setPendingSavedDeviceLoad(studentIds);
  }, [setSelectedStudents]);

  useEffect(() => {
    if (!pendingSavedDeviceLoad) return;
    const ready =
      pendingSavedDeviceLoad.every((id) => selectedStudents.includes(id)) &&
      dateRange.start && dateRange.end;
    if (ready) {
      setPendingSavedDeviceLoad(null);
      loadData();
    }
  }, [pendingSavedDeviceLoad, selectedStudents, dateRange]);
  
  // Handle dwell click from DwellAnalysis table
  const focusMapToLocation = useCallback((location: [number, number] | null, zoom: number) => {
    if (location === null) {
      if (originalMapExtent) {
        setMapCenter(originalMapExtent.center);
        setMapZoom(originalMapExtent.zoom);
        setFollowMode(originalMapExtent.followMode);
        setOriginalMapExtent(null);
      }
      return;
    }

    if (!originalMapExtent) {
      const live = getLiveMapExtent();
      setOriginalMapExtent({ center: live.center, zoom: live.zoom, followMode });
      // Prevent follow-mode (or other automatic centering) from fighting the zoom-to selection.
      setFollowMode(false);
    }

    setMapCenter(location);
    setMapZoom(zoom);
  }, [originalMapExtent, getLiveMapExtent, followMode, setFollowMode, setMapCenter, setMapZoom]);

  const handleDwellClick = useCallback((dwell: any | null) => {
    if (dwell === null) return focusMapToLocation(null, 0);
    return focusMapToLocation([dwell.latitude, dwell.longitude], 16);
  }, [focusMapToLocation]);

  // PTT caption → fly to the transmit spot and scrub the slider there (optionally playing).
  const seekToPtt = useCallback((u: PathfinderPttUtterance, play = false) => {
    if (u.latitude != null && u.longitude != null) {
      focusMapToLocation([u.latitude, u.longitude], 17);
    }
    setPttSeek({ time: new Date(u.started_at), play, nonce: Date.now() });
  }, [focusMapToLocation]);

  // Map popup "Open in panel": switch to the PTT tool and scrub to that line.
  const openPttInPanel = useCallback((id: number) => {
    const u = pttUtterances.find((x) => x.id === id);
    selectTool('ptt-comms');
    if (u) setPttSeek({ time: new Date(u.started_at), nonce: Date.now() });
  }, [pttUtterances, selectTool]);
  // Patch place_name onto nearby dwells after an on-demand geocode lookup so
  // the Dwell Analysis title and map dwell labels stay in sync.
  const handleDwellPlaceNameResolved = useCallback((
    lat: number,
    lon: number,
    placeName: string,
    placeType?: string,
  ) => {
    const thresh = 0.001; // ~100m — matches DwellAnalysis grouping
    const patch = (list: any[]) =>
      list.map((d) => {
        if (typeof d?.latitude !== 'number' || typeof d?.longitude !== 'number') return d;
        if (Math.abs(d.latitude - lat) >= thresh || Math.abs(d.longitude - lon) >= thresh) return d;
        return {
          ...d,
          place_name: placeName,
          ...(placeType ? { place_type: placeType } : {}),
        };
      });
    const store = usePathfinderStore.getState();
    setDwellLocations(patch(store.dwellLocations || []));
    setVisibleDwells(patch(store.visibleDwells || []));
    setAreaSearchDwellLocations(patch(store.areaSearchDwellLocations || []));
    setVisibleAreaSearchDwells(patch(store.visibleAreaSearchDwells || []));
  }, [setDwellLocations, setVisibleDwells, setAreaSearchDwellLocations, setVisibleAreaSearchDwells]);

  // Note: auto-fit-to-data is handled inside `PathfinderMapGL` (cameraForBounds
  // with maxZoom: 15) so we no longer need a parent-side helper. Trying to fit
  // here used a coarse zoom-bucket heuristic and stomped on the map's proper
  // fit, snapping the camera between dispersed clusters at a tight zoom.

  // Handle uploaded file data (CSV, KML, GeoJSON) - stored separately from student data
  const handleCSVDataLoaded = useCallback((newPoints: PathfinderDataPoint[], fileName: string) => {
    const validPoints = newPoints.filter(p => {
      const [lat, lng] = p.coordinates;
      return Number.isFinite(lat) && Number.isFinite(lng) &&
             lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
    });

    if (validPoints.length === 0) {
      console.warn('No valid data points found in uploaded file');
      return;
    }

    // Accumulate into the separate uploaded data store (not the student data)
    setUploadedDataPoints((prev: PathfinderDataPoint[]) => [...prev, ...validPoints]);

    const newRoutes = pathfinderApi.generateRoutes(validPoints);
    setUploadedRoutes((prev: PathfinderRoute[]) => [...prev, ...newRoutes]);

    // Track the file name and set it visible by default
    const cleanName = fileName.replace(/\.(csv|geojson|json|kml)$/i, '');
    setUploadedFileNames((prev: string[]) => prev.includes(cleanName) ? prev : [...prev, cleanName]);
    setUploadedFileVisibility((prev: Record<string, boolean>) => ({ ...prev, [cleanName]: true }));

    setShowUploadedData(true);
    setShowDataPoints(true);
  }, [setUploadedDataPoints, setUploadedRoutes, setUploadedFileNames, setUploadedFileVisibility, setShowUploadedData, setShowDataPoints]);

  // Load dwell locations for selected students and date range
  const loadDwellLocations = async (): Promise<any[]> => {
    if (!selectedCourse || !dateRange.start || !dateRange.end || selectedStudents.length === 0) {
      debugLog('pathfinder', '🏠 [loadDwellLocations] Skipping - missing required params');
      return [];
    }

    try {
      setLoadingMessage('Loading dwell locations...');

      const dwells = await pathfinderApi.getDwells({
        courseId: selectedCourse,
        startDate: dateRange.start,
        endDate: dateRange.end,
        timeZone: queryTimeZone,
        limit: 50000,
      });

      console.log('[loadDwellLocations] API returned', dwells.length, 'dwells');
      console.log('[loadDwellLocations] selectedStudents:', selectedStudents);
      if (dwells.length > 0) {
        console.log('[loadDwellLocations] Sample dwell entity_ids:', dwells.slice(0, 5).map((d: any) => d.entity_id));
      }
      
      // Filter dwells to only include selected students
      const studentDwells = dwells.filter((dwell: any) =>
        selectedStudents.includes(dwell.entity_id)
      );
      console.log('[loadDwellLocations] After filter:', studentDwells.length, 'dwells match');
      
      debugLog('pathfinder', '🏠 [loadDwellLocations] After filtering for selected students:', studentDwells.length, 'dwells');
      if (studentDwells.length > 0) {
        debugLog('pathfinder', '🏠 [loadDwellLocations] Sample dwell:', {
          entity_id: studentDwells[0].entity_id,
          deviceType: studentDwells[0].deviceType || studentDwells[0].device_type,
          start_time: studentDwells[0].start_time,
          end_time: studentDwells[0].end_time,
        });
      }
      
      setDwellLocations(studentDwells);
      
      // Initially show all dwells
      setVisibleDwells(studentDwells);
      
      return studentDwells;
    } catch (error) {
      console.error('❌ Failed to load dwell locations:', error);
      // Don't throw - dwells are optional supplementary data
      return [];
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    
    try {
      setLoading(true);
      const results = await pathfinderApi.searchData(searchTerm, activeFilters);
      setDataPoints(results);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load initial data — skip if returning to the page with data already loaded
  useEffect(() => {
    if (!hasData) {
      loadDatabases();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (availableCourses.length === 0) {
      loadCourses();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedCourse) {
      debugLog('pathfinder', '📚 Loading students for course:', selectedCourse, 'coursesData keys:', Object.keys(coursesData));
    loadStudents();
  } else {
    setAvailableStudents([]);
    // Only clear selected students if NOT in a Quick Launch flow
    if (!quickLaunchInProgress.current && !quickLaunchPendingLoad && quickLaunchCoTravelers.length === 0) {
      setSelectedStudents([]);
    }
  }
}, [selectedCourse, coursesData, quickLaunchPendingLoad, quickLaunchCoTravelers.length]); // Also re-run when coursesData is populated

  // ========== QUICK LAUNCH URL PARAMETER HANDLING ==========
  // When the page is loaded with Quick Launch params, auto-configure and load data
  useEffect(() => {
    // Only process once per page load
    if (quickLaunchProcessed.current) return;
    
    // Check for Quick Launch parameters
    const quickLaunchId = searchParams.get('quickLaunchId');
    const studentParam = searchParams.get('student');
    const courseParam = searchParams.get('course');
    const dateParam = searchParams.get('date');
    const startTimeParam = searchParams.get('start');
    const endTimeParam = searchParams.get('end');
    const startUtcParam = searchParams.get('startUtc');
    const endUtcParam = searchParams.get('endUtc');
    const coTravelersParam = searchParams.get('coTravelers');
    
    // If no Quick Launch params, skip
    if (!quickLaunchId && !studentParam) return;
    
    // Mark as processed to prevent re-runs
    quickLaunchProcessed.current = true;
    // Mark Quick Launch in progress (ref survives re-renders)
    quickLaunchInProgress.current = true;
    
    debugLog('pathfinder', '🚀 Quick Launch detected:', {
      quickLaunchId,
      student: studentParam,
      course: courseParam,
      date: dateParam,
      startTime: startTimeParam,
      endTime: endTimeParam,
      startUtc: startUtcParam,
      endUtc: endUtcParam,
      coTravelers: coTravelersParam
    });
    
    // Show notification
    setQuickLaunchNotification({
      open: true,
      message: `Quick Launch: Loading ${studentParam || 'student'}...`,
      severity: 'info'
    });
    
    // Build date range from params
    let startDate: Date | null = null;
    let endDate: Date | null = null;

    const startUtc = parseUtcInstant(startUtcParam);
    const endUtc = parseUtcInstant(endUtcParam);
    if (startUtc && endUtc) {
      startDate = startUtc;
      endDate = endUtc;
      setDateRangeIsUtcInstant(true);
    } else if (dateParam) {
      // Parse the exercise date - manually to avoid timezone issues
      // Date format: "2026-01-06"
      const [year, month, day] = dateParam.split('-').map(Number);
      const exerciseDate = new Date(year, month - 1, day); // month is 0-indexed
      
      if (startTimeParam && endTimeParam) {
        // Parse start and end times (format: HH:MM)
        const [startHours, startMinutes] = startTimeParam.split(':').map(Number);
        const [endHours, endMinutes] = endTimeParam.split(':').map(Number);
        
        startDate = new Date(exerciseDate);
        startDate.setHours(startHours, startMinutes, 0, 0);
        
        endDate = new Date(exerciseDate);
        endDate.setHours(endHours, endMinutes, 0, 0);
        
        // Handle overnight exercises (end time < start time means next day)
        if (endDate < startDate) {
          endDate.setDate(endDate.getDate() + 1);
        }
      } else {
        // Default to full day
        startDate = new Date(exerciseDate);
        startDate.setHours(0, 0, 0, 0);
        
        endDate = new Date(exerciseDate);
        endDate.setHours(23, 59, 59, 999);
      }
      setDateRangeIsUtcInstant(false);
    }
    
    // Parse co-travelers - these are separate entities (SRP, RP, etc.), NOT students
    const coTravelers = coTravelersParam ? coTravelersParam.split(',').filter(Boolean) : [];
    debugLog('pathfinder', '🚀 Quick Launch: Parsed co-travelers:', coTravelers);
    
    // Function to apply Quick Launch configuration once courses are loaded
    const applyQuickLaunchConfig = async () => {
      try {
        // Set course (this will trigger loadStudents via useEffect)
        if (courseParam) {
          debugLog('pathfinder', '🚀 Quick Launch: Setting course:', courseParam);
          setSelectedCourse(courseParam);
        }
        
        // Wait for course change to propagate and students to load
        // The useEffect [selectedCourse] triggers loadStudents which needs coursesData
        debugLog('pathfinder', '🚀 Quick Launch: Waiting for students to load...');
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // IMPORTANT: Only set the PRIMARY student in selectedStudents
        // Co-travelers are NOT students - they're separate entities loaded differently
        if (studentParam) {
          debugLog('pathfinder', '🚀 Quick Launch: Setting primary student:', studentParam);
          setSelectedStudents([studentParam]);
        }
        
        // Store co-travelers separately - they'll be loaded after primary data
        if (coTravelers.length > 0) {
          debugLog('pathfinder', '🚀 Quick Launch: Storing co-travelers for later load:', coTravelers);
          setQuickLaunchCoTravelers(coTravelers);
        }
        
        // Set date range
        if (startDate && endDate) {
          debugLog('pathfinder', '🚀 Quick Launch: Setting date range:', { startDate, endDate });
          setDateRange({ start: startDate, end: endDate });
        }
        
        // Wait for state to update
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Set flag to trigger load from the dedicated useEffect
        // This ensures load happens after React has processed all state updates
        setQuickLaunchPendingLoad(true);
        
        // Force a re-render to trigger the load effect
        setQuickLaunchNotification({
          open: true,
          message: `Quick Launch: Loading data for ${studentParam}${coTravelers.length > 0 ? ` + ${coTravelers.length} co-travelers` : ''}...`,
          severity: 'info'
        });
        
      } catch (error) {
        console.error('Quick Launch error:', error);
        setQuickLaunchNotification({
          open: true,
          message: 'Quick Launch: Failed to apply configuration',
          severity: 'error'
        });
      }
    };
    
    // Apply configuration after a short delay to ensure courses are loaded
    setTimeout(applyQuickLaunchConfig, 1000);
    
  }, [searchParams, availableCourses]);

  // ========== QUICK LAUNCH AUTO-LOAD TRIGGER ==========
  // This effect triggers the actual load once state is ready
  useEffect(() => {
    // Check if we should trigger a Quick Launch load
    if (!quickLaunchPendingLoad) return;
    
    // Ensure we have the required state set
    if (selectedStudents.length === 0 || !dateRange.start || !dateRange.end) {
      debugLog('pathfinder', '🚀 Quick Launch: Waiting for state...', { 
        students: selectedStudents.length, 
        hasDateRange: !!(dateRange.start && dateRange.end) 
      });
      return;
    }
    
    // Clear the flag
    setQuickLaunchPendingLoad(false);
    
    debugLog('pathfinder', '🚀 Quick Launch: Triggering auto-load with state:', {
      course: selectedCourse,
      students: selectedStudents,
      dateRange: { start: dateRange.start, end: dateRange.end },
      coTravelers: quickLaunchCoTravelers
    });
    
    // Async function to load data + co-travelers
    const loadWithCoTravelers = async () => {
      try {
        // Step 1: Load the primary student's data
        setLoading(true);
        setLoadError(null);
        setLoadingMessage('Loading primary student data...');
        
        const query = {
          dateRange: [dateRange.start, dateRange.end] as [Date, Date],
          timeZone: queryTimeZone,
          studentIds: selectedStudents,
          selectedCourse: selectedCourse,
          deviceTypes: ['phone', 'watch', 'vehicle', 'wardriver'],
          limit: undefined
        };
        
        const points = await pathfinderApi.queryDataPoints(query);
        debugLog('pathfinder', '🚀 Quick Launch: Loaded primary student points:', points.length);
        
        // Generate routes from points BEFORE setting state
        const primaryRoutes = pathfinderApi.generateRoutes(points);
        debugLog('pathfinder', '🚀 Quick Launch: Generated primary routes:', primaryRoutes.length);
        
        setDataPoints(points);
        setRoutes(primaryRoutes);
        setVisibleDataPoints(points);
        setVisibleRoutes(primaryRoutes);
        await extractDevicesEfficiently(points);
        await loadDwellLocations();

        // Step 2: If we have pre-computed co-travelers, load their tracks
        if (quickLaunchCoTravelers.length > 0) {
          debugLog('pathfinder', '🚀 Quick Launch: Loading co-traveler tracks for:', quickLaunchCoTravelers);
          setLoadingMessage(`Loading ${quickLaunchCoTravelers.length} co-traveler tracks...`);
          
          // The sidebar uses formatted device IDs like "Trainee-09-watch" (entityId + deviceType)
          // Extract unique device types from the loaded GPS points
          const studentId = selectedStudents[0];
          const deviceTypes = Array.from(new Set(points.map(p => {
            // Device type is in the point data (e.g., 'phone', 'watch', 'vehicle')
            return p.deviceType || 'phone';
          })));
          
          // Build formatted device IDs that match what the sidebar uses
          const formattedDeviceIds = deviceTypes.map(type => `${studentId}-${type}`);
          debugLog('pathfinder', '🚀 Quick Launch: Device types from GPS data:', deviceTypes);
          debugLog('pathfinder', '🚀 Quick Launch: Using formatted device IDs for sidebar:', formattedDeviceIds);
          
          // Create mock co-traveler results for the FIRST device only (avoid duplicates in sidebar)
          // Quick Launch doesn't know which device had the encounters, so we just pick the first one
          const primaryDeviceId = formattedDeviceIds[0];
          const mockCoTravelerResults: any[] = [];
          
          quickLaunchCoTravelers.forEach(ctId => {
            // Determine entity type from ID
            let entityType = 'Unknown';
            if (ctId.startsWith('SRP-')) entityType = 'SRP';
            else if (ctId.startsWith('RP-')) entityType = 'RP';
            else if (ctId.startsWith('Trainee-') || ctId.startsWith('Student-')) entityType = 'Student';
            else if (ctId.startsWith('DHOST-')) entityType = 'DHOST';
            else if (ctId.startsWith('DHOQC-')) entityType = 'DHOQC';
            
            mockCoTravelerResults.push({
              primaryDevice: primaryDeviceId, // Use first device for all co-travelers
              coTravelerDevice: ctId,
              coTravelerEntityType: entityType,
              totalEncounters: 1, // Placeholder
              encounters: [], // Empty encounters array for compatibility
              totalDuration: 0,
              confidenceScore: 0.8
            });
          });
          
          debugLog('pathfinder', '🚀 Quick Launch: Created', mockCoTravelerResults.length, 'mock co-traveler results');
          debugLog('pathfinder', '🚀 Quick Launch: First few results:', mockCoTravelerResults.slice(0, 3));
          
          // Store the mock results so they appear in the sidebar
          setCoTravelerResults(mockCoTravelerResults);
          
          // Initialize visibility - enable all by default for Quick Launch
          const initialVisibility: Record<string, boolean> = {};
          mockCoTravelerResults.forEach(result => {
            const key = `${result.primaryDevice}-${result.coTravelerDevice}`;
            initialVisibility[key] = true; // Enable by default
          });
          setCoTravelerVisibility(initialVisibility);
          
          // Load the actual GPS tracks for co-travelers
          // Use unique co-traveler IDs (not duplicated per device)
          const uniqueCoTravelerResults = quickLaunchCoTravelers.map(ctId => {
            let entityType = 'Unknown';
            if (ctId.startsWith('SRP-')) entityType = 'SRP';
            else if (ctId.startsWith('RP-')) entityType = 'RP';
            else if (ctId.startsWith('Trainee-') || ctId.startsWith('Student-')) entityType = 'Student';
            else if (ctId.startsWith('DHOST-')) entityType = 'DHOST';
            else if (ctId.startsWith('DHOQC-')) entityType = 'DHOQC';
            
            return {
              primaryDevice: formattedDeviceIds[0] || selectedStudents[0],
              coTravelerDevice: ctId,
              coTravelerEntityType: entityType,
              totalEncounters: 1,
              encounters: [],
              totalDuration: 0,
              confidenceScore: 0.8
            };
          });
          
          await loadCoTravelerTracks(uniqueCoTravelerResults, points, primaryRoutes);
          
          debugLog('pathfinder', '🚀 Quick Launch: Co-traveler tracks loaded!');
          
          // Clear the Quick Launch co-travelers after loading
          setQuickLaunchCoTravelers([]);
        }
        
        setQuickLaunchNotification({
          open: true,
          message: `Quick Launch: Data loaded successfully!${quickLaunchCoTravelers.length > 0 ? ` (${quickLaunchCoTravelers.length} co-travelers)` : ''}`,
          severity: 'success'
        });
        
        // Clear URL parameters so refresh doesn't re-run Quick Launch
        setSearchParams({}, { replace: true });
        debugLog('pathfinder', '🚀 Quick Launch: Cleared URL parameters');
        
      } catch (error: any) {
        console.error('🚀 Quick Launch: Error loading data:', error);
        setLoadError(error?.message || 'Failed to load data');
        setQuickLaunchNotification({
          open: true,
          message: 'Quick Launch: Failed to load data',
          severity: 'error'
        });
        // Also clear URL params on error to prevent retry loop on refresh
        setSearchParams({}, { replace: true });
      } finally {
        setLoading(false);
        setLoadingMessage('');
        // Mark Quick Launch as complete
        quickLaunchInProgress.current = false;
      }
    };
    
    loadWithCoTravelers();
    
  }, [quickLaunchPendingLoad, selectedStudents, dateRange, selectedCourse, quickLaunchCoTravelers]);

  // Only clear data when students or date range changes, don't auto-load
  useEffect(() => {
    // Skip clearing during Quick Launch flow
    if (quickLaunchInProgress.current) {
      return;
    }
    
    if (selectedStudents.length === 0 || !dateRange.start || !dateRange.end) {
      // Clear data when no students selected or no date range
      setDataPoints([]);
      setVisibleDataPoints([]);
      setRoutes([]);
      setVisibleRoutes([]);
      setTimeRange(null);
      setActualDevices({});
      setSelectedDevices({});
    }
    // Don't auto-load data - user must click the Load button
  }, [selectedStudents, dateRange]);

  // Story Mode callbacks (memoized to prevent infinite loops)
  // Supports null to "unfocus" and restore original map extent.
  const handleStoryModeMapChange = useCallback((center: [number, number] | null) => {
    focusMapToLocation(center, 15);
  }, [focusMapToLocation]);

  const handleStoryModeTimeChange = useCallback((time: Date) => {
    setCurrentTime(time);
  }, []);

  // Map narrative overlay: clicking an event flies to it and scrubs the slider.
  const handleNarrativeEventSelect = useCallback((event: StoryEvent) => {
    const location = event.type === 'dwell' ? event.location : event.startLocation;
    if (location) focusMapToLocation(location, 15);
    setCurrentTime(event.startTime);
  }, [focusMapToLocation]);

  // Create studentColors mapping for MapGL component
  // Always use all dataPoints/routes for color calculation to ensure colors are available.
  // Color resolution is delegated to `resolvePathfinderColor` so primary,
  // cotraveler, and device-tree paths all paint the same hue for the same
  // (entity_type, device_type, device_id) tuple. SRP/RP overrides come for
  // free via that helper.
  const studentColors = useMemo(() => {
    const colors: Record<string, string> = {};

    // Assign colors based on device type from data points (use all dataPoints, not just visible)
    dataPoints.forEach(point => {
      const deviceType = (point.deviceType || 'phone').toLowerCase();
      const deviceId = point.deviceId || point.studentId || '';
      const studentId = point.studentId || point.deviceId || '';
      // Cotraveler points carry coTravelerKey (e.g. "Student-22 - Phone")
      // which is the key the device-tree stores user-picked colors under.
      // Prefer it for resolution so the picker's color flows through.
      const coTravelerKey = (point as any).coTravelerKey as string | undefined;
      const isAdtech = (point.metadata as any)?.source === 'adtech';
      const color = resolvePathfinderColor({
        entityType: isAdtech ? 'ADTECH' : undefined,
        // colorForAdtechId strips a trailing -phone/-watch/-vehicle, so
        // `${regId}-phone` hashes the same as the bare registration id.
        deviceId: coTravelerKey || deviceId || studentId,
        deviceType,
        customDeviceColors,
      });

      if (deviceId) {
        colors[deviceId] = color;
      }
      if (studentId) {
        colors[studentId] = color;
      }
      if (coTravelerKey) {
        colors[coTravelerKey] = color;
      }
      if (studentId && deviceType) {
        colors[`${studentId}-${deviceType}`] = color;
      }
    });
    
    // Also color routes based on their device type (use all routes, not just visible).
    // Reuse colors already assigned from data points (covers AdTech PoL).
    routes.forEach(route => {
      const coTravelerKey = (route as any).coTravelerKey as string | undefined;
      if (route.deviceId) {
        const parts = route.deviceId.split('-');
        const deviceType = parts[parts.length - 1]?.toLowerCase() || 'phone';
        const preexisting =
          colors[route.deviceId] ||
          (route.studentId ? colors[route.studentId] : undefined) ||
          (coTravelerKey ? colors[coTravelerKey] : undefined);
        const color = preexisting || resolvePathfinderColor({
          deviceId: coTravelerKey || route.deviceId,
          deviceType,
          customDeviceColors,
        });
        colors[route.deviceId] = color;
        if (route.studentId) colors[route.studentId] = color;
        if (coTravelerKey) {
          colors[coTravelerKey] = color;
        }
      }
    });

    return colors;
  }, [dataPoints, routes, customDeviceColors]);

  // Per-cotraveler legend swatches. Filtered to currently-visible cotravelers
  // so the map legend mirrors what's actually painted on the map. Each entry
  // resolves its color through `resolvePathfinderColor` with `customDeviceColors`,
  // so user-picked colors (single or group) flow through automatically.
  const coTravelerLegendItems = useMemo(() => {
    if (!coTravelerResults || coTravelerResults.length === 0) return [];
    const items: Array<{ name: string; color: string; entityType?: string }> = [];
    const seen = new Set<string>();
    for (const result of coTravelerResults) {
      const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
      if (coTravelerVisibility[resultKey] !== true) continue;
      if (seen.has(result.coTravelerDevice)) continue;
      seen.add(result.coTravelerDevice);
      // Best-effort device-type extraction so Student cotravelers (which
      // get phone/watch/vehicle-distinct hues) render the right color.
      const lowerName = result.coTravelerDevice.toLowerCase();
      const deviceType = lowerName.includes('vehicle')
        ? 'vehicle'
        : lowerName.includes('watch')
        ? 'watch'
        : lowerName.includes('phone')
        ? 'phone'
        : undefined;
      const color = resolvePathfinderColor({
        entityType: result.coTravelerEntityType,
        deviceType,
        deviceId: result.coTravelerDevice,
        customDeviceColors,
      });
      items.push({
        name: result.coTravelerDevice,
        color,
        entityType: result.coTravelerEntityType,
      });
    }
    return items;
  }, [coTravelerResults, coTravelerVisibility, customDeviceColors]);

  // Active tab data selection - determines which dataset to display
  // These must be defined BEFORE the tabs useMemo since StoryMode uses them

  const areaCirclesWithData = useMemo(
    () => drawCircles.filter(c => c.results && c.results.length > 0),
    [drawCircles]
  );

  const sentinelTabIndex = 1 + areaCirclesWithData.length;
  const isSentinelTabActive = useMemo(() => {
    return leftPanelTab === sentinelTabIndex;
  }, [leftPanelTab, sentinelTabIndex]);
  const isEntityContextTab = leftPanelTab === 0 || isSentinelTabActive;

  /** Circle id for the active Area N tab; null on Entities or Sentinel. */
  const activeAreaCircleId = useMemo(() => {
    if (leftPanelTab >= 1 && leftPanelTab < sentinelTabIndex) {
      return areaCirclesWithData[leftPanelTab - 1]?.id ?? null;
    }
    return null;
  }, [leftPanelTab, sentinelTabIndex, areaCirclesWithData]);

  const activeDataPoints = useMemo(() => {
    if (isEntityContextTab) return dataPoints;
    if (activeAreaCircleId) {
      return areaSearchDataPoints.filter(p => p.areaSearchCircleId === activeAreaCircleId);
    }
    return areaSearchDataPoints;
  }, [isEntityContextTab, dataPoints, areaSearchDataPoints, activeAreaCircleId]);

  const activeRoutes = useMemo(() => {
    if (isEntityContextTab) return routes;
    if (activeAreaCircleId) {
      const pts = areaSearchDataPoints.filter(p => p.areaSearchCircleId === activeAreaCircleId);
      return pathfinderApi.generateRoutes(pts);
    }
    return areaSearchRoutes;
  }, [isEntityContextTab, routes, areaSearchDataPoints, areaSearchRoutes, activeAreaCircleId]);

  const activeSelectedDevices = useMemo(() => {
    if (isEntityContextTab) return selectedDevices;
    if (leftPanelTab >= 1 && leftPanelTab < sentinelTabIndex) {
      const idx = leftPanelTab - 1;
      const circle = areaCirclesWithData[idx];
      if (!circle) return areaSearchSelectedDevices;
      const defaults = buildAreaSelectedMapFromCircle(circle);
      return perAreaSelectedDevices[circle.id] ?? defaults;
    }
    return areaSearchSelectedDevices;
  }, [
    leftPanelTab,
    isEntityContextTab,
    selectedDevices,
    areaSearchSelectedDevices,
    sentinelTabIndex,
    areaCirclesWithData,
    perAreaSelectedDevices,
  ]);

  const packetDwellLocations = useMemo(
    () => (areaSearchDwellLocations || []).filter((d: any) => String(d.areaSearchCircleId || '').startsWith('intel-packet-')),
    [areaSearchDwellLocations],
  );
  const packetVisibleDwells = useMemo(
    () => (visibleAreaSearchDwells || []).filter((d: any) => String(d.areaSearchCircleId || '').startsWith('intel-packet-')),
    [visibleAreaSearchDwells],
  );

  const activeDwellLocations = useMemo(() => {
    if (isEntityContextTab) return [...dwellLocations, ...packetDwellLocations];
    if (activeAreaCircleId) {
      return [
        ...areaSearchDwellLocations.filter((d: any) => d.areaSearchCircleId === activeAreaCircleId),
        ...packetDwellLocations,
      ];
    }
    return areaSearchDwellLocations;
  }, [isEntityContextTab, dwellLocations, areaSearchDwellLocations, activeAreaCircleId, packetDwellLocations]);

  // Tabs will be defined after filteredVisibleDataPoints and filteredVisibleDwells

  // Visible data based on active tab and time filtering
  const activeVisibleDataPoints = useMemo(() => {
    if (isEntityContextTab) return visibleDataPoints;
    if (activeAreaCircleId) {
      return areaSearchDataPoints.filter(p => p.areaSearchCircleId === activeAreaCircleId);
    }
    return areaSearchDataPoints;
  }, [isEntityContextTab, visibleDataPoints, areaSearchDataPoints, activeAreaCircleId]);

  const activeVisibleRoutes = useMemo(() => {
    if (isEntityContextTab) return visibleRoutes;
    if (activeAreaCircleId) {
      const pts = areaSearchDataPoints.filter(p => p.areaSearchCircleId === activeAreaCircleId);
      return pathfinderApi.generateRoutes(pts);
    }
    return areaSearchRoutes;
  }, [isEntityContextTab, visibleRoutes, areaSearchDataPoints, areaSearchRoutes, activeAreaCircleId]);

  const activeVisibleDwells = useMemo(() => {
    if (isEntityContextTab) return [...visibleDwells, ...packetVisibleDwells];
    if (activeAreaCircleId) {
      return [
        ...visibleAreaSearchDwells.filter((d: any) => d.areaSearchCircleId === activeAreaCircleId),
        ...packetVisibleDwells,
      ];
    }
    return visibleAreaSearchDwells;
  }, [isEntityContextTab, visibleDwells, visibleAreaSearchDwells, activeAreaCircleId, packetVisibleDwells]);

  const mapDrawCircles = useMemo(() => {
    if (leftPanelTab >= 1 && leftPanelTab < sentinelTabIndex && activeAreaCircleId) {
      return drawCircles.filter(c => c.id === activeAreaCircleId);
    }
    return drawCircles;
  }, [drawCircles, leftPanelTab, sentinelTabIndex, activeAreaCircleId]);

  // Filter dataPoints based on selectedStudents, selectedDevices AND active annotations (using active tab data)
  const filteredVisibleDataPoints = useMemo(() => {
    // First filter by selected students
    const activeSelectedStudents = isEntityContextTab ? selectedStudents : Object.keys(areaSearchActualDevices);
    
    let filtered = activeVisibleDataPoints.filter(point => {
      // Always show co-traveler data if visible
      if ((point as any).isCoTraveler) return true;
      
      // Only show points from selected students
      if (!activeSelectedStudents.includes(point.studentId)) return false;
      return true;
    });
    
    // Then filter by selected devices
    filtered = filtered.filter(point => {
      // Always show co-traveler data if visible
      if ((point as any).isCoTraveler) return true;
      
      // Get the selected devices for this student
      const studentDevices = activeSelectedDevices[point.studentId] || [];
      
      // If student has no devices selected (empty array), hide their data
      if (studentDevices.length === 0) return false;
      
      const rawPtType = (point.deviceType || 'phone').toLowerCase();
      const normalizedPtType = rawPtType.includes('phone') ? 'phone' : rawPtType;
      const deviceKey = `${point.studentId}-${normalizedPtType}`;
      return studentDevices.includes(deviceKey);
    });
    
    // If filtering by annotations is enabled AND there are active annotations
    if (filterByAnnotations && activeAnnotations.length > 0) {
      filtered = filtered.filter(point => {
        // Co-traveler data passes through
        if ((point as any).isCoTraveler) return true;
        
        // Check if point is in any active annotation's polygon for its entity
        return activeAnnotations.some(annotation => {
          // Must match entity
          if (annotation.entity_id !== point.studentId) return false;
          
          // Check polygon if present
          if (annotation.geometry?.coordinates?.[0] && point.coordinates) {
            return isPointInPolygon(point.coordinates, annotation.geometry.coordinates[0]);
          }
          
          // No polygon means all points for this entity pass
          return true;
        });
      });
    }
    
    // Apply chart filter (day, hour, day+hour filtering)
    if (chartFilter) {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      
      filtered = filtered.filter(point => {
        const ts = point.timestamp;
        const date = ts instanceof Date ? ts : new Date(ts);
        
        if (isNaN(date.getTime())) {
          return true; // Keep points with invalid timestamps
        }
        
        const pointDay = days[date.getDay()];
        const pointHour = date.getHours();
        
        if (chartFilter.type === 'dayHour') {
          return pointDay === chartFilter.day && pointHour === chartFilter.hour;
        } else if (chartFilter.type === 'day') {
          return pointDay === chartFilter.day;
        } else if (chartFilter.type === 'hour') {
          return pointHour === chartFilter.hour;
        }
        return true;
      });
    }
    
    // Apply White Cell sampling - show only one point per interval
    if (whiteCellInterval !== null && whiteCellInterval > 0) {
      const intervalMs = whiteCellInterval * 60 * 1000; // Convert minutes to milliseconds
      
      // Group points by device (studentId + deviceType)
      const deviceGroups: Record<string, typeof filtered> = {};
      
      filtered.forEach(point => {
        // Co-traveler data passes through unchanged
        if ((point as any).isCoTraveler) {
          const coTravelerKey = '__cotraveler__';
          if (!deviceGroups[coTravelerKey]) deviceGroups[coTravelerKey] = [];
          deviceGroups[coTravelerKey].push(point);
          return;
        }
        
        const rawPtType = (point.deviceType || 'phone').toLowerCase();
        const normalizedPtType = rawPtType.includes('phone') ? 'phone' : rawPtType;
        const deviceKey = `${point.studentId}-${normalizedPtType}`;
        if (!deviceGroups[deviceKey]) deviceGroups[deviceKey] = [];
        deviceGroups[deviceKey].push(point);
      });
      
      // For each device, sample one point per interval
      const sampledPoints: typeof filtered = [];
      
      Object.entries(deviceGroups).forEach(([deviceKey, points]) => {
        // Co-traveler points pass through unchanged
        if (deviceKey === '__cotraveler__') {
          sampledPoints.push(...points);
          return;
        }
        
        // Sort by timestamp
        const sorted = [...points].sort((a, b) => 
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        
        if (sorted.length === 0) return;
        
        // Keep track of the last sampled timestamp
        let lastSampledTime = new Date(sorted[0].timestamp).getTime();
        sampledPoints.push(sorted[0]); // Always keep the first point
        
        for (let i = 1; i < sorted.length; i++) {
          const pointTime = new Date(sorted[i].timestamp).getTime();
          
          // Only add point if it's at least intervalMs after the last sampled point
          if (pointTime - lastSampledTime >= intervalMs) {
            sampledPoints.push(sorted[i]);
            lastSampledTime = pointTime;
          }
        }
      });
      
      filtered = sampledPoints;
    }
    
    return filtered;
  }, [activeVisibleDataPoints, activeSelectedDevices, activeAnnotations, isPointInPolygon, filterByAnnotations, chartFilter, selectedStudents, isEntityContextTab, areaSearchActualDevices, whiteCellInterval]);

  // Data for charts - includes all filters EXCEPT chartFilter (so charts always show full pattern)
  // Charts should show the complete activity pattern and highlight the selected bar
  const chartDataPoints = useMemo(() => {
    const activeSelectedStudents = isEntityContextTab ? selectedStudents : Object.keys(areaSearchActualDevices);
    
    let filtered = activeVisibleDataPoints.filter(point => {
      if ((point as any).isCoTraveler) return true;
      if (!activeSelectedStudents.includes(point.studentId)) return false;
      return true;
    });
    
    filtered = filtered.filter(point => {
      if ((point as any).isCoTraveler) return true;
      const studentDevices = activeSelectedDevices[point.studentId] || [];
      if (studentDevices.length === 0) return false;
      // Normalize cellphone/smartphone/samsungphone -> "phone" so the key
      // matches what extractDevicesEfficiently stores in activeSelectedDevices.
      const rawPtType = (point.deviceType || 'phone').toLowerCase();
      const normalizedPtType = rawPtType.includes('phone') ? 'phone' : rawPtType;
      const deviceKey = `${point.studentId}-${normalizedPtType}`;
      return studentDevices.includes(deviceKey);
    });
    
    if (filterByAnnotations && activeAnnotations.length > 0) {
      filtered = filtered.filter(point => {
        if ((point as any).isCoTraveler) return true;
        return activeAnnotations.some(annotation => {
          if (annotation.entity_id !== point.studentId) return false;
          if (annotation.geometry?.coordinates?.[0] && point.coordinates) {
            return isPointInPolygon(point.coordinates, annotation.geometry.coordinates[0]);
          }
          return true;
        });
      });
    }
    
    // NOTE: chartFilter is NOT applied here - charts should always show full pattern
    
    return filtered;
  }, [activeVisibleDataPoints, activeSelectedDevices, activeAnnotations, isPointInPolygon, filterByAnnotations, selectedStudents, isEntityContextTab, areaSearchActualDevices]);

  // Filter routes based on selectedStudents, selectedDevices AND active annotations (using active tab data)
  const filteredVisibleRoutes = useMemo(() => {
    // First filter by selected students
    const activeSelectedStudents = isEntityContextTab ? selectedStudents : Object.keys(areaSearchActualDevices);
    
    let filtered = activeVisibleRoutes.filter(route => {
      // Always show co-traveler routes if visible
      if ((route as any).isCoTravelerRoute) return true;
      
      // Extract student ID from deviceId (e.g., "Student-14-phone" -> "Student-14")
      const studentId = route.deviceId.replace(/-[^-]+$/, '');
      
      // Only show routes from selected students
      if (!activeSelectedStudents.includes(studentId)) return false;
      return true;
    });
    
    // Then filter by selected devices
    filtered = filtered.filter(route => {
      // Always show co-traveler routes if visible
      if ((route as any).isCoTravelerRoute) return true;
      
      // Extract student ID from deviceId (e.g., "Student-14-phone" -> "Student-14")
      const studentId = route.deviceId.replace(/-[^-]+$/, '');
      
      // Get the selected devices for this student
      const studentDevices = activeSelectedDevices[studentId] || [];
      
      // If student has no devices selected (empty array), hide their routes
      if (studentDevices.length === 0) return false;
      
      // Route deviceId format is also usually "Student-07-phone"
      return studentDevices.includes(route.deviceId);
    });
    
    // If filtering by annotations is enabled AND there are active annotations
    if (filterByAnnotations && activeAnnotations.length > 0) {
      filtered = filtered.map(route => {
        // Co-traveler routes pass through
        if ((route as any).isCoTravelerRoute) return route;
        
        // Extract student ID from deviceId (e.g., "Student-14-phone" -> "Student-14")
        const studentId = route.deviceId.replace(/-[^-]+$/, '');
        
        // Check if any annotation applies to this student
        const relevantAnnotations = activeAnnotations.filter(a => a.entity_id === studentId);
        if (relevantAnnotations.length === 0) return null; // No annotation for this student
        
        // Filter route coordinates by annotation polygons
        const filteredCoords = route.coordinates.filter(coord => {
          return relevantAnnotations.some(annotation => {
            if (annotation.geometry?.coordinates?.[0]) {
              return isPointInPolygon(coord, annotation.geometry.coordinates[0]);
            }
            return true; // No polygon = all points pass
          });
        });
        
        if (filteredCoords.length < 2) return null; // Need at least 2 points for a route
        
        // Also filter the points array to match
        const filteredPoints = route.points.filter(point => {
          if (!point.coordinates) return false;
          return relevantAnnotations.some(annotation => {
            if (annotation.geometry?.coordinates?.[0]) {
              return isPointInPolygon(point.coordinates, annotation.geometry.coordinates[0]);
            }
            return true;
          });
        });
        
        return { ...route, coordinates: filteredCoords, points: filteredPoints };
      }).filter(Boolean) as typeof filtered;
    }
    
    // Apply chart filter (day, hour, day+hour filtering) to routes (including co-traveler routes)
    if (chartFilter && chartFilter.type !== 'location') {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      
      filtered = filtered.map(route => {
        // Filter points by time (applies to both primary and co-traveler routes)
        const filteredPoints = route.points.filter(point => {
          if (!point.timestamp) return false;
          const ts = point.timestamp;
          const date = ts instanceof Date ? ts : new Date(ts);
          
          if (isNaN(date.getTime())) return true; // Keep points with invalid timestamps
          
          const pointDay = days[date.getDay()];
          const pointHour = date.getHours();
          
          if (chartFilter.type === 'dayHour') {
            return pointDay === chartFilter.day && pointHour === chartFilter.hour;
          }
          if (chartFilter.type === 'day') {
            return pointDay === chartFilter.day;
          }
          if (chartFilter.type === 'hour') {
            return pointHour === chartFilter.hour;
          }
          return true;
        });
        
        if (filteredPoints.length < 2) return null; // Need at least 2 points for a route
        
        // Extract coordinates from filtered points
        const filteredCoords = filteredPoints
          .filter(p => p.coordinates)
          .map(p => p.coordinates as [number, number]);
        
        if (filteredCoords.length < 2) return null;
        
        return { ...route, coordinates: filteredCoords, points: filteredPoints };
      }).filter(Boolean) as typeof filtered;
    }
    
    return filtered;
  }, [activeVisibleRoutes, activeSelectedDevices, activeAnnotations, isPointInPolygon, filterByAnnotations, chartFilter, selectedStudents, isEntityContextTab, areaSearchActualDevices]);

  // Filter dwells based on selectedStudents, selectedDevices AND active annotations (using active tab data)
  const filteredVisibleDwells = useMemo(() => {
    // First filter by selected students
    const activeSelectedStudents = isEntityContextTab ? selectedStudents : Object.keys(areaSearchActualDevices);
    
    const isPacketDwell = (dwell: any) => String(dwell.areaSearchCircleId || '').startsWith('intel-packet-');
    let filtered = activeVisibleDwells.filter(dwell => {
      if (isPacketDwell(dwell)) return true;
      const dwellEntityId = dwell.entityId || dwell.entity_id;
      // Only show dwells from selected students
      if (!activeSelectedStudents.includes(dwellEntityId)) return false;
      return true;
    });
    
    // Then filter by selected devices
    filtered = filtered.filter(dwell => {
      if (isPacketDwell(dwell)) return true;
      const dwellEntityId = dwell.entityId || dwell.entity_id;
      
      // Get the selected devices for this student
      const studentDevices = activeSelectedDevices[dwellEntityId] || [];
      
      // If student has no devices selected (empty array), hide their dwells
      if (studentDevices.length === 0) return false;
      
      // Filter by device: check if dwell's device matches any selected device
      // Normalize device type variants (cellphone/samsungphone -> phone, etc.)
      const rawType = (dwell.deviceType || dwell.device_type || 'phone').toLowerCase();
      const normalizedType = rawType.includes('phone') ? 'phone' : rawType;
      
      // Match against any device key variant for this student
      return studentDevices.some((d: string) => {
        const dLower = d.toLowerCase();
        return dLower.includes(normalizedType) || dLower.includes(rawType);
      });
    });
    
    // If filtering by annotations is enabled AND there are active annotations
    if (filterByAnnotations && activeAnnotations.length > 0) {
      filtered = filtered.filter(dwell => {
        const dwellEntityId = dwell.entityId || dwell.entity_id;
        const dwellCoords: [number, number] = [dwell.latitude, dwell.longitude];
        
        // Check if dwell is in any active annotation's polygon for its entity
        return activeAnnotations.some(annotation => {
          // Must match entity
          if (annotation.entity_id !== dwellEntityId) return false;
          
          // Check polygon if present
          if (annotation.geometry?.coordinates?.[0] && dwellCoords[0] && dwellCoords[1]) {
            return isPointInPolygon(dwellCoords, annotation.geometry.coordinates[0]);
          }
          
          // No polygon means all dwells for this entity pass
          return true;
        });
      });
    }
    
    // Apply chart filter (day, hour, day+hour, or location filtering)
    if (chartFilter) {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      
      filtered = filtered.filter(dwell => {
        // Location filter - match by place name or address
        if (chartFilter.type === 'location') {
          const dwellName = dwell.place_name || dwell.place_address || 
            (dwell.latitude && dwell.longitude ? `${dwell.latitude.toFixed(3)}, ${dwell.longitude.toFixed(3)}` : 'Unknown');
          return dwellName === chartFilter.location;
        }
        
        // Time-based filters (parse dwells as UTC if timezone is missing)
        const startDate = parseBackendDateTime(dwell.start_time);
        if (!startDate) return false;
        const pointDay = days[startDate.getDay()];
        const pointHour = startDate.getHours();
        
        if (chartFilter.type === 'dayHour') {
          return pointDay === chartFilter.day && pointHour === chartFilter.hour;
        }
        if (chartFilter.type === 'day') {
          return pointDay === chartFilter.day;
        }
        if (chartFilter.type === 'hour') {
          return pointHour === chartFilter.hour;
        }
        
        return true;
      });
    }
    
    return filtered;
  }, [activeVisibleDwells, activeSelectedDevices, activeAnnotations, isPointInPolygon, filterByAnnotations, chartFilter, selectedStudents, isEntityContextTab, areaSearchActualDevices]);

  // Same as `filteredVisibleDwells` but additionally cropped to the current map
  // viewport when the user has toggled "Crop to map view". This is what the
  // Dwell Analysis right-side table renders — the map itself keeps showing
  // every visible dwell so panning around doesn't hide markers under your cursor.
  const tableFilteredVisibleDwells = useMemo(() => {
    if (!filterByMapExtent || !mapBounds) return filteredVisibleDwells;
    return filteredVisibleDwells.filter((dwell: any) => isInMapBounds(dwell.latitude, dwell.longitude));
  }, [filteredVisibleDwells, filterByMapExtent, mapBounds, isInMapBounds]);

  // Cropped variant for Interactive Charts data points. Both primary AND
  // co-traveler points are filtered by coordinates: when "Crop to map view"
  // is on, the user explicitly wants the chart to reflect only what's
  // currently on screen. Co-traveler visibility toggles still apply downstream
  // (in the InteractiveCharts tab's coTravelerResults filter), so a cropped
  // chart is the intersection of "in viewport" AND "visibility on".
  const tableFilteredChartDataPoints = useMemo(() => {
    if (!filterByMapExtent || !mapBounds) return chartDataPoints;
    return chartDataPoints.filter(point => {
      const coords = point.coordinates;
      if (!coords || coords.length < 2) return false;
      return isInMapBounds(coords[0], coords[1]);
    });
  }, [chartDataPoints, filterByMapExtent, mapBounds, isInMapBounds]);

  // Dwells for charts - excludes chartFilter so charts always show full pattern
  const chartDwellLocations = useMemo(() => {
    const activeSelectedStudents = isEntityContextTab ? selectedStudents : Object.keys(areaSearchActualDevices);
    
    let filtered = activeVisibleDwells.filter(dwell => {
      const dwellEntityId = dwell.entityId || dwell.entity_id;
      if (!activeSelectedStudents.includes(dwellEntityId)) return false;
      return true;
    });
    
    filtered = filtered.filter(dwell => {
      const dwellEntityId = dwell.entityId || dwell.entity_id;
      const studentDevices = activeSelectedDevices[dwellEntityId] || [];
      if (studentDevices.length === 0) return false;
      
      const deviceType = (dwell.deviceType || dwell.device_type || 'phone');
      const dwellDeviceKey = `${dwellEntityId} - ${deviceType.charAt(0).toUpperCase() + deviceType.slice(1).toLowerCase()}`;
      const dwellDeviceKeyOld = `${dwellEntityId}-${deviceType.toLowerCase()}`;
      
      return studentDevices.includes(dwellDeviceKey) || studentDevices.includes(dwellDeviceKeyOld);
    });
    
    if (filterByAnnotations && activeAnnotations.length > 0) {
      filtered = filtered.filter(dwell => {
        const dwellEntityId = dwell.entityId || dwell.entity_id;
        const dwellCoords: [number, number] = [dwell.latitude, dwell.longitude];
        return activeAnnotations.some(annotation => {
          if (annotation.entity_id !== dwellEntityId) return false;
          if (annotation.geometry?.coordinates?.[0] && dwellCoords[0] && dwellCoords[1]) {
            return isPointInPolygon(dwellCoords, annotation.geometry.coordinates[0]);
          }
          return true;
        });
      });
    }
    
    // NOTE: chartFilter is NOT applied here - charts should always show full pattern
    
    return filtered;
  }, [activeVisibleDwells, activeSelectedDevices, activeAnnotations, isPointInPolygon, filterByAnnotations, selectedStudents, isEntityContextTab, areaSearchActualDevices]);

  // Cropped variants used by the Interactive Charts and Co-Traveler tabs when
  // "Crop to map view" is active. Defined here (after their inputs) to keep
  // declaration order valid.
  const tableFilteredChartDwellLocations = useMemo(() => {
    if (!filterByMapExtent || !mapBounds) return chartDwellLocations;
    return chartDwellLocations.filter((dwell: any) => isInMapBounds(dwell.latitude, dwell.longitude));
  }, [chartDwellLocations, filterByMapExtent, mapBounds, isInMapBounds]);

  // Co-traveler results filtered to those with at least one encounter location
  // inside the current viewport. Empty-encounter results pass through so the
  // user still sees them when they have a frequent-location match instead.
  // Used by the Co-Traveler Analysis tab where the panel is per-result, not
  // per-encounter — keeping a result with even one in-bounds encounter is
  // what the user expects.
  const tableFilteredCoTravelerResults = useMemo(() => {
    if (!filterByMapExtent || !mapBounds) return coTravelerResults;
    return coTravelerResults.filter(result => {
      const encs = result.encounters || [];
      const freq = result.frequentLocations || [];
      const encInside = encs.some(e => {
        const loc = e.location;
        return Array.isArray(loc) && loc.length >= 2 && isInMapBounds(loc[0], loc[1]);
      });
      if (encInside) return true;
      // Fallback: check frequent locations so digital co-travelers (which may
      // synthesize cluster centers rather than per-encounter coords) survive.
      return freq.some(f => {
        const loc = f.location;
        return Array.isArray(loc) && loc.length >= 2 && isInMapBounds(loc[0], loc[1]);
      });
    });
  }, [coTravelerResults, filterByMapExtent, mapBounds, isInMapBounds]);

  // Same idea but ALSO cropped per-encounter, for the Interactive Charts tab.
  // The chart re-derives `encounterTimestamps` directly from
  // `result.encounters`, so unless we trim each result's encounters here, the
  // SRP / RP / Co-Traveler series in the activity heatmap, hourly activity,
  // and weekly pattern charts won't reflect the user's current view.
  //
  // We crop on TWO axes:
  //   1. Map extent — when "Crop to map view" is on, drop encounters outside
  //      the current viewport (this was the leak that made the crop toggle
  //      look like a no-op for SRP/RP series).
  //   2. Playback time window — when the timeline is windowed (`playbackWindow`
  //      is non-null, e.g. user dragged a 32-minute slice), drop encounters
  //      outside that time range. The primary subject's points are already
  //      windowed via `visibleDataPoints`, so without this the co-traveler
  //      series kept showing hours/days the user wasn't actually looking at.
  // Map-only variant of the co-traveler crop. Mirrors the chart version's
  // time-window logic, but intentionally SKIPS the "Crop to map view" extent
  // filter — the map shouldn't hide its own pins just because they're near
  // the edge of the current viewport, and applying the extent filter here
  // would also fight follow-mode (pins blink in/out as the camera pans).
  // Co-traveler encounters carry their own per-encounter timestamps, which
  // is why the map's three GeoJSON memos (connections / lines / heatmap)
  // need this gating — without it they always render every encounter
  // regardless of slider position, which is the "co-travelers don't move
  // with the slider" bug.
  const mapVisibleCoTravelerResults = useMemo(() => {
    if (playbackWindow == null) return coTravelerResults;
    const winStart = playbackWindow.start.getTime();
    const winEnd = playbackWindow.end.getTime();
    const inWindow = (ts: any) => {
      const t = ts instanceof Date ? ts.getTime() : new Date(ts).getTime();
      return Number.isFinite(t) && t >= winStart && t <= winEnd;
    };
    const out: typeof coTravelerResults = [];
    coTravelerResults.forEach(result => {
      const encsInside = (result.encounters || []).filter(e => inWindow(e.timestamp));
      if (encsInside.length === 0) return;
      out.push({
        ...result,
        encounters: encsInside,
        totalEncounters: encsInside.length,
      });
    });
    return out;
  }, [coTravelerResults, playbackWindow]);

  // Raw AdTech area-search dots obey the time slider too: when the timeline is
  // windowed, only plot observations whose timestamp falls inside it (ALL mode
  // => playbackWindow null => show everything). Mirrors the RF/co-traveler
  // gating so "any loaded data" moves with the slider.
  const mapVisibleAdtechSignals = useMemo(() => {
    if (playbackWindow == null) return adtechMapSignals;
    const winStart = playbackWindow.start.getTime();
    const winEnd = playbackWindow.end.getTime();
    return adtechMapSignals.filter((s) => {
      if (!s.timestamp) return false;
      const t = new Date(s.timestamp).getTime();
      return Number.isFinite(t) && t >= winStart && t <= winEnd;
    });
  }, [adtechMapSignals, playbackWindow]);

  const tableFilteredCoTravelerResultsForCharts = useMemo(() => {
    const cropExtent = filterByMapExtent && mapBounds;
    const cropTime = playbackWindow != null;
    if (!cropExtent && !cropTime) return coTravelerResults;
    const winStart = cropTime ? playbackWindow!.start.getTime() : -Infinity;
    const winEnd = cropTime ? playbackWindow!.end.getTime() : Infinity;
    const inWindow = (ts: any) => {
      if (!cropTime) return true;
      const t = ts instanceof Date ? ts.getTime() : new Date(ts).getTime();
      return Number.isFinite(t) && t >= winStart && t <= winEnd;
    };
    const inExtent = (loc: any) =>
      !cropExtent || (Array.isArray(loc) && loc.length >= 2 && isInMapBounds(loc[0], loc[1]));
    const out: typeof coTravelerResults = [];
    coTravelerResults.forEach(result => {
      const encsInside = (result.encounters || []).filter(e =>
        inExtent(e.location) && inWindow(e.timestamp)
      );
      // Frequent locations are aggregated, no per-row timestamp to gate on,
      // so we only filter them by extent.
      const freqInside = (result.frequentLocations || []).filter(f => inExtent(f.location));
      if (encsInside.length === 0 && freqInside.length === 0) return;
      out.push({
        ...result,
        encounters: encsInside,
        frequentLocations: freqInside.length > 0 ? freqInside : result.frequentLocations,
        totalEncounters: encsInside.length,
      });
    });
    return out;
  }, [coTravelerResults, filterByMapExtent, mapBounds, isInMapBounds, playbackWindow]);

  const allToolTabs = useMemo(() => [
    { 
      id: 'interactive-charts',
      group: 'analyze' as PathfinderToolGroupId,
      label: 'Interactive Charts', 
      icon: <ChartIcon />, 
      component: (
        <InteractiveCharts
          dataPoints={tableFilteredChartDataPoints.filter(point => {
            // Filter out hidden co-traveler points (same logic as map)
            if ((point as any).isCoTraveler) {
              const pointStudentId = (point.studentId || '').toLowerCase();
              const pointDeviceId = (point.deviceId || '').toLowerCase();
              
              for (const result of coTravelerResults) {
                const coTravelerDevice = (result.coTravelerDevice || '').toLowerCase();
                const coTravelerEntity = coTravelerDevice.includes(' - ') 
                  ? coTravelerDevice.split(' - ')[0] 
                  : coTravelerDevice;
                
                const isMatch = 
                  coTravelerEntity === pointStudentId ||
                  coTravelerEntity === pointDeviceId ||
                  coTravelerDevice === pointDeviceId ||
                  (pointStudentId && coTravelerDevice.includes(pointStudentId)) ||
                  (pointStudentId && coTravelerEntity.includes(pointStudentId)) ||
                  (pointDeviceId && coTravelerEntity.includes(pointDeviceId));
                
                if (isMatch) {
                  const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
                  return coTravelerVisibility[resultKey] === true;
                }
              }
              return false;
            }
            return true;
          })}
          dwellLocations={tableFilteredChartDwellLocations}
          coTravelerResults={tableFilteredCoTravelerResultsForCharts.filter(result => {
            const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
            return coTravelerVisibility[resultKey] === true;
          })}
          studentColors={studentColors}
          activeFilter={chartFilter}
          onFilterChange={setChartFilter}
        />
      )
    },
    { 
      id: 'area-search',
      group: 'find' as PathfinderToolGroupId,
      label: 'Area Search', 
      icon: <AreaSearchIcon />, 
      component: (
        <AreaSearch
          selectedStudents={selectedStudents}
          selectedCourse={selectedCourse}
          dateRange={dateRange}
          queryTimeZone={queryTimeZone}
          timeRange={timeRange}
          onDrawModeChange={handleDrawModeChange}
          onCircleAdd={handleCircleAdd}
          onCircleUpdate={handleCircleUpdate}
          onCircleDelete={handleCircleDelete}
          drawCircles={drawCircles}
          isDrawingMode={isDrawingMode}
          defaultRadius={defaultRadius}
          onRadiusChange={setDefaultRadius}
          onLoadAreaData={handleLoadAreaData}
          adtechEnabled={adtechEnabled}
          onLoadAreaDwells={handleLoadAreaDwells}
          onLoadAdtechToMap={handleLoadAdtechToMap}
          onLoadSavedDevices={handleLoadSavedDevices}
          onLoadAdtechPol={handleLoadAdtechPol}
          onLookupAdtechIps={handleLookupAdtechIps}
          adtechPolLoading={adtechPolLoading}
          students={availableStudents}
          // Freeform polygon draw → full area analysis (Tracer-style)
          isDrawingAdtechPolygon={isDrawingAdtechPolygon}
          onDrawAdtechPolygonModeChange={(on: boolean) => {
            if (on) {
              // Entering polygon draw — turn off circle draw to avoid conflicts.
              handleDrawModeChange(false);
              setIsDrawingAdtechPolygon(true);
            } else {
              setIsDrawingAdtechPolygon(false);
            }
          }}
        />
      )
    },
    {
      id: 'geofence',
      group: 'find' as PathfinderToolGroupId,
      label: 'Geofence',
      icon: <PentagonIcon />,
      component: (
        <GeofencePanel
          dateRange={dateRange}
          queryTimeZone={queryTimeZone}
          adtechEnabled={adtechEnabled}
          geofences={geofences}
          loadingGeofences={geofencesLoading}
          selectedGeofenceId={selectedGeofenceId}
          onSelectGeofence={setSelectedGeofenceId}
          isDrawing={isDrawingGeofence}
          onStartDrawing={() => setIsDrawingGeofence(true)}
          onStopDrawing={() => setIsDrawingGeofence(false)}
          pendingGeofencePolygon={pendingGeofencePolygon}
          onClearPending={() => setPendingGeofencePolygon(null)}
          onGeofencesChanged={loadGeofences}
          onZoomTo={(lat, lng) => { setMapCenter([lat, lng]); setMapZoom(15); }}
          onLoadAdtechToMap={handleLoadAdtechToMap}
          courseId={selectedCourse}
          onLoadAdtechPol={handleLoadAdtechPol}
          adtechPolLoading={adtechPolLoading}
        />
      )
    },
    ...(overwatchFlag ? [{
      id: 'overwatch',
      group: 'find' as PathfinderToolGroupId,
      label: 'OSINT',
      icon: <NewspaperIcon sx={{ color: overwatchArticles.length > 0 ? '#ff9800' : undefined }} />,
      component: (
        <OverwatchPanel
          overwatchEnabled={overwatchEnabled}
          dateRange={dateRange}
          queryTimeZone={queryTimeZone}
          mapCenter={mapCenter}
          mapBounds={mapBounds}
          loadedArticles={overwatchArticles}
          onLoadArticlesToMap={setOverwatchArticles}
          onClearArticles={() => setOverwatchArticles([])}
          onZoomTo={(lat, lng) => { setMapCenter([lat, lng]); setMapZoom(13); }}
          onZoomToExtent={(locs) => {
            const pts = (locs || []).filter((l) => l && l.latitude != null && l.longitude != null);
            if (pts.length === 0) return;
            const lats = pts.map((p) => p.latitude);
            const lngs = pts.map((p) => p.longitude);
            const minLat = Math.min(...lats), maxLat = Math.max(...lats);
            const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
            const centerLat = (minLat + maxLat) / 2;
            const centerLng = (minLng + maxLng) / 2;
            const maxDiff = Math.max(maxLat - minLat, maxLng - minLng);
            // Zoom ladder mirrors PathfinderMapGL's auto-fit rungs.
            let zoom = 13;
            if (pts.length > 1) {
              if (maxDiff >= 20) zoom = 2;
              else if (maxDiff >= 5) zoom = 4;
              else if (maxDiff >= 1) zoom = 6;
              else if (maxDiff >= 0.5) zoom = 8;
              else if (maxDiff >= 0.2) zoom = 9;
              else if (maxDiff >= 0.1) zoom = 10;
              else if (maxDiff >= 0.05) zoom = 11;
              else if (maxDiff >= 0.01) zoom = 12;
              else zoom = 13;
            }
            setMapCenter([centerLat, centerLng]);
            setMapZoom(zoom);
          }}
        />
      )
    }] : []),
    { 
      id: 'co-traveler',
      group: 'analyze' as PathfinderToolGroupId,
      label: 'Co-Traveler Analysis', 
      icon: <PeopleIcon />, 
      component: (
        <EnhancedCoTravelerAnalysis 
          dataPoints={dataPoints}
          selectedStudents={selectedStudents}
          selectedDevices={selectedDevices}
          dateRange={dateRange}
          selectedCourse={selectedCourse}
          adtechEnabled={adtechEnabled}
          coTravelerResults={tableFilteredCoTravelerResults}
          coTravelerVisibility={coTravelerVisibility}
          onVisualizationUpdate={async (results) => {
            setCoTravelerResults(results);
            // Don't auto-enable co-traveler layers; keep them OFF by default and respect user toggles.
            // If results are cleared, ensure connections are also cleared.
            if (results.length === 0) setShowCoTravelerConnections(false);

            // Initialize co-traveler visibility (default to visible)
            // Use functional update to avoid stale closure issues
            setCoTravelerVisibility(prev => {
              const newVisibility: Record<string, boolean> = {};
              results.forEach(result => {
                const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
                // Keep existing visibility if set, otherwise default to true (visible)
                newVisibility[resultKey] = prev[resultKey] ?? true;
              });
              return newVisibility;
            });
            
            // HEATMAP DATA: Extract encounter locations (WHERE co-traveling occurred)
            const encounterAreas = results.flatMap(result => 
              result.encounters.map((encounter: any) => ({
                location: encounter.location,
                timestamp: encounter.timestamp,
                primaryDevice: result.primaryDevice,
                coTravelerDevice: result.coTravelerDevice,
                proximity: encounter.proximity,
                confidence: encounter.confidence,
                duration: encounter.duration
              }))
            );
            
            setCoTravelerEncounterAreas(encounterAreas);
            
            // STEP 2: Load COMPLETE GPS tracks for ALL identified co-travelers
            // Use refs to get current values (avoid stale closure)
            if (results.length > 0) {
              await loadCoTravelerTracks(results, dataPointsRef.current, routesRef.current);
            }
          }}
          onVisibilityChange={setCoTravelerVisibility}
        />
      )
    },
    { 
      id: 'dwell-analysis',
      group: 'analyze' as PathfinderToolGroupId,
      label: 'Dwell Analysis', 
      icon: <DwellIcon />, 
      component: (
        <DwellAnalysis
          selectedStudents={selectedStudents}
          selectedCourse={selectedCourse}
          dateRange={dateRange}
          dwellLocations={tableFilteredVisibleDwells}
          onDwellClick={handleDwellClick}
          onPlaceNameResolved={handleDwellPlaceNameResolved}
        />
      )
    },
    {
      id: 'biometrics',
      group: 'analyze' as PathfinderToolGroupId,
      label: 'Biometrics',
      icon: <HeartIcon />,
      component: (
        <BiometricsPanel
          selectedStudents={selectedStudents}
          selectedCourse={selectedCourse}
          dateRange={dateRange}
          queryTimeZone={queryTimeZone}
          studentColors={studentColors}
          onFocusLocation={focusMapToLocation}
          onHighlightedPointsChange={setBiometricHighlights}
        />
      )
    },
    {
      id: 'story-mode',
      group: 'narrate' as PathfinderToolGroupId,
      label: 'Story Mode',
      icon: <MenuBookIcon />,
      component: (
        <StoryMode
          dataPoints={dataPoints}
          dwellLocations={dwellLocations}
          currentTime={currentTime}
          isFullExtent={isFullExtent}
          selectedStudents={selectedStudents}
          selectedDevices={selectedDevices}
          onMapCenterChange={handleStoryModeMapChange}
          onTimeChange={handleStoryModeTimeChange}
          pttUtterances={isCommercialBanner ? pttUtterances : []}
        />
      )
    },
    { 
      id: 'digital-pol',
      group: 'narrate' as PathfinderToolGroupId,
      label: 'Digital PoL', 
      icon: <PsychologyIcon />, 
      component: <DigitalPoL
        selectedCourse={selectedCourse}
        selectedStudents={selectedStudents}
        timespan={14}
        startDate={dateRange.start}
        endDate={dateRange.end}
        onDigitalPoLDataChange={handleDigitalPoLDataChange}
        onFocusLocation={(loc) => focusMapToLocation(loc, 15)}
        filterByMapExtent={filterByMapExtent}
        mapBounds={mapBounds}
        // Honor the per-device toggles from the Device List so AI insights
        // and the PowerPoint report only consider the device classes the
        // user has switched on (e.g. phone-only when watch/vehicle are off).
        selectedDevices={selectedDevices}
      />
    },
    ...(rfEnabled && RFSignalEnvironment ? [{
      id: 'signal-environment',
      group: 'analyze' as PathfinderToolGroupId,
      label: 'Signal Environment',
      icon: <SensorsIcon />,
      component: (
        <RFSignalEnvironment
          data={signalData}
          layerVisibility={signalLayerVisibility}
          onLayerVisibilityChange={setSignalLayerVisibility}
          deviceTypeFilter={signalDeviceTypeFilter}
          onDeviceTypeFilterChange={setSignalDeviceTypeFilter}
          // Pipe the map-extent crop through so the WiFi/Bluetooth/Cellular
          // tables only list signals visible in the current viewport when
          // the "Filter by map" toggle is on. Pass null when it's off so the
          // module's own isInBounds() short-circuits and we don't drop rows.
          mapBounds={filterByMapExtent ? mapBounds : null}
          onSignalFocus={(lat: number, lon: number) => focusMapToLocation([lat, lon], 17)}
          onSignalUnfocus={() => focusMapToLocation(null, 15)}
        />
      ),
    }] : []),
    ...(isCommercialBanner ? [{
      id: 'ptt-comms',
      group: 'narrate' as PathfinderToolGroupId,
      label: 'PTT',
      icon: <PttChatIcon />,
      component: (
        <PttCaptionPanel
          utterances={pttUtterances}
          query={pttSearch}
          onQueryChange={setPttSearch}
          activeId={activePttId}
          currentTime={currentTime}
          playbackWindow={playbackWindow}
          senderColors={pttSenderColors}
          timeZone={preferredTimezone}
          live={pttLiveEligible ? { connected: pttLiveConnected } : undefined}
          onSelect={(u) => seekToPtt(u, false)}
          onPlayFrom={(u) => seekToPtt(u, true)}
        />
      ),
    }] : []),
    {
      id: 'annotations',
      group: 'narrate' as PathfinderToolGroupId,
      label: 'Annotations',
      icon: <AssignmentIcon />,
      component: (
        <AnnotationPanel
          courseId={selectedCourse}
          selectedStudents={selectedStudents}
          dateRange={dateRange}
          isDrawingAnnotation={isDrawingAnnotation}
          onDrawModeChange={setIsDrawingAnnotation}
          drawnGeometry={drawnAnnotationGeometry}
          onClearDrawing={() => setDrawnAnnotationGeometry(null)}
          onJumpToAnnotation={(annotation) => {
            if (annotation.center_latitude && annotation.center_longitude) {
              setMapCenter([annotation.center_latitude, annotation.center_longitude]);
              setMapZoom(15);
            }
          }}
          onLoadAnnotationData={handleLoadAnnotationData}
          onToggleAnnotation={handleToggleAnnotation}
          activeAnnotationIds={activeAnnotations.map(a => a.id)}
          dataPoints={dataPoints}
          filterByMapExtent={filterByMapExtent}
          mapBounds={mapBounds}
        />
      )
    },
  ], [
    filteredVisibleDataPoints,
    dataPoints,
    chartFilter,
    setChartFilter,
    filteredVisibleDwells,
    dwellLocations,
    coTravelerResults,
    studentColors,
    selectedStudents,
    selectedCourse,
    dateRange,
    timeRange,
    handleDrawModeChange,
    handleCircleAdd,
    handleCircleUpdate,
    handleCircleDelete,
    drawCircles,
    isDrawingMode,
    defaultRadius,
    handleLoadAreaData,
    selectedDevices,
    loadCoTravelerTracks,
    currentTime,
    showDataPoints,
    showRouteLines,
    showHeatmap,
    showDwells,
    showCoTravelerConnections,
    visibleRoutes,
    visibleDwells,
    handleStoryModeMapChange,
    handleStoryModeTimeChange,
    isDrawingAnnotation,
    drawnAnnotationGeometry,
    selectedCourse,
    selectedStudents,
    handleLoadAnnotationData,
    handleToggleAnnotation,
    activeAnnotations,
    filterByAnnotations,
    chartFilter,
    coTravelerVisibility,
    chartDataPoints,
    chartDwellLocations,
    tableFilteredVisibleDwells,
    tableFilteredChartDataPoints,
    tableFilteredChartDwellLocations,
    tableFilteredCoTravelerResults,
    tableFilteredCoTravelerResultsForCharts,
    filterByMapExtent,
    mapBounds,
    playbackWindow,
    rfEnabled,
    signalData,
    signalLayerVisibility,
    setSignalLayerVisibility,
    signalDeviceTypeFilter,
    setSignalDeviceTypeFilter,
    focusMapToLocation,
    setBiometricHighlights,
    studentColors,
    queryTimeZone,
    adtechEnabled,
    handleLoadAreaDwells,
    setDefaultRadius,
    // Geofence tab deps
    geofences,
    geofencesLoading,
    selectedGeofenceId,
    isDrawingGeofence,
    pendingGeofencePolygon,
    loadGeofences,
    handleLoadAdtechToMap,
    handleLoadAdtechPol,
    adtechPolLoading,
    setMapCenter,
    setMapZoom,
    // Overwatch / Intel Packet tab deps
    overwatchFlag,
    overwatchEnabled,
    overwatchArticles,
    mapCenter,
    // PTT tab deps
    isCommercialBanner,
    pttUtterances,
    pttSearch,
    activePttId,
    pttLiveEligible,
    pttLiveConnected,
    preferredTimezone,
    seekToPtt,
  ]);

  const effectiveAccess = useEffectiveAccess();
  const tabs = useMemo(() => {
    return allToolTabs.filter((tab) => {
      const key = PATHFINDER_TAB_PERMISSION_BY_LABEL[tab.label];
      if (!key) return true;
      return hasPermission(effectiveAccess, key);
    });
  }, [allToolTabs, effectiveAccess]);

  const activeTool = useMemo(
    () => tabs.find((t) => t.id === activeToolId) ?? tabs[0] ?? null,
    [tabs, activeToolId],
  );

  useEffect(() => {
    if (tabs.length === 0) return;
    if (!tabs.some((t) => t.id === activeToolId)) {
      setActiveToolId(tabs[0].id);
    }
  }, [tabs, activeToolId, setActiveToolId]);

  // Ctrl/Cmd+K opens the tool jump palette while Pathfinder is mounted.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'k') return;
      e.preventDefault();
      setToolPaletteOpen(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      overflow: 'hidden' 
    }}>
      {/* Top Navigation Bar - Style */}
      <Box sx={{ 
        bgcolor: 'background.paper', 
        color: 'text.primary', 
        p: { xs: 0.25, sm: 1 },
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.5, sm: 2 },
        flexWrap: { xs: 'nowrap', lg: 'nowrap' },
        minHeight: { xs: 36, sm: 56 },
        maxHeight: { xs: 44, sm: '120px' },
        overflowY: 'auto',
        flexShrink: 0
      }}>

      {/* ===== MOBILE: Compact single-line toolbar ===== */}
      {isSmallScreen ? (
        <>
          {/* Hamburger menu - opens the nav drawer */}
          <IconButton
            onClick={toggleMobileDrawer}
            size="small"
            sx={{
              color: 'text.primary',
              width: 32,
              height: 32,
            }}
          >
            <MenuIcon sx={{ fontSize: '1.2rem' }} />
          </IconButton>

          {/* Controls button - opens bottom drawer with all selections */}
          <Tooltip title="Open controls">
            <IconButton
              onClick={() => setMobileControlsOpen(true)}
              size="small"
              sx={{
                color: 'primary.main',
                border: '1.5px solid',
                borderColor: 'primary.main',
                borderRadius: 1.5,
                width: 32,
                height: 32,
              }}
            >
              <TuneIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>

          {/* Compact summary chips */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, overflow: 'hidden', minWidth: 0 }}>
            {selectedCourse && (
              <Chip
                label={availableCourses.find(c => c.id === selectedCourse)?.title?.substring(0, 12) || courseSingular}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.65rem', maxWidth: 90, '& .MuiChip-label': { px: 0.75 } }}
              />
            )}
            {selectedStudents.length > 0 && (
              <Chip
                icon={<PeopleIcon sx={{ fontSize: '0.75rem !important' }} />}
                label={`${selectedStudents.length}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }}
              />
            )}
            {dateRange.start && dateRange.end && (
              <Chip
                icon={<CalendarIcon sx={{ fontSize: '0.7rem !important' }} />}
                label={`${dateRange.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${dateRange.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }}
              />
            )}
          </Box>

          {/* Quick Load button */}
          <Button
            variant="contained"
            size="small"
            onClick={loadData}
            disabled={loading || selectedStudents.length === 0}
            sx={{ minWidth: 50, height: 28, fontSize: '0.7rem', px: 1, textTransform: 'none' }}
          >
            {loading && !loadingWithCoTravelers ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              'Load'
            )}
          </Button>

          {/* Clear Button - only show when data is loaded */}
          {(dataPoints.length > 0 || routes.length > 0 || dwellLocations.length > 0 || areaSearchDataPoints.length > 0) && (
            <Tooltip title="Clear all map data">
              <span>
                <IconButton
                  color="error"
                  size="small"
                  onClick={clearMapData}
                  disabled={loading}
                  sx={{
                    width: 28,
                    height: 28,
                    border: '1px solid',
                    borderColor: 'error.main',
                  }}
                >
                  <ClearIcon sx={{ fontSize: '0.9rem' }} />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* Upload Data */}
          <Tooltip title="Upload CSV/KML/JSON">
            <IconButton
              size="small"
              onClick={() => setUploadDialogOpen(true)}
              sx={{
                color: 'primary.main',
                width: 28,
                height: 28,
              }}
            >
              <UploadIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>

          {/* Toggle Right Panel (tools) — open grouped tool drawer for discovery */}
          <IconButton 
            onClick={() => {
              const next = !mobileRightPanelOpen;
              setMobileRightPanelOpen(next);
              if (next) setMobileToolDrawerOpen(true);
            }}
            size="small"
            sx={{ 
              color: 'primary.main',
              width: 28,
              height: 28,
            }}
          >
            <ChartIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </>
      ) : (
        <>
        {/* ===== DESKTOP: Full toolbar (unchanged) ===== */}

      {/* Course Selection */}
        <FormControl size="small" sx={{ minWidth: { sm: 180, md: 220 }, flex: '0 0 auto' }}>
          <InputLabel sx={{ 
            color: 'text.primary',
            fontWeight: 'bold',
            fontSize: '1rem',
          }}>
            {courseSingular}
          </InputLabel>
          <Select
            value={selectedCourse || ''}
            label={courseSingular}
            onChange={(e) => setSelectedCourse(e.target.value || undefined)}
            disabled={loading || availableCourses.length === 0}
            sx={{ 
              color: 'text.primary',
              backgroundColor: 'action.hover',
              fontWeight: '500',
              fontSize: '0.875rem',
              height: 40,
              '& .MuiOutlinedInput-notchedOutline': { 
                borderColor: 'divider',
                borderWidth: '2px'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.light'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main'
              },
              '& .MuiSvgIcon-root': { color: 'text.primary', fontSize: '1.5rem' }
            }}
          >
            <MenuItem value="">
              <em>{allLabel}</em>
            </MenuItem>
            {availableCourses.map((course) => {
              const isMostRecent = course.id === mostRecentCourses.dhost || course.id === mostRecentCourses.dhoqc;
              return (
                <MenuItem 
                  key={course.id} 
                  value={course.id}
                  sx={isMostRecent ? {
                    backgroundColor: 'rgba(0, 200, 150, 0.15)',
                    borderLeft: '3px solid',
                    borderLeftColor: '#00c896',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 200, 150, 0.25)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(0, 200, 150, 0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 200, 150, 0.4)',
                      },
                    },
                  } : undefined}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {course.title}
                    {isMostRecent && (
                      <Typography 
                        component="span" 
                        sx={{ 
                          fontSize: '0.65rem', 
                          fontWeight: 700,
                          color: '#00c896',
                          backgroundColor: 'rgba(0, 200, 150, 0.2)',
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 1,
                          letterSpacing: '0.5px',
                        }}
                      >
                        LATEST
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {/* Student Selection */}
        <FormControl size="small" sx={{ minWidth: { sm: 180, md: 220 }, flex: '0 0 auto' }}>
          <InputLabel sx={{ 
            color: 'text.primary',
            fontWeight: 'bold',
            fontSize: '1rem',
          }}>
            {plural}
          </InputLabel>
          <Select
            multiple
            value={selectedStudents}
            label={plural}
            onChange={(e) => setSelectedStudents(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            disabled={loading || availableStudents.length === 0}
            renderValue={(selected) => {
              if (selected.length === 0) return 'Select';
              if (selected.length === 1) {
                const student = availableStudents.find(s => s.id === selected[0]);
                const name = student?.name || selected[0];
                return name.length > 15 ? name.substring(0, 15) + '...' : name;
              }
              return `${selected.length} selected`;
            }}
            sx={{ 
              color: 'text.primary',
              backgroundColor: 'action.hover',
              fontWeight: '500',
              fontSize: '0.875rem',
              height: 40,
              '& .MuiOutlinedInput-notchedOutline': { 
                borderColor: 'divider',
                borderWidth: '2px'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.light'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main'
              },
              '& .MuiSvgIcon-root': { color: 'text.primary', fontSize: '1.5rem' }
            }}
          >
            {availableStudents.map((student) => (
              <MenuItem key={student.id} value={student.id}>{student.name}</MenuItem>
            ))}
          </Select>
        </FormControl>


        {/* Date Range Button - Opens Modal */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<CalendarIcon sx={{ fontSize: '1.25rem' }} />}
          onClick={() => setDateTimePickerOpen(true)}
          sx={{
            minWidth: { sm: 180, md: 220 },
            flex: '0 0 auto',
            borderColor: 'divider',
            borderWidth: '2px',
            color: 'text.primary',
            backgroundColor: 'action.hover',
            height: 40,
            px: 2,
            '&:hover': {
              borderColor: 'primary.light',
              backgroundColor: 'action.hover',
            },
            textTransform: 'none',
            justifyContent: 'flex-start',
          }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
              {dateRange.start && dateRange.end ? (
                <>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
                    {dateRange.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {' - '}
                    {dateRange.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1 }}>
                    {dateRange.start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    {' - '}
                    {dateRange.end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" sx={{ fontSize: '0.875rem' }}>Date</Typography>
              )}
            </Box>
          </Button>



        {/* Load Button with dropdown options */}
        <ButtonGroup 
          variant="contained" 
          color="primary"
          size="small"
          disabled={loading || selectedStudents.length === 0}
          sx={{ height: 40 }}
        >
          <Button
            onClick={loadData}
            sx={{ minWidth: 80, fontSize: '0.875rem', px: 2 }}
          >
            {loading && !loadingWithCoTravelers ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              'Load'
            )}
          </Button>
          <Button
            size="small"
            onClick={(e) => setLoadMenuAnchor(e.currentTarget)}
            sx={{ px: 0.5, minWidth: 'auto' }}
          >
            <ArrowDropDownIcon sx={{ fontSize: '1.5rem' }} />
          </Button>
        </ButtonGroup>
        <Menu
          anchorEl={loadMenuAnchor}
          open={Boolean(loadMenuAnchor)}
          onClose={() => setLoadMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem 
            onClick={() => {
              setLoadMenuAnchor(null);
              loadData();
            }}
            disabled={loading}
          >
            <ListItemIcon>
              <PeopleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary="Load Selected" 
              secondary="Just the selected devices"
            />
          </MenuItem>
          {rfEnabled && (
            <MenuItem
              onClick={() => {
                setLoadMenuAnchor(null);
                loadSignalEnvironment();
              }}
              disabled={loading}
            >
              <ListItemIcon>
                <SensorsIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Load Signal Environment"
                secondary="WiFi, Bluetooth, Cellular, GNSS observations"
              />
            </MenuItem>
          )}
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block' }}>
            Physical Co-Traveler
          </Typography>
          <MenuItem 
            onClick={() => {
              setLoadMenuAnchor(null);
              loadDataWithCoTravelers('all');
            }}
            disabled={loading}
          >
            <ListItemIcon>
              {loadingWithCoTravelers ? (
                <CircularProgress size={20} />
              ) : (
                <GroupIcon fontSize="small" color="secondary" />
              )}
            </ListItemIcon>
            <ListItemText 
              primary="All Co-Travelers" 
              secondary="Selected + all co-traveler tracks"
            />
          </MenuItem>
          {!isCommercialBanner && (
            <MenuItem
              onClick={() => {
                setLoadMenuAnchor(null);
                loadDataWithCoTravelers('students');
              }}
              disabled={loading}
            >
              <ListItemIcon>
                <SchoolIcon fontSize="small" sx={{ color: '#4caf50' }} />
              </ListItemIcon>
              <ListItemText
                primary={`${singular} Co-Travelers`}
                secondary={`Selected + ${singular} co-travelers only`}
              />
            </MenuItem>
          )}
          {!isCommercialBanner && (
            <MenuItem
              onClick={() => {
                setLoadMenuAnchor(null);
                loadDataWithCoTravelers('rp_srp');
              }}
              disabled={loading}
            >
              <ListItemIcon>
                <ShieldIcon fontSize="small" sx={{ color: '#f44336' }} />
              </ListItemIcon>
              <ListItemText
                primary="RP & SRP Co-Travelers"
                secondary="Selected + Role Player co-travelers only"
              />
            </MenuItem>
          )}
          {rfEnabled && <Divider key="digital-cotrav-divider" sx={{ my: 0.5 }} />}
          {rfEnabled && (
            <Typography
              key="digital-cotrav-heading"
              variant="caption"
              sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block' }}
            >
              Digital Co-Traveler
            </Typography>
          )}
          {rfEnabled && (
            <MenuItem
              key="digital-cotrav-wifi"
              onClick={() => { setLoadMenuAnchor(null); loadDataWithCoTravelers('digital_wifi'); }}
              disabled={loading}
            >
              <ListItemIcon><WifiIcon fontSize="small" sx={{ color: '#2196f3' }} /></ListItemIcon>
              <ListItemText primary="Digital Co-Travelers (WiFi)" secondary="Find WiFi APs that follow operator across locations" />
            </MenuItem>
          )}
          {rfEnabled && (
            <MenuItem
              key="digital-cotrav-bluetooth"
              onClick={() => { setLoadMenuAnchor(null); loadDataWithCoTravelers('digital_bluetooth'); }}
              disabled={loading}
            >
              <ListItemIcon><BluetoothIcon fontSize="small" sx={{ color: '#00bcd4' }} /></ListItemIcon>
              <ListItemText primary="Digital Co-Travelers (Bluetooth)" secondary="Find BLE devices shadowing operator movements" />
            </MenuItem>
          )}
          {rfEnabled && (
            <MenuItem
              key="digital-cotrav-all"
              onClick={() => { setLoadMenuAnchor(null); loadDataWithCoTravelers('digital_all'); }}
              disabled={loading}
            >
              <ListItemIcon><SensorsIcon fontSize="small" sx={{ color: '#ff9800' }} /></ListItemIcon>
              <ListItemText primary="Digital Co-Travelers (All Signals)" secondary="WiFi + Bluetooth + Cellular co-occurrence analysis" />
            </MenuItem>
          )}
          {adtechEnabled && <Divider key="adtech-cotrav-divider" sx={{ my: 0.5 }} />}
          {adtechEnabled && (
            <Typography
              key="adtech-cotrav-heading"
              variant="caption"
              sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block' }}
            >
              AdTech
            </Typography>
          )}
          {adtechEnabled && (
            <MenuItem
              key="adtech-cotrav"
              onClick={() => { setLoadMenuAnchor(null); loadDataWithCoTravelers('adtech'); }}
              disabled={loading}
            >
              <ListItemIcon><SensorsIcon fontSize="small" sx={{ color: '#e91e63' }} /></ListItemIcon>
              <ListItemText primary="AdTech Co-Travelers" secondary="Commercial mobile-location devices recurring near operator" />
            </MenuItem>
          )}
        </Menu>

        {/* Clear Button - only show when data is loaded */}
        {(dataPoints.length > 0 || routes.length > 0 || dwellLocations.length > 0 || areaSearchDataPoints.length > 0) && (
          <Tooltip title="Clear all map data">
            <span>
              <IconButton
                color="error"
                size="small"
                onClick={clearMapData}
                disabled={loading}
                sx={{
                  ml: 1,
                  width: 36,
                  height: 36,
                  border: '1px solid',
                  borderColor: 'error.main',
                }}
              >
                <ClearIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* Upload Data Button */}
        <Tooltip title="Upload CSV, KML, or GeoJSON">
          <IconButton
            size="small"
            onClick={() => setUploadDialogOpen(true)}
            sx={{
              ml: 1,
              width: 36,
              height: 36,
              border: '1px solid',
              borderColor: 'primary.main',
              color: 'primary.main',
            }}
          >
            <UploadIcon sx={{ fontSize: '1.25rem' }} />
          </IconButton>
        </Tooltip>

        {/* Filter Map Data */}
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
          <TextField
            placeholder="filter map data"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'text.primary',
                '& fieldset': { borderColor: 'divider' },
                '& input::placeholder': { color: 'text.secondary' }
              }
            }}
          />
          <IconButton 
            sx={{ color: 'text.primary' }}
            onClick={handleSearch}
            disabled={loading || !searchTerm.trim()}
          >
            <SearchIcon />
          </IconButton>
          {/* "Crop to map view" toggle moved onto the map toolbar
              (PathfinderMapGL) so it lives where the map controls do. */}
        </Box>
        </>
      )}
      </Box>

      {/* Divider between header and main content */}
      <Divider />

      {/* Main Content Area - Style Layout */}
      <Box sx={{
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
        bgcolor: theme.palette.mode === 'dark' ? '#111418' : 'background.default',
      }}>
        {/* Left Sidebar - Device Tree (with tabs only when area search has data) */}
        <Box sx={{ 
          width: { xs: '100%', sm: leftPanelCollapsed ? 0 : leftPanelWidth },
          minWidth: { xs: 0, sm: leftPanelCollapsed ? 0 : 220 },
          maxWidth: { xs: '100%', sm: leftPanelCollapsed ? 0 : 500 },
          bgcolor: 'background.paper', 
          borderRight: { xs: 0, sm: leftPanelCollapsed ? 0 : 1 },
          borderColor: 'divider',
          display: { xs: 'none', sm: 'flex' }, // Hide on mobile - use overlay/modal pattern instead
          flexDirection: 'column',
          overflow: 'hidden',
          flexShrink: 0,
          // Transition only `width` (POL pattern). Animating multiple layout
          // properties at once causes extra reflows during the collapse and
          // amplifies the resize-storm visible to Mapbox.
          transition: 'width 0.3s ease',
          // Allow the scroll child below to shrink inside the flex column; without
          // minHeight: 0 the flex item's min-height is "auto" and the inner
          // overflow:auto stops early — Wi-Fi list scrolls but BLE never appears.
          minHeight: 0,
        }}>
          {/* Left panel tabs */}
          <Tabs 
            value={leftPanelTab} 
            onChange={(_, v) => setLeftPanelTab(v)}
            variant="fullWidth"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              minHeight: 36,
              '& .MuiTab-root': { 
                minHeight: 36, 
                py: 0.5,
                fontSize: '0.7rem',
                fontWeight: 'bold'
              }
            }}
          >
            <Tab 
              label={`${plural} ${Object.keys(actualDevices).length > 0 ? `(${Object.keys(actualDevices).length})` : ''}`}
              sx={{ textTransform: 'none' }}
            />
            {drawCircles.filter(c => c.results && c.results.length > 0).map((circle, idx) => (
              <Tab 
                key={circle.id}
                label={`Area ${idx + 1} (${circle.results?.length || 0})`}
                sx={{ textTransform: 'none' }}
              />
            ))}
            <Tab 
              icon={<PsychologyIcon sx={{ fontSize: 16 }} />}
              label="Sentinel"
              sx={{ textTransform: 'none' }}
            />
          </Tabs>

          {/* Content Area — single scroll container for the whole left panel */}
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
            {/* Saved Devices moved to the Area Search "Saved Devices" sub-tab. */}
            {leftPanelTab === 0 && (
              Object.keys(actualDevices).length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Load {pluralLower} to see their devices
                  </Typography>
                </Box>
              ) : (
                <HierarchicalDeviceTree
                  students={memoizedStudentData}
                  onStudentToggle={(studentId: string, selected: boolean) => {
                    const activeDeviceIds = (actualDevices[studentId] || [])
                      .filter((device: any) => device.active !== false)
                      .map((device: any) => device.id);
                    setSelectedDevices({
                      ...selectedDevices,
                      [studentId]: selected ? activeDeviceIds : [],
                    });
                  }}
                  onDeviceToggle={(studentId: string, deviceId: string, selected: boolean) => {
                    const studentDevices = selectedDevices[studentId] || [];
                    
                    if (selected) {
                      setSelectedDevices({
                        ...selectedDevices,
                        [studentId]: [...studentDevices, deviceId]
                      });
                    } else {
                      setSelectedDevices({
                        ...selectedDevices,
                        [studentId]: studentDevices.filter((id: string) => id !== deviceId)
                      });
                    }
                  }}
                  onStudentExpand={(studentId, expanded) => {
                    setExpandedStudents({
                      ...expandedStudents,
                      [studentId]: expanded
                    });
                  }}
                  selectedStudents={selectedStudents}
                  selectedDevices={selectedDevices}
                  coTravelerResults={coTravelerResults}
                  coTravelerVisibility={coTravelerVisibility}
                  onCoTravelerVisibilityChange={setCoTravelerVisibility}
                  customDeviceColors={customDeviceColors}
                  onDeviceColorChange={(deviceId, color) => {
                    if (color) {
                      setCustomDeviceColors(prev => ({ ...prev, [deviceId]: color }));
                    } else {
                      // Remove the custom color (reset to default)
                      setCustomDeviceColors(prev => {
                        const next = { ...prev };
                        delete next[deviceId];
                        return next;
                      });
                    }
                  }}
                />
              )
            )}

            {/* Signal Environment Section - shown when signal data is loaded */}
            {leftPanelTab === 0 && rfEnabled && signalData && (() => {
              const inTimeWindow = (s: any) => {
                if (!playbackWindow) return true;
                const first = s.first_seen ? new Date(s.first_seen).getTime() : 0;
                const last = s.last_seen ? new Date(s.last_seen).getTime() : Infinity;
                return first <= playbackWindow.end.getTime() && last >= playbackWindow.start.getTime();
              };
              const matchesDeviceType = (s: any) =>
                signalDeviceTypeFilter.length === 0 ||
                signalDeviceTypeFilter.includes(s.device_type || 'Unknown');
              const wifiInWindow = (signalData.wifi || []).filter(inTimeWindow);
              const btInWindow = (signalData.bluetooth || []).filter(inTimeWindow);
              const cellInWindow = (signalData.cellular || []).filter(inTimeWindow);
              const wifiCount = wifiInWindow.filter(matchesDeviceType).length;
              const btCount = btInWindow.filter(matchesDeviceType).length;
              const cellCount = cellInWindow.length; // cellular has no device_type taxonomy
              return (
              <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 1 }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1.5,
                  py: 1,
                  bgcolor: 'action.hover',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <SensorsIcon sx={{ fontSize: 18, color: '#4caf50' }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" fontWeight={700} noWrap sx={{ display: 'block', lineHeight: 1.2 }}>
                        Signal Environment
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', lineHeight: 1.2 }}>
                        {wifiCount + btCount + cellCount} signals{playbackWindow ? ' (in window)' : ''}
                        {signalDeviceTypeFilter.length > 0 ? ` · ${signalDeviceTypeFilter.length} type${signalDeviceTypeFilter.length === 1 ? '' : 's'}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                  {signalDeviceTypeFilter.length > 0 && (
                    <Typography
                      variant="caption"
                      onClick={() => setSignalDeviceTypeFilter([])}
                      sx={{ fontSize: '0.6rem', color: 'primary.main', cursor: 'pointer', flexShrink: 0, ml: 0.5 }}
                    >
                      Clear
                    </Typography>
                  )}
                </Box>
                <Box sx={{ px: 1.5, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {[
                    { key: 'wifi' as const, label: 'WiFi', count: wifiCount, color: '#2196f3', icon: <WifiIcon sx={{ fontSize: 14 }} /> },
                    { key: 'bluetooth' as const, label: 'Bluetooth', count: btCount, color: '#00bcd4', icon: <BluetoothIcon sx={{ fontSize: 14 }} /> },
                    { key: 'cellular' as const, label: 'Cellular', count: cellCount, color: '#ff9800', icon: <CellTowerIcon sx={{ fontSize: 14 }} /> },
                  ].map(({ key, label, count, color, icon }) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 0.5 }}>
                      <Switch
                        size="small"
                        checked={signalLayerVisibility[key] !== false}
                        onChange={() => setSignalLayerVisibility((prev: any) => ({ ...prev, [key]: !prev[key] }))}
                        sx={{
                          '& .MuiSwitch-thumb': { width: 12, height: 12 },
                          '& .MuiSwitch-track': { backgroundColor: color + '40' },
                          '&.Mui-checked .MuiSwitch-thumb': { backgroundColor: color },
                          flexShrink: 0,
                        }}
                      />
                      {icon}
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color, flex: 1 }}>
                        {label}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                        {count}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              );
            })()}

            {/* Uploaded Data Section - shown in Students tab when files have been uploaded */}
            {leftPanelTab === 0 && uploadedDataPoints.length > 0 && (
              <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 1 }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1.5,
                  py: 1,
                  bgcolor: 'action.hover',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <FolderIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" fontWeight={700} noWrap sx={{ display: 'block', lineHeight: 1.2 }}>
                        Uploaded Data
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', lineHeight: 1.2 }}>
                        {uploadedFileNames.length} file{uploadedFileNames.length !== 1 ? 's' : ''} &middot; {uploadedDataPoints.length} pts
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Tooltip title={showUploadedData ? 'Hide uploaded data' : 'Show uploaded data'}>
                      <Switch
                        size="small"
                        checked={showUploadedData}
                        onChange={(_, checked) => setShowUploadedData(checked)}
                        sx={{ '& .MuiSwitch-switchBase': { p: 0.5 }, '& .MuiSwitch-thumb': { width: 14, height: 14 } }}
                      />
                    </Tooltip>
                    <Tooltip title="Upload more files">
                      <IconButton size="small" onClick={() => setUploadDialogOpen(true)} sx={{ p: 0.25 }}>
                        <UploadIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Clear all uploaded data">
                      <IconButton size="small" color="error" onClick={clearUploadedData} sx={{ p: 0.25 }}>
                        <DeleteSweepIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                {/* List uploaded file names as toggleable chips */}
                <Box sx={{ px: 1.5, pb: 1 }}>
                  {uploadedFileNames.map((name) => {
                    const isVisible = uploadedFileVisibility[name] !== false;
                    const pts = uploadedDataPoints.filter(p =>
                      (p.metadata?.originalFileName || '').replace(/\.(csv|geojson|json|kml)$/i, '') === name
                    ).length;
                    return (
                      <Chip
                        key={name}
                        label={`${name} (${pts})`}
                        size="small"
                        variant={isVisible ? 'filled' : 'outlined'}
                        color="warning"
                        onClick={() => setUploadedFileVisibility((prev: Record<string, boolean>) => ({ ...prev, [name]: !isVisible }))}
                        sx={{
                          mr: 0.5,
                          mb: 0.5,
                          height: 22,
                          fontSize: '0.6rem',
                          cursor: 'pointer',
                          opacity: isVisible ? 1 : 0.5,
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Per-Area Tabs - each analyzed circle gets its own device tree */}
            {areaCirclesWithData.map((circle, idx) => {
              const areaTabIndex = 1 + idx;
              if (leftPanelTab !== areaTabIndex) return null;
              
              // Build device map grouped by entity (parse "Entity - DeviceType" format)
              const areaDeviceMap: Record<string, any[]> = {};
              const areaSelectedMap: Record<string, string[]> = {};
              const pointCounts: Record<string, number> = {};
              
              circle.results?.forEach((result: any) => {
                const fullId = result.entityId || result.entity_id || 'Unknown';
                // Parse "DoubleCross75 - phone" format back to entity + device
                const dashIdx = fullId.lastIndexOf(' - ');
                const entityId = dashIdx > 0 ? fullId.substring(0, dashIdx) : fullId;
                const rawType = dashIdx > 0 ? fullId.substring(dashIdx + 3) : (result.deviceType || 'phone');
                const deviceType = rawType.toLowerCase().includes('phone') ? 'phone' : rawType.toLowerCase();
                const deviceId = `${entityId}-${deviceType}`;
                
                if (!areaDeviceMap[entityId]) {
                  areaDeviceMap[entityId] = [];
                  areaSelectedMap[entityId] = [];
                }
                
                pointCounts[deviceId] = (pointCounts[deviceId] || 0) + (result.pointCount || 0);
                
                if (!areaDeviceMap[entityId].find((d: any) => d.id === deviceId)) {
                  areaDeviceMap[entityId].push({
                    id: deviceId,
                    type: deviceType,
                    name: `${deviceType.charAt(0).toUpperCase() + deviceType.slice(1)}`,
                    active: true,
                    color: resolvePathfinderColor({ deviceType }),
                    dataPointCount: result.pointCount || 0,
                    totalDataPoints: 0,
                  });
                  areaSelectedMap[entityId].push(deviceId);
                }
              });
              
              // Update point counts and totals
              const totalPoints = Object.values(pointCounts).reduce((a, b) => a + b, 0);
              Object.values(areaDeviceMap).forEach(devices => {
                devices.forEach((d: any) => {
                  d.dataPointCount = pointCounts[d.id] || d.dataPointCount;
                  d.totalDataPoints = totalPoints;
                });
              });

              const uniqueEntities = Object.keys(areaDeviceMap);
              const areaStudents = uniqueEntities.map((entityId, index) => ({
                id: entityId,
                name: entityId,
                devices: areaDeviceMap[entityId],
                selected: true,
                expanded: perAreaExpandedEntities[circle.id]?.[entityId] ?? (index === 0),
              }));

              return (
                <Box key={circle.id}>
                  <Typography variant="caption" sx={{ px: 1.5, py: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600, bgcolor: 'action.hover' }}>
                    Area {idx + 1} • {uniqueEntities.length} entities • {circle.radius}m radius
                  </Typography>
                  <HierarchicalDeviceTree
                    students={areaStudents}
                    onStudentToggle={(entityId: string, selected: boolean) => {
                      setPerAreaSelectedDevices(prev => {
                        const circleState = { ...(prev[circle.id] || areaSelectedMap) };
                        const activeDeviceIds = (areaDeviceMap[entityId] || [])
                          .filter((device: any) => device.active !== false)
                          .map((device: any) => device.id);
                        circleState[entityId] = selected ? activeDeviceIds : [];
                        return { ...prev, [circle.id]: circleState };
                      });
                    }}
                    onDeviceToggle={(entityId: string, deviceId: string, selected: boolean) => {
                      setPerAreaSelectedDevices(prev => {
                        const circleState = { ...(prev[circle.id] || areaSelectedMap) };
                        const entityDevices = circleState[entityId] || [];
                        if (selected) {
                          circleState[entityId] = entityDevices.includes(deviceId) ? entityDevices : [...entityDevices, deviceId];
                        } else {
                          circleState[entityId] = entityDevices.filter((id: string) => id !== deviceId);
                        }
                        return { ...prev, [circle.id]: circleState };
                      });
                    }}
                    onStudentExpand={(entityId, expanded) => {
                      setPerAreaExpandedEntities(prev => ({
                        ...prev,
                        [circle.id]: {
                          ...(prev[circle.id] || {}),
                          [entityId]: expanded,
                        },
                      }));
                    }}
                    selectedStudents={uniqueEntities}
                    selectedDevices={perAreaSelectedDevices[circle.id] || areaSelectedMap}
                    coTravelerResults={[]}
                    coTravelerVisibility={{}}
                    onCoTravelerVisibilityChange={() => {}}
                  />
                </Box>
              );
            })}

            {/* Ask Sentinel Tab */}
            {leftPanelTab === sentinelTabIndex && (
              <PathfinderSentinel
                dataPoints={dataPoints}
                routes={routes}
                dwellLocations={dwellLocations}
                actualDevices={actualDevices}
                selectedStudents={selectedStudents}
                selectedDevices={selectedDevices}
                timeRange={timeRange}
                onStudentSelect={undefined}
                itinerarySubmissions={itinerarySubmissions}
                onZoomToLocation={(coords) => {
                  setMapCenter([coords[1], coords[0]]);
                  setMapZoom(16);
                }}
                isDrawingAnalysisArea={isDrawingAnalysisArea}
                analysisArea={analysisArea}
                onMapAction={(action) => {
                  if (action.type === 'start_area_analysis') {
                    setIsDrawingAnalysisArea(true);
                    setIsDrawingAnnotation(true); // Reuse annotation drawing mode
                  } else if (action.type === 'cancel_area_analysis') {
                    setIsDrawingAnalysisArea(false);
                    setAnalysisArea(null);
                    setIsDrawingAnnotation(false);
                    setDrawnAnnotationGeometry(null);
                  }
                }}
              />
            )}
          </Box>
        </Box>

        {/* Left Panel Collapse Tab */}
        <Tooltip title={leftPanelCollapsed ? 'Show device panel' : 'Hide device panel'} placement="right">
          <Box
            onClick={() => setLeftPanelCollapsed(prev => !prev)}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              position: 'absolute',
              left: leftPanelCollapsed ? 0 : leftPanelWidth,
              top: 72,
              width: 24,
              height: 72,
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              bgcolor: 'background.paper',
              color: 'text.secondary',
              border: 1,
              borderColor: 'divider',
              borderLeft: leftPanelCollapsed ? 1 : 0,
              borderRadius: '0 8px 8px 0',
              boxShadow: 2,
              zIndex: 20,
              transition: 'left 0.3s ease, background-color 0.2s, color 0.2s',
              '&:hover': {
                bgcolor: 'action.hover',
                color: 'text.primary',
              },
            }}
          >
            {leftPanelCollapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
          </Box>
        </Tooltip>

        {/* Left Panel Resize Handle - Hidden on mobile */}
        <Box
          onMouseDown={handleLeftPanelMouseDown}
          sx={{
            display: { xs: 'none', sm: leftPanelCollapsed ? 'none' : 'block' },
            width: '4px',
            cursor: 'col-resize',
            bgcolor: isResizingLeft ? 'primary.main' : 'transparent',
            transition: 'background-color 0.2s',
            '&:hover': {
              bgcolor: 'primary.light',
            },
            position: 'relative',
            zIndex: 10,
          }}
        />

        {/* Main Map Area */}
        <Box
          // PathfinderMapGL fullscreens this column (map + floating timeline)
          // rather than just the map canvas, so the slider stays usable.
          data-pathfinder-map-column=""
          sx={{ 
          flex: '1 1 0%',
          minWidth: 0,
          display: 'flex', 
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: theme.palette.mode === 'dark' ? '#111418' : 'background.default',
        }}>
          <Box ref={mapTimelineBoundsRef} sx={{
            flex: 1,
            position: 'relative',
            width: '100%',
            minHeight: 0,
            bgcolor: theme.palette.mode === 'dark' ? '#111418' : 'background.default',
          }}>
          
          {/* SET8TimeSlider handles all playback functionality */}
          
          {loading && (
            <Box sx={{
              position: 'absolute',
              top: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              bgcolor: 'background.paper',
              color: 'text.primary',
              padding: '6px 14px',
              borderRadius: 1,
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>
              <Box sx={{
                width: 14,
                height: 14,
                border: '2px solid currentColor',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                opacity: 0.8,
              }} />
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {loadingMessage || 'Loading...'}
              </Typography>
            </Box>
          )}
          
          {/* Error Message Display */}
          {loadError && !loading && (
            <Box sx={{
              position: 'absolute',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1100,
              backgroundColor: 'error.main',
              color: 'white',
              padding: '10px 20px',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              maxWidth: '80%',
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                ⚠️ {loadError}
              </Typography>
              <Box
                component="button"
                onClick={() => setLoadError(null)}
                sx={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: 1,
                  color: 'white',
                  padding: '2px 8px',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                ✕
              </Box>
            </Box>
          )}
          
        {/* Map Container with Style Selector */}
        <Box sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          bgcolor: theme.palette.mode === 'dark' ? '#111418' : 'background.default',
        }}>
          {/* Now-speaking ticker: follow radio traffic during playback without the PTT panel open */}
          {isCommercialBanner && showPttMessages && nowSpeakingPtt && (
            <Box
              onClick={() => openPttInPanel(nowSpeakingPtt.id)}
              sx={{
                position: 'absolute',
                top: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 5,
                maxWidth: 'min(80%, 640px)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 999,
                bgcolor: 'rgba(17,20,24,0.88)',
                color: '#fff',
                boxShadow: 3,
                cursor: 'pointer',
                backdropFilter: 'blur(4px)',
              }}
              title="Open in PTT panel"
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: pttSenderColor(nowSpeakingPtt, pttSenderColors),
                  boxShadow: `0 0 0 4px ${pttSenderColor(nowSpeakingPtt, pttSenderColors)}44`,
                }}
              />
              <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                {nowSpeakingPtt.sender || nowSpeakingPtt.entity_id || nowSpeakingPtt.device_id}
                {nowSpeakingPtt.team ? ` · ${nowSpeakingPtt.team}` : ''}
              </Typography>
              <Typography
                variant="body2"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.95 }}
              >
                {(nowSpeakingPtt.transcript || '').trim() || '(no transcript)'}
              </Typography>
            </Box>
          )}
          <PathfinderMapGL
            dataPoints={(() => {
              const basePoints = filteredVisibleDataPoints;
              
              const filtered = basePoints.filter(point => {
                // Filter co-traveler points based on visibility toggles
                if ((point as any).isCoTraveler) {
                  const pointStudentId = (point.studentId || '').toLowerCase();
                  const pointDeviceId = (point.deviceId || '').toLowerCase();
                  
                  // Check if any co-traveler result matches this point
                  for (const result of coTravelerResults) {
                    const coTravelerDevice = (result.coTravelerDevice || '').toLowerCase();
                    const coTravelerEntity = coTravelerDevice.includes(' - ') 
                      ? coTravelerDevice.split(' - ')[0] 
                      : coTravelerDevice;
                    
                    const isMatch = 
                      coTravelerEntity === pointStudentId ||
                      coTravelerEntity === pointDeviceId ||
                      coTravelerDevice === pointDeviceId ||
                      (pointStudentId && coTravelerDevice.includes(pointStudentId)) ||
                      (pointStudentId && coTravelerEntity.includes(pointStudentId)) ||
                      (pointDeviceId && coTravelerEntity.includes(pointDeviceId));
                    
                    if (isMatch) {
                      const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
                      return coTravelerVisibility[resultKey] === true;
                    }
                  }
                  return false; // No match = hide
                }
                return true; // Always show primary subject points
              });
              
              // Merge uploaded data at display time, filtered by per-file visibility
              const uploadedPts = showUploadedData ? uploadedDataPoints.filter(p => {
                const name = (p.metadata?.originalFileName || '').replace(/\.(csv|geojson|json|kml)$/i, '');
                return uploadedFileVisibility[name] !== false;
              }) : [];
              return [...filtered, ...uploadedPts];
            })()}
            routes={filteredVisibleRoutes.filter(route => {
              // Filter co-traveler routes based on visibility toggles  
              if ((route as any).isCoTravelerRoute) {
                // Default to visible - only hide if explicitly set to hidden
                let shouldShow = true;
                
                const routeStudentId = (route.studentId || '').toLowerCase();
                const routeDeviceId = (route.deviceId || '').toLowerCase();
                
                // Check if any co-traveler result matches this route and is hidden
                for (const result of coTravelerResults) {
                  const coTravelerDevice = (result.coTravelerDevice || '').toLowerCase();
                  
                  // Extract entity ID from the co-traveler device (e.g., "student-07 - phone" → "student-07")
                  const coTravelerEntity = coTravelerDevice.includes(' - ') 
                    ? coTravelerDevice.split(' - ')[0] 
                    : coTravelerDevice;
                  
                  // Flexible matching: check if IDs match in various formats
                  const isMatch = 
                    coTravelerEntity === routeStudentId ||
                    coTravelerEntity === routeDeviceId ||
                    coTravelerDevice === routeDeviceId ||
                    (routeStudentId && coTravelerDevice.includes(routeStudentId)) ||
                    (routeStudentId && coTravelerEntity.includes(routeStudentId)) ||
                    (routeDeviceId && coTravelerEntity.includes(routeDeviceId));
                  
                  if (isMatch) {
                    const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
                    // Only show if explicitly set to true (default OFF)
                    shouldShow = coTravelerVisibility[resultKey] === true;
                    break; // Found match, use this visibility
                  }
                }
                
                return shouldShow;
              }
              return true; // Always show primary subject routes
            }).concat(showUploadedData ? uploadedRoutes.filter(r => {
              const firstPt = r.points?.[0];
              if (!firstPt?.metadata?.originalFileName) return true;
              const name = firstPt.metadata.originalFileName.replace(/\.(csv|geojson|json|kml)$/i, '');
              return uploadedFileVisibility[name] !== false;
            }) : [])}
            biometricHighlights={biometricHighlights}
            studentColors={studentColors}
            mapSettings={pathfinderMapSettings}
            showDataPoints={showDataPoints}
            onShowDataPointsChange={setShowDataPoints}
            showRouteLines={showRouteLines}
            onShowRouteLinesChange={setShowRouteLines}
            showDirectionArrows={showDirectionArrows}
            onShowDirectionArrowsChange={setShowDirectionArrows}
            showHeatmap={showHeatmap}
            onShowHeatmapChange={setShowHeatmap}
            coTravelerResults={mapVisibleCoTravelerResults.filter(result => {
              const resultKey = `${result.primaryDevice}-${result.coTravelerDevice}`;
              return coTravelerVisibility[resultKey] === true; // Only show if explicitly enabled
            })}
            showCoTravelerConnections={showCoTravelerConnections}
            showCoTravelerPoints={showCoTravelerPoints}
            onShowCoTravelerPointsChange={setShowCoTravelerPoints}
            showCoTravelerLines={showCoTravelerLines}
            onShowCoTravelerLinesChange={setShowCoTravelerLines}
            showCoTravelerHeatmap={showCoTravelerHeatmap}
            onShowCoTravelerHeatmapChange={setShowCoTravelerHeatmap}
            enableClustering={enableClustering}
            onEnableClusteringChange={setEnableClustering}
            // Used by the new Map Layers dialog (centered popup) - keeps map
            // controls visually attached to the map. The CompactTimeSlider
            // still receives the same boolean state for backward compat but
            // its layers drawer was removed.
            whiteCellInterval={whiteCellInterval}
            onWhiteCellIntervalChange={setWhiteCellInterval}
            timeRange={timeRange}
            currentPlaybackTime={currentTime}
            playbackWindow={playbackWindow}
            followMode={followMode}
            dwellLocations={filteredVisibleDwells}
            showDwells={showDwells}
            onShowDwellsChange={setShowDwells}
            highlightClosestSrp={highlightClosestSrp}
            selectedStudentId={selectedStudents[0]}
            courseId={selectedCourse}
            onLoadAdtechPol={handleLoadAdtechPol}
            adtechPolLoading={adtechPolLoading}
            hideGaps={hideGaps}
            hideImplausibleMovement={hideImplausibleMovement}
            implausibleMaxSpeedKmh={implausibleMaxSpeedKmh}
            gapMaxTimeMinutes={gapMaxTimeMinutes}
            gapMaxDistanceM={gapMaxDistanceM}
            digitalPoLData={showDigitalPoLOnMap ? digitalPoLData : []}
            showDigitalPoL={showDigitalPoLOnMap}
            onMapChange={(center, zoom) => {
              setMapCenter(center);
              setMapZoom(zoom);
            }}
            onMapBoundsChange={handleMapBoundsChange}
            filterByMapExtent={filterByMapExtent}
            onToggleFilterByMapExtent={() => setFilterByMapExtent(v => !v)}
            onOpenMapSettings={() => setMapSettingsOpen(true)}
            timeSliderMinimized={timelineMinimized}
            onRestoreTimeSlider={() => setTimelineMinimized(false)}
            showCameras={showCamerasOnMap}
            onCameraCountChange={setCameraCountOnMap}
            onCameraControlsChange={setCameraControlsOnMap}
            showDwellLabels={showDwellLabelsOnMap}
            onRegisterFitBounds={registerFitBounds}
            camerasInViewDockEl={camerasInViewDockEl}
            // Auto-refresh after the admin GPS-cleanup tool runs a
            // quarantine / delete / restore. We just re-call the same
            // loader the page uses for any other data refresh so the
            // map immediately reflects the cleanup.
            onRefreshDataRequested={() => {
              try {
                loadData();
              } catch (e) {
                console.warn('[PathfinderPage] cleanup refresh failed', e);
              }
            }}
            // Area search / circle drawing props
            isDrawingMode={isDrawingMode}
            drawCircles={mapDrawCircles}
            defaultRadius={defaultRadius}
            onCircleDrawn={handleCircleDrawn}
            // Annotation drawing props (geofence drawing reuses this same
            // vertex-based polygon mode; completion is routed by context below)
            isAnnotationMode={isDrawingAnnotation || isDrawingAnalysisArea || isDrawingGeofence || isDrawingAdtechPolygon}
            onAnnotationDrawn={(geometry: any) => {
              // Freeform polygon area takes precedence — turn the drawn ring into
              // a first-class search area and run the full area analysis in it.
              if (isDrawingAdtechPolygon) {
                setIsDrawingAdtechPolygon(false);
                handleAdtechPolygonComplete(geometry);
                return;
              }
              // Geofence draw takes precedence when active — hand the polygon
              // to the Geofence panel's save dialog instead of the annotation flow.
              if (isDrawingGeofence) {
                setPendingGeofencePolygon(geometry);
                setIsDrawingGeofence(false);
                return;
              }
              setDrawnAnnotationGeometry(geometry);
              // Only exit annotation mode if not in Sentinel analysis area drawing mode
              if (!isDrawingAnalysisArea) {
                setIsDrawingAnnotation(false);
              }
            }}
            onAnnotationModeCancel={() => {
              // Cancel annotation drawing
              setIsDrawingAnnotation(false);
              setDrawnAnnotationGeometry(null);
              // Cancel geofence drawing if it was active
              setIsDrawingGeofence(false);
              // Cancel ad-hoc adtech polygon draw if it was active
              setIsDrawingAdtechPolygon(false);
              // If in Sentinel mode, also cancel analysis area drawing
              if (isDrawingAnalysisArea) {
                setIsDrawingAnalysisArea(false);
                setAnalysisArea(null);
              }
            }}
            // Geofence overlay
            geofences={geofences}
            selectedGeofenceId={selectedGeofenceId}
            // Overwatch media article markers
            overwatchArticles={overwatchArticles}
            // AdTech area-search signals + focused device
            adtechSignals={mapVisibleAdtechSignals}
            adtechFocusId={adtechFocusId}
            hasAdtechResults={adtechMapSignals.length > 0}
            adtechTableVisible={adtechTableVisible}
            onToggleAdtechTable={() => setAdtechTableVisible((v) => !v)}
            // Itinerary submissions
            itinerarySubmissions={itinerarySubmissions}
            showItinerary={showItineraryOnMap}
            // Selected trainee name for legend
            pttUtterances={isCommercialBanner && showPttMessages ? pttUtterances : []}
            showPttMessages={isCommercialBanner && showPttMessages}
            onShowPttMessagesChange={setShowPttMessages}
            clusterPttMessages={clusterPttMessages}
            pttPlaybackWindow={playbackWindow}
            pttSenderColors={pttSenderColors}
            onPttOpenInPanel={openPttInPanel}
            selectedTraineeName={selectedStudents.length > 0 
              ? (availableStudents.find(s => s.id === selectedStudents[0])?.name || selectedStudents[0])
              : undefined
            }
            // Selected trainee devices with colors for legend.
            // studentColors holds multiple keys per logical device (deviceId,
            // `${studentId}-${deviceType}`, and a coTravelerKey for cotraveler
            // points), plus aliases like "Cellphone" that all resolve to the
            // canonical "Phone" device-type label. Without dedup we get
            // duplicate rows for Phone in the legend's "Selected: <entity>"
            // panel (commercial-side BeaconBee was showing "Phone, Watch,
            // Phone"). Reduce to the canonical (deviceType, color) set.
            selectedTraineeDevices={(() => {
              if (selectedStudents.length === 0) return [];
              const selected = selectedStudents[0];
              const seen = new Map<string, { deviceType: string; color: string }>();
              for (const [deviceId, color] of Object.entries(studentColors) as [string, string][]) {
                // The legend's selected-entity bucket only shows the primary
                // entity's own devices, so we only keep keys that strip down
                // to the selected entity id when the trailing -<suffix>
                // (deviceType) is removed.
                const studentId = deviceId.replace(/-[^-]+$/, '');
                if (studentId !== selected) continue;
                const lower = deviceId.toLowerCase();
                const deviceType = lower.includes('vehicle')
                  ? 'Vehicle'
                  : lower.includes('phone')
                  ? 'Phone'
                  : lower.includes('watch')
                  ? 'Watch'
                  : 'Device';
                if (!seen.has(deviceType)) {
                  seen.set(deviceType, { deviceType, color });
                }
              }
              return Array.from(seen.values());
            })()}
            coTravelerLegendItems={coTravelerLegendItems}
            uploadedFiles={uploadedFileNames.map(name => ({
              name,
              visible: uploadedFileVisibility[name] !== false,
              pointCount: uploadedDataPoints.filter(p =>
                (p.metadata?.originalFileName || '').replace(/\.(csv|geojson|json|kml)$/i, '') === name
              ).length,
            }))}
            onToggleUploadedFile={(name: string, visible: boolean) =>
              setUploadedFileVisibility((prev: Record<string, boolean>) => ({ ...prev, [name]: visible }))
            }
            signalMarkers={rfEnabled && signalData ? (() => {
              const inWindow = (s: any) => {
                if (!playbackWindow) return true;
                const first = s.first_seen ? new Date(s.first_seen).getTime() : 0;
                const last = s.last_seen ? new Date(s.last_seen).getTime() : Infinity;
                return first <= playbackWindow.end.getTime() && last >= playbackWindow.start.getTime();
              };
              const matchesType = (s: any) =>
                signalDeviceTypeFilter.length === 0 ||
                signalDeviceTypeFilter.includes(s.device_type || 'Unknown');
              return [
                ...(signalData.wifi || []).filter((w: any) => w.latitude && w.longitude && signalLayerVisibility.wifi && inWindow(w) && matchesType(w)).map((w: any) => ({
                  latitude: w.latitude, longitude: w.longitude, type: 'wifi' as const,
                  label: w.ssid || w.bssid || 'WiFi AP', count: w.observation_count || 1,
                  device_type: w.device_type, manufacturer: w.manufacturer,
                })),
                ...(signalData.bluetooth || []).filter((b: any) => b.latitude && b.longitude && signalLayerVisibility.bluetooth && inWindow(b) && matchesType(b)).map((b: any) => ({
                  latitude: b.latitude, longitude: b.longitude, type: 'bluetooth' as const,
                  label: b.name || b.address || 'BLE Device', count: b.observation_count || 1,
                  device_type: b.device_type, manufacturer: b.manufacturer,
                })),
                ...(signalData.cellular || []).filter((c: any) => c.latitude && c.longitude && signalLayerVisibility.cellular && inWindow(c)).map((c: any) => ({
                  latitude: c.latitude, longitude: c.longitude, type: 'cellular' as const,
                  label: c.operator || c.cell_tower_id || 'Cell Tower', count: c.observation_count || 1,
                })),
              ];
            })() : []}
            showSignalMarkers={rfEnabled && !!signalData}
          />
          {/* Story narrative HUD. Lives inside the map column so it
              survives fullscreen; toggled from the map toolbar. */}
          <PathfinderMapNarrativeOverlay
            currentTime={currentTime}
            isFullExtent={isFullExtent}
            onEventSelect={handleNarrativeEventSelect}
            dataPoints={dataPoints}
            routes={routes}
            dwellLocations={dwellLocations}
            actualDevices={actualDevices}
            itinerarySubmissions={itinerarySubmissions}
            coTravelerResults={coTravelerResults}
            signalData={signalData}
          />
          </Box>

          {/* Floating timeline. Defaults to bottom-center of the map, then
              the drag handle lets users move it anywhere within the map
              bounds without resizing the map column. */}
          <Box
            ref={timelineOverlayRef}
            sx={{
              position: 'absolute',
              // Minimized: the card leaves the map entirely; the map toolbar's
              // timeline button brings it back. Stays mounted (display:none) so
              // the slider's playback state and the docked camera strip survive.
              display: timelineMinimized ? 'none' : 'block',
              ...(timelinePosition
                ? { left: timelinePosition.left, bottom: timelinePosition.bottom }
                : { left: '50%', bottom: 12, transform: 'translateX(-50%)' }),
              width: { xs: 'calc(100% - 24px)', md: 'min(920px, calc(100% - 32px))' },
              zIndex: 1200,
              bgcolor: theme.palette.mode === 'dark'
                ? 'rgba(18, 20, 24, 0.88)'
                : 'rgba(255, 255, 255, 0.90)',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
              backdropFilter: 'blur(8px)',
              // Visible so the slider's time label can sit fully above the
              // track instead of being clipped by this card / the map column.
              overflow: 'visible',
            }}
          >
            <Box sx={{ position: 'relative' }}>
              <Box
                onPointerDown={handleTimelineDragStart}
                sx={{
                  height: 14,
                  cursor: timelineDragRef.current ? 'grabbing' : 'grab',
                  touchAction: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.07)'
                    : 'rgba(0,0,0,0.06)',
                  '&::after': {
                    content: '""',
                    width: 44,
                    height: 4,
                    borderRadius: 999,
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.35)'
                      : 'rgba(0,0,0,0.25)',
                  },
                }}
                title="Drag timeline"
              />
              {!timelineMinimized && (
                <Tooltip title="Minimize timeline" placement="top">
                  <IconButton
                    size="small"
                    aria-label="Minimize timeline"
                    onClick={() => setTimelineMinimized(true)}
                    onPointerDown={(e) => e.stopPropagation()}
                    sx={{
                      position: 'absolute',
                      right: 2,
                      top: -1,
                      p: 0,
                      width: 16,
                      height: 16,
                      color: 'text.secondary',
                    }}
                  >
                    <MinimizeTimelineIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box>
            {/* Slot for the map's "cameras in view" Street View strip so it
                reads as part of the timeline card (see PathfinderMapGL). */}
            <Box ref={setCamerasInViewDockEl} sx={{ '&:empty': { display: 'none' } }} />
            <CompactTimeSlider 
              dataPoints={activeDataPoints}
              routes={activeRoutes}
              timeRange={timeRange}
              selectedStudents={isEntityContextTab ? selectedStudents : Object.keys(areaSearchActualDevices)}
              selectedDevices={activeSelectedDevices}
              dwellLocations={activeDwellLocations}
              showDataPoints={showDataPoints}
              onShowDataPointsChange={setShowDataPoints}
              showRouteLines={showRouteLines}
              onShowRouteLinesChange={setShowRouteLines}
              showDirectionArrows={showDirectionArrows}
              onShowDirectionArrowsChange={setShowDirectionArrows}
              showHeatmap={showHeatmap}
              onShowHeatmapChange={setShowHeatmap}
              enableClustering={enableClustering}
              onEnableClusteringChange={setEnableClustering}
              showCoTravelerPoints={showCoTravelerPoints}
              onShowCoTravelerPointsChange={setShowCoTravelerPoints}
              showCoTravelerLines={showCoTravelerLines}
              onShowCoTravelerLinesChange={setShowCoTravelerLines}
              showCoTravelerHeatmap={showCoTravelerHeatmap}
              onShowCoTravelerHeatmapChange={setShowCoTravelerHeatmap}
              showDwells={showDwells}
              onShowDwellsChange={setShowDwells}
              highlightClosestSrp={highlightClosestSrp}
              onHighlightClosestSrpChange={setHighlightClosestSrp}
              showPttMessages={showPttMessages}
              onShowPttMessagesChange={isCommercialBanner ? setShowPttMessages : undefined}
              clusterPttMessages={clusterPttMessages}
              onClusterPttMessagesChange={isCommercialBanner ? setClusterPttMessages : undefined}
              hideGaps={hideGaps}
              onHideGapsChange={setHideGaps}
              hideImplausibleMovement={hideImplausibleMovement}
              onHideImplausibleMovementChange={setHideImplausibleMovement}
              implausibleMaxSpeedKmh={implausibleMaxSpeedKmh}
              onImplausibleMaxSpeedKmhChange={setImplausibleMaxSpeedKmh}
              onUseOrgTrackFilterDefaults={handleUseOrgTrackFilterDefaults}
              trackFiltersUserCustomized={trackFiltersUserCustomized}
              whiteCellInterval={whiteCellInterval}
              onWhiteCellIntervalChange={setWhiteCellInterval}
              mapSettingsOpen={mapSettingsOpen}
              onOpenMapSettings={() => setMapSettingsOpen(true)}
              onMapSettingsClose={() => setMapSettingsOpen(false)}
              seekRequest={pttSeek}
              showCameras={showCamerasOnMap}
              onShowCamerasChange={setShowCamerasOnMap}
              cameraCount={cameraCountOnMap}
              cameraControls={cameraControlsOnMap}
              showDwellLabels={showDwellLabelsOnMap}
              onShowDwellLabelsChange={setShowDwellLabelsOnMap}
              dwellCount={filteredVisibleDwells.length}
              showItinerary={showItineraryOnMap}
              onShowItineraryChange={setShowItineraryOnMap}
              hasItineraryData={itinerarySubmissions.length > 0}
              onZoomToBounds={zoomToBounds}
              onTimeUpdate={useCallback((time, points, progressiveRoutes, isFullExtent, window) => {
                
                // Collect all selected device IDs for filtering (use active tab's devices)
                const selectedDeviceIds = Object.values(activeSelectedDevices).flat();
                
                // Filter dwells by selected devices AND current time window (for playback / timeframe changes)
                let visibleDwellsInWindow = activeDwellLocations.filter(dwell => {
                  if (String(dwell.areaSearchCircleId || '').startsWith('intel-packet-')) return true;
                  // If no devices selected, show all dwells (default behavior)
                  if (selectedDeviceIds.length === 0) return true;
                  
                  // Filter by device: check if dwell's device matches any selected device
                  // Dwell has entityId and deviceType, construct deviceKey like "Student-XX-devicetype"
                  const entityId = dwell.entityId || dwell.entity_id;
                  const deviceType = (dwell.deviceType || dwell.device_type || 'phone').toLowerCase();
                  const dwellDeviceKey = `${entityId}-${deviceType}`;
                  
                  // Also check new format "Entity - DeviceType" for compatibility
                  const dwellDeviceKeyNew = `${entityId} - ${deviceType.charAt(0).toUpperCase() + deviceType.slice(1)}`;
                  
                  return selectedDeviceIds.includes(dwellDeviceKey) || selectedDeviceIds.includes(dwellDeviceKeyNew);
                });
                
                // Apply time window filtering (when not showing full extent)
                if (!isFullExtent && window?.start && window?.end) {
                  const windowStartMs = window.start.getTime();
                  const windowEndMs = window.end.getTime();
                  
                  visibleDwellsInWindow = visibleDwellsInWindow.filter(dwell => {
                    if (String(dwell.areaSearchCircleId || '').startsWith('intel-packet-')) return true;
                    const startRaw = dwell.start_time ?? dwell.startTime ?? dwell.start;
                    const endRaw = dwell.end_time ?? dwell.endTime ?? dwell.end;
                    const start = parseBackendDateTime(startRaw);
                    const end = parseBackendDateTime(endRaw);
                    if (!start || !end) return false;
                    const startMs = start.getTime();
                    const endMs = end.getTime();
                    // Keep dwells that overlap the window
                    return startMs <= windowEndMs && endMs >= windowStartMs;
                  });
                }
                
                // Apply annotation filtering to dwells if enabled
                if (filterByAnnotations && activeAnnotations.length > 0) {
                  visibleDwellsInWindow = visibleDwellsInWindow.filter(dwell => {
                    const dwellEntityId = dwell.entityId || dwell.entity_id;
                    const dwellCoords: [number, number] = [dwell.latitude, dwell.longitude];
                    
                    return activeAnnotations.some(annotation => {
                      if (annotation.entity_id !== dwellEntityId) return false;
                      if (annotation.geometry?.coordinates?.[0] && dwellCoords[0] && dwellCoords[1]) {
                        return isPointInPolygon(dwellCoords, annotation.geometry.coordinates[0]);
                      }
                      return true;
                    });
                  });
                }
                
                // React 18+ automatic batching - all updates in same event are batched automatically
                setCurrentTime(time);
                setIsFullExtent(isFullExtent);
                setPlaybackWindow(!isFullExtent && window?.start && window?.end ? { start: window.start, end: window.end } : null);
                setVisibleDataPoints(points);
                setVisibleRoutes(progressiveRoutes);
                if (leftPanelTab === 0) {
                  setVisibleDwells(visibleDwellsInWindow.filter((d: any) => !String(d.areaSearchCircleId || '').startsWith('intel-packet-')));
                } else {
                  setVisibleAreaSearchDwells(visibleDwellsInWindow);
                }
                
                // If follow mode is enabled, center map on the point closest to current playback time
                if (followMode && points.length > 0) {
                  // Find the point closest to the current playback time (not just the last in array)
                  const currentTimeMs = time.getTime();
                  let closestPoint = points[0];
                  let minTimeDiff = Math.abs(points[0].timestamp.getTime() - currentTimeMs);
                  
                  for (const point of points) {
                    const timeDiff = Math.abs(point.timestamp.getTime() - currentTimeMs);
                    if (timeDiff < minTimeDiff) {
                      minTimeDiff = timeDiff;
                      closestPoint = point;
                    }
                  }
                  
                  const newCenter: [number, number] = [closestPoint.coordinates[0], closestPoint.coordinates[1]];
                  setMapCenter(newCenter);
                }
              }, [followMode, activeDwellLocations, activeSelectedDevices, filterByAnnotations, activeAnnotations, isPointInPolygon, leftPanelTab])}
              onFollowModeChange={useCallback((enabled: boolean) => {
                setFollowMode(enabled);
              }, [])}
            />
            </Box>
          </Box>
        </Box>

        {/* Floating AdTech results table — X / toolbar hide keep points;
            DeleteSweep clears the map layer. */}
        {adtechMapSignals.length > 0 && adtechTableVisible && (
          <AdtechResultsTable
            signals={adtechMapSignals}
            onClose={handleCloseAdtechTable}
            onClear={handleClearAdtech}
            onRowClick={handleAdtechRowClick}
            focusedId={adtechFocusId}
            sourceLabel={adtechSourceLabel}
            onLoadPol={handleLoadAdtechPol}
            polLoading={adtechPolLoading}
            polMessage={adtechPolMessage}
            courseId={selectedCourse}
          />
        )}
        </Box>

        {/* Right Sidebar - Analysis Tools - Responsive */}
        <Box sx={{ 
          width: { 
            xs: mobileRightPanelOpen ? '100%' : 0, // Full width overlay on mobile
            sm: mobileRightPanelOpen ? '80%' : 0,
            md: rightPanelCollapsed ? PATHFINDER_TOOL_RAIL_WIDTH : Math.min(rightPanelWidth, 500),
            lg: rightPanelCollapsed ? PATHFINDER_TOOL_RAIL_WIDTH : Math.min(rightPanelWidth, 580),
            xl: rightPanelCollapsed ? PATHFINDER_TOOL_RAIL_WIDTH : Math.min(rightPanelWidth, 680),
          },
          minWidth: { xs: 0, md: rightPanelCollapsed ? PATHFINDER_TOOL_RAIL_WIDTH : 280 },
          maxWidth: { xs: '100%', md: '45vw' },
          flexShrink: 1,
          bgcolor: 'background.paper', 
          borderLeft: { xs: 0, md: 1 },
          borderColor: 'divider',
          display: { xs: mobileRightPanelOpen ? 'flex' : 'none', md: 'flex' },
          flexDirection: 'column',
          position: { xs: 'absolute', md: 'relative' },
          right: { xs: 0, md: 'auto' },
          top: { xs: 0, md: 'auto' },
          bottom: { xs: 0, md: 'auto' },
          zIndex: { xs: 1200, md: 'auto' },
          transition: 'width 0.3s ease',
          boxShadow: { xs: mobileRightPanelOpen ? '-4px 0 20px rgba(0,0,0,0.3)' : 'none', md: 'none' },
          overflow: 'hidden',
        }}>
          {/* Resize Handle */}
          {!rightPanelCollapsed && (
            <Box
              onMouseDown={handleMouseDown}
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 5,
                cursor: 'col-resize',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  opacity: 0.5,
                },
                zIndex: 10,
              }}
            />
          )}

          {/* Collapse tab — same style as left panel, kept INSIDE the right
              panel so the map toolbar cannot cover it. */}
          <Tooltip
            title={rightPanelCollapsed ? 'Show tools panel' : 'Hide tools panel'}
            placement="left"
          >
            <Box
              onClick={handleRightPanelToggle}
              sx={{
                display: { xs: 'none', md: 'flex' },
                position: 'absolute',
                left: 0,
                // Sit inside the panel edge; when collapsed (rail-only) keep it
                // near the top so tool icons below stay clickable.
                top: rightPanelCollapsed ? 8 : 72,
                width: 24,
                height: 72,
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                bgcolor: 'background.paper',
                color: 'text.secondary',
                border: 1,
                borderColor: 'divider',
                borderLeft: 0,
                borderRadius: '0 8px 8px 0',
                boxShadow: 2,
                zIndex: 20,
                transition: 'background-color 0.2s, color 0.2s',
                '&:hover': {
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                },
              }}
            >
              {rightPanelCollapsed ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
            </Box>
          </Tooltip>

          <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0, minWidth: 0 }}>
            {/* Tool body — hidden when desktop panel is collapsed (rail stays) */}
            {(!rightPanelCollapsed || isSmallScreen) && (
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.25,
                    py: 0.75,
                    minHeight: 44,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'action.hover',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                  }}
                >
                  {isSmallScreen && (
                    <IconButton
                      size="small"
                      onClick={() => setMobileRightPanelOpen(false)}
                      aria-label="Close tools panel"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                      {activeTool?.icon}
                    </Box>
                    <Typography
                      variant="subtitle2"
                      noWrap
                      sx={{ fontWeight: 700, letterSpacing: '0.01em' }}
                    >
                      {activeTool?.label ?? 'Tools'}
                    </Typography>
                  </Box>
                  {isSmallScreen && (
                    <Tooltip title="Browse tools">
                      <IconButton
                        size="small"
                        onClick={() => setMobileToolDrawerOpen(true)}
                        aria-label="Browse tools"
                      >
                        <MenuIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Search tools (Ctrl/Cmd+K)">
                    <IconButton
                      size="small"
                      onClick={() => setToolPaletteOpen(true)}
                      aria-label="Search tools"
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                  {activeTool && (
                    <Box
                      key={activeTool.id}
                      sx={{
                        p: 3,
                        // Scrolling tools (PTT transcript) fill the rail height so their
                        // own list scrolls instead of being clipped to a fixed minimum.
                        ...(activeTool.id === 'ptt-comms'
                          ? { height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }
                          : {}),
                      }}
                    >
                      {activeTool.component}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Desktop icon rail — always visible on md+ (also when collapsed) */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, flexShrink: 0 }}>
              <PathfinderToolRail
                tools={tabs}
                activeToolId={activeTool?.id ?? activeToolId}
                onSelect={selectTool}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <PathfinderToolPalette
        open={toolPaletteOpen}
        onClose={() => setToolPaletteOpen(false)}
        tools={tabs}
        activeToolId={activeTool?.id ?? activeToolId}
        onSelect={selectTool}
      />

      {/* Mobile: grouped tool list in a bottom drawer */}
      <Drawer
        anchor="bottom"
        open={mobileToolDrawerOpen}
        onClose={() => setMobileToolDrawerOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '70vh',
            pt: 1,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
          <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: 'divider' }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Tools
          </Typography>
          <IconButton size="small" onClick={() => setMobileToolDrawerOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <PathfinderToolRail
          variant="list"
          tools={tabs}
          activeToolId={activeTool?.id ?? activeToolId}
          onSelect={(id) => {
            selectTool(id);
            setMobileToolDrawerOpen(false);
          }}
        />
      </Drawer>

      {/* Mobile Controls Drawer */}
      <Drawer
        anchor="bottom"
        open={mobileControlsOpen}
        onClose={() => setMobileControlsOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '80vh',
            px: 2,
            pb: 3,
            pt: 1,
          }
        }}
      >
        {/* Drag handle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: 'divider' }} />
        </Box>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Pathfinder Controls
          </Typography>
          <IconButton size="small" onClick={() => setMobileControlsOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Course Selection */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel sx={{ fontWeight: 'bold' }}>{courseSingular}</InputLabel>
          <Select
            value={selectedCourse || ''}
            label={courseSingular}
            onChange={(e) => setSelectedCourse(e.target.value || undefined)}
            disabled={loading || availableCourses.length === 0}
            sx={{ 
              backgroundColor: 'action.hover',
              fontWeight: '500',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
            }}
          >
            <MenuItem value="">
              <em>{allLabel}</em>
            </MenuItem>
            {availableCourses.map((course) => {
              const isMostRecent = course.id === mostRecentCourses.dhost || course.id === mostRecentCourses.dhoqc;
              return (
                <MenuItem 
                  key={course.id} 
                  value={course.id}
                  sx={isMostRecent ? {
                    backgroundColor: 'rgba(0, 200, 150, 0.15)',
                    borderLeft: '3px solid',
                    borderLeftColor: '#00c896',
                  } : undefined}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {course.title}
                    {isMostRecent && (
                      <Chip label="LATEST" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, color: '#00c896', bgcolor: 'rgba(0, 200, 150, 0.2)' }} />
                    )}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {/* Participant selection (labels from feature flags) */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel sx={{ fontWeight: 'bold' }}>{plural}</InputLabel>
          <Select
            multiple
            value={selectedStudents}
            label={plural}
            onChange={(e) => setSelectedStudents(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            disabled={loading || availableStudents.length === 0}
            renderValue={(selected) => {
              if (selected.length === 0) return `Select ${pluralLower}`;
              if (selected.length === 1) {
                const student = availableStudents.find(s => s.id === selected[0]);
                return student?.name || selected[0];
              }
              return `${selected.length} ${pluralLower} selected`;
            }}
            sx={{ 
              backgroundColor: 'action.hover',
              fontWeight: '500',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
            }}
          >
            {availableStudents.map((student) => (
              <MenuItem key={student.id} value={student.id}>{student.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Range */}
        <Button
          variant="outlined"
          fullWidth
          size="medium"
          startIcon={<CalendarIcon />}
          onClick={() => {
            setDateTimePickerOpen(true);
          }}
          sx={{
            mb: 2,
            borderColor: 'divider',
            color: 'text.primary',
            backgroundColor: 'action.hover',
            height: 44,
            textTransform: 'none',
            justifyContent: 'flex-start',
            '&:hover': { borderColor: 'primary.light', backgroundColor: 'action.hover' },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {dateRange.start && dateRange.end ? (
              <>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {dateRange.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {' - '}
                  {dateRange.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                  {dateRange.start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  {' - '}
                  {dateRange.end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </>
            ) : (
              <Typography variant="body2">Select date range</Typography>
            )}
          </Box>
        </Button>

        {/* Filter Map Data */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            placeholder="Filter map data"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
                setMobileControlsOpen(false);
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'text.primary',
                '& fieldset': { borderColor: 'divider' },
                '& input::placeholder': { color: 'text.secondary' }
              }
            }}
          />
          <IconButton 
            onClick={() => {
              handleSearch();
              setMobileControlsOpen(false);
            }}
            disabled={loading || !searchTerm.trim()}
            sx={{ color: 'text.primary' }}
          >
            <SearchIcon />
          </IconButton>
          {/* "Crop to map view" toggle moved onto the map toolbar
              (PathfinderMapGL) so it lives where the map controls do. */}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Load Actions */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => {
              loadData();
              setMobileControlsOpen(false);
            }}
            disabled={loading || selectedStudents.length === 0}
            startIcon={loading && !loadingWithCoTravelers ? <CircularProgress size={16} color="inherit" /> : <PeopleIcon />}
            sx={{ height: 44, textTransform: 'none', fontWeight: 600 }}
          >
            Load Selected
          </Button>

          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, mb: 0.5, textAlign: 'center' }}>
            Physical Co-Traveler
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={() => {
                loadDataWithCoTravelers('all');
                setMobileControlsOpen(false);
              }}
              disabled={loading || selectedStudents.length === 0}
              startIcon={loadingWithCoTravelers ? <CircularProgress size={16} /> : <GroupIcon />}
              sx={{ height: 40, textTransform: 'none', fontSize: '0.75rem' }}
            >
              All
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                loadDataWithCoTravelers('students');
                setMobileControlsOpen(false);
              }}
              disabled={loading || selectedStudents.length === 0}
              startIcon={<SchoolIcon />}
              sx={{ height: 40, textTransform: 'none', fontSize: '0.75rem', color: '#4caf50', borderColor: '#4caf50' }}
            >
              Students
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                loadDataWithCoTravelers('rp_srp');
                setMobileControlsOpen(false);
              }}
              disabled={loading || selectedStudents.length === 0}
              startIcon={<ShieldIcon />}
              sx={{ height: 40, textTransform: 'none', fontSize: '0.75rem', color: '#f44336', borderColor: '#f44336' }}
            >
              RP/SRP
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* CSV/KML/JSON Upload Dialog */}
      <CSVUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onDataLoaded={handleCSVDataLoaded}
      />

      {/* Date/Time Range Picker Dialog */}
      <DateTimeRangePicker
        open={dateTimePickerOpen}
        startDate={dateRange.start}
        endDate={dateRange.end}
        onClose={() => setDateTimePickerOpen(false)}
        onApply={(range) => {
          setDateRangeIsUtcInstant(false);
          setDateRange(range);
        }}
        minDate={_availableDateRange.min}
        maxDate={_availableDateRange.max}
      />

      {/* Quick Launch Notification */}
      <Snackbar
        open={quickLaunchNotification.open}
        autoHideDuration={6000}
        onClose={() => setQuickLaunchNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setQuickLaunchNotification(prev => ({ ...prev, open: false }))}
          severity={quickLaunchNotification.severity}
          sx={{ width: '100%' }}
          icon={quickLaunchNotification.severity === 'success' ? <RocketIcon /> : undefined}
        >
          {quickLaunchNotification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PathfinderPage;
